import '@fontsource/inter';
import React, {useState, useEffect, useRef, useCallback, memo, useMemo, useLayoutEffect} from 'react';
import {createPortal} from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import Mermaid from './Mermaid';
import Conversations from './Conversations';
import {useLocation, useNavigate, createSearchParams, Routes, Route, Navigate, useParams} from 'react-router-dom';
import AdkClient from './AdkClient';
import * as Messages from './Messages';
import config, {isConfigLoaded} from './config';
import {createParser} from 'eventsource-parser';
import {getHubConfig} from './config';
import AuthCallback from './auth/AuthCallback';
import Sidebar from './components/Sidebar';
import NavigationSidebar from './components/NavigationSidebar';
import ConversationsTopBar from './components/ConversationsTopBar';
import CodeBlock from './components/CodeBlock';
import SettingsLayout from './components/SettingsLayout';
import Connectors from './pages/Connectors';
import Advanced from './pages/Advanced';
import UserSettings from './pages/UserSettings';
import Projects from './pages/Projects';
import Templates from './pages/Templates';
import PlaybookLauncher from './pages/PlaybookLauncher';
import Learn from './pages/Learn';
import News from './pages/News';
import Discover from './pages/Discover';
import Memories from './pages/Memories';
import Theme from './pages/Theme';
import Analytics from './pages/Analytics';
import Privacy from './pages/Privacy';
import Billing from './pages/Billing';

import InstructionsConfig from './components/InstructionsConfig';
import Toast from './components/Toast';
import Modal from './components/Modal';
import Canvas from './components/Canvas';
import AIInstructionsModal from './components/AIInstructionsModal';
import SavePromptModal from './components/SavePromptModal';
import SaveMessageModal from './components/SaveMessageModal';
import ConversationHistoryModal from './components/ConversationHistoryModal';
import AIModeDropdown from './components/AIModeDropdown';
import ModelDropdown from './components/ModelDropdown';
import {InlineUIRenderer} from './components/InlineUI';
import MCPAppView from './components/MCPAppView';
import ReasoningStatus from './components/ReasoningStatus';
import OnboardingGuide from './components/OnboardingGuide';
import BrowserStreamViewer from './components/BrowserStreamViewer';
import BrowserStreamViewerStandalone from './components/BrowserStreamViewerStandalone';
import Icon from './components/icons/Icon';
import contextManager from './utils/ContextManager';
import * as CodeExtractor from './utils/CodeExtractor';
import {generateImage, detectImageGenerationRequest} from './ImageGeneration';
import {generateVideo, detectVideoGenerationRequest} from './VideoGeneration';
import indexedDbStorage from './utils/indexedDbStorage';
import indexedDbFileStorage, {GIT_STATE} from './utils/indexedDbFileStorage';
import {EXCLUDED_FILES_FOR_AI} from './utils/indexedDbFileStorage';
import Login from './pages/Login';
import Register from './pages/Register';
import JoinCollaboration from './pages/JoinCollaboration';
import {AuthProvider, useAuth} from './context/AuthContext';
import {OrganizationProvider, useSelectedOrganization} from './context/OrganizationContext';
import {PermissionsProvider} from './context/PermissionsContext';
import {TenantAccessProvider} from './context/TenantAccessContext';
import {RedirectToLogin} from '@mia-platform-internal/authtool-client-js/react';
import {NotificationProvider} from './context/NotificationContext';
import MobileAIMode from './components/MobileAIMode';
import MobileNav from './components/MobileNav';
import MobileCanvasDrawer from './components/MobileCanvasDrawer';
import MobileCodePage from './components/MobileCodePage';
import MobilePreviewPage from './components/MobilePreviewPage';
import ProjectCreationModal from './components/ProjectCreationModal';
import Home from './components/Home';
import GlobalSearch from './components/GlobalSearch';
import TenantSwitchOverlay from './components/TenantSwitchOverlay';
import {exportConversationToPDF} from './utils/pdfExport';
import miaPlatformDataService from './services/MiaPlatformDataService';
import miaFlowAuth from './auth/MiaFlowAuth';
import GitLabAuth from './auth/GitLabAuth';
import GitHubAuth from './auth/GitHubAuth';
import BitbucketAuth from './auth/BitbucketAuth';
import AzureDevOpsAuth from './auth/AzureDevOpsAuth';
import analyticsService from './services/analyticsService';
import {useAutoDiffApplier} from './hooks/useAutoDiffApplier.jsx';
import {getBeHeaders, getBffHeaders} from './utils/apiHeaders';
import VirtualFileSystem from './utils/virtualFileSystem';
import {isMiaFlowTool} from './utils/toolApplication';
import {registerModalStateCallback, showScriptExecutionConfirmation, showScriptExecutionError} from './utils/scriptExecutionConfirmation.jsx';
/* Lines 18-22 omitted */

import SlashCommandMenu from './components/SlashCommandMenu';
import {filterSpeckitCommands, buildCommandsFromSpecs} from './constants/speckitCommands';
import CreateProjectModal from './components/CreateProjectModal';
import './App.css';
import './auth/auth.css';
import './Mobile.css';

const AI_API_PREFIX = 'api/catalog'

function getPlaybookAgentRefs(playbook) {
    return ((playbook.flow?.nodes) || [])
        .filter(n => n.type === 'agent' && n.ref)
        .map(n => ({ itemId: n.ref, id: n.ref, name: n.ref }));
}
function resolveRefNames(items) {
    return (items || []).map(r => (typeof r === 'string' ? r : (r.name || r.itemId))).filter(Boolean);
}
// Compute the scoped ADK app_name: {playbook_snake}_{agent_snake}
// Mirrors catalog_agent_service._snake() on the backend.
function _scopedName(playbookName, agentRef) {
    const snake = s => (s || '').toLowerCase().replace(/[-\s.]+/g, '_');
    return `${snake(playbookName)}_${snake(agentRef)}`;
}
// Find the root agent in a playbook's flow graph — the agent that has no
// incoming edges from other nodes (i.e., the orchestrator / entry point).
function getRootAgentRef(playbook) {
    const refs = getPlaybookAgentRefs(playbook);
    if (refs.length === 0) return null;
    if (refs.length === 1) return refs[0];
    const flow = playbook.flow || {};
    const nodes = flow.nodes || [];
    const edges = flow.edges || [];
    if (edges.length === 0) return refs[0];
    // Build ref-string → node id map
    const refToNodeId = {};
    for (const n of nodes) {
        if (n.type === 'agent' && n.ref) refToNodeId[n.ref] = n.id || n.ref;
    }
    // IDs that appear as edge targets (i.e. are sub-agents)
    const targetIds = new Set(edges.map(e => e.target || e.to).filter(Boolean));
    for (const ref of refs) {
        const refId = ref.itemId || ref.id || ref.name;
        const nodeId = refToNodeId[refId] || refId;
        if (!targetIds.has(nodeId)) return ref;
    }
    return refs[0]; // fallback if graph is cyclic
}

/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */

/**
 * Checks if a file should be sent to the AI based on its extension and filename.
 * Returns true for source code and config files, false for binary/asset files.
 */
const isCodeFile = (filePath) => {
    // Extract filename and extension
    const fileName = filePath.split('/').pop();
    const ext = fileName.split('.').pop().toLowerCase();

    // Check if file is in the excluded list (imported from indexedDbFileStorage)
    if (EXCLUDED_FILES_FOR_AI.includes(fileName)) {
        return false;
    }

    // Binary/Asset files to EXCLUDE (never send to AI)
    const binaryExtensions = [
        // Images
        'png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'ico', 'webp', 'avif',
        // Fonts
        'woff', 'woff2', 'ttf', 'otf', 'eot',
        // Videos/Audio
        'mp4', 'mov', 'avi', 'mkv', 'webm', 'mp3', 'wav', 'ogg', 'flac',
        // Archives
        'zip', 'tar', 'gz', 'rar', '7z', 'bz2',
        // Binaries
        'exe', 'dll', 'so', 'dylib', 'bin', 'dmg', 'pkg',
        // Documents
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'
    ];

    if (binaryExtensions.includes(ext)) {
        return false;
    }

    // Source code and config files to INCLUDE
    const codeExtensions = [
        // JavaScript/TypeScript
        'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs',
        // Web
        'html', 'css', 'scss', 'sass', 'less',
        // Python
        'py', 'pyw', 'pyx', 'pyi',
        // Java/Kotlin/Scala
        'java', 'kt', 'kts', 'scala',
        // C/C++
        'c', 'cpp', 'cc', 'cxx', 'h', 'hpp', 'hxx',
        // Other languages
        'go', 'rs', 'rb', 'php', 'swift', 'cs', 'sh', 'bash', 'zsh',
        // Config files
        'json', 'yaml', 'yml', 'toml', 'xml', 'ini', 'conf', 'config',
        // Documentation
        'md', 'mdx', 'txt', 'rst',
        // Build/Project files
        'gradle', 'maven', 'cmake', 'make', 'dockerfile'
    ];

    return codeExtensions.includes(ext);
};

// Renders markdown with Mermaid support and safe external links; memoized for performance
const MemoizedMessageContent = memo(({text, contentType, structuredContent, rawStructuredText, onChoice}) => {
    const markdownComponents = useMemo(() => ({
        code({node, inline, className, children, ...props}) {
            const match = (className || '').match(/language-mermaid/);
            const chartCode = String(children).replace(/\n$/, '');
            return !inline && match ? (
                <Mermaid chart={chartCode} />
            ) : (
                <CodeBlock inline={inline} className={className} {...props}>
                    {children}
                </CodeBlock>
            );
        },
        // Prevent <pre> from being wrapped in <p> tags (HTML validation error)
        p: ({node, children, ...props}) => {
            // Check if paragraph contains a code block
            const hasCodeBlock = React.Children.toArray(children).some(
                child => child?.props?.className?.includes('language-') && !child?.props?.inline
            );
            // Use div instead of p if it contains a code block
            return hasCodeBlock ? <div {...props}>{children}</div> : <p {...props}>{children}</p>;
        },
        // Open links safely in a new tab (prevents reverse tabnabbing)
        // Special handling for file:// links to open in Monaco editor
        a: ({node, href, ...props}) => {
            // Check if it's a file link (format: file://path/to/file.js or file://path/to/file.js#L42)
            if (href?.startsWith('file://')) {
                const [filePath, lineFragment] = href.replace('file://', '').split('#L');
                const lineNumber = lineFragment ? parseInt(lineFragment, 10) : undefined;

                return (
                    <a
                        {...props}
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();

                            // Get context for navigation
                            const projectId = canvasProjectContext?.projectId;
                            const serviceName = canvasProjectContext?.selectedMicroservice?.name;
                            const branch = canvasProjectContext?.selectedBranch || 'main';
                            const tenantId = params.tenantId;

                            // Navigate to /code tab if not already there
                            if (projectId && serviceName && tenantId) {
                                const currentTab = params.tab;

                                // If not on code tab, navigate there first
                                if (currentTab !== 'code') {
                                    navigate(`/chat/${tenantId}/${projectId}/${encodeURIComponent(serviceName)}/${encodeURIComponent(branch)}/code`);
                                }

                                // Dispatch openFile event after a small delay to ensure Canvas is ready
                                setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent('openFile', {
                                        detail: {
                                            filePath,
                                            lineNumber,
                                            projectId,
                                            serviceName
                                        }
                                    }));
                                }, currentTab !== 'code' ? 200 : 0);
                            } else {
                                // Fallback: just dispatch the event if context is missing
                                window.dispatchEvent(new CustomEvent('openFile', {
                                    detail: {
                                        filePath,
                                        lineNumber,
                                        projectId,
                                        serviceName
                                    }
                                }));
                            }
                        }}
                        style={{ color: '#42A5F5', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {props.children}
                    </a>
                );
            }

            // Regular external links
            return (
                <a {...props} href={href} rel="noopener noreferrer" target="_blank">
                    {props.children}
                </a>
            );
        },
    }), []);

    return (
        <>
            {/* Render structured content if present */}
            {contentType && structuredContent && (
                <InlineUIRenderer
                    content={structuredContent}
                    onChoice={onChoice}
                    rawText={rawStructuredText}
                    type={contentType}
                />
            )}

            {/* Render markdown text if present */}
            {text && (
                <ReactMarkdown
                    components={markdownComponents}
                    remarkPlugins={[remarkGfm]}
                >
                    {text}
                </ReactMarkdown>
            )}
        </>
    );
});



const MAX_SUGGESTED_TOOLS = 128;
function MainChat({restartOnboardingTour, onNewConversation, isCreationModalOpen, creationProjectName, creationServiceName, creationStep, creationMode = 'code', hideCanvas = false}) {
    // React Router hooks (must be at the top before any other code)
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { user, authorizedFetch } = useAuth();

    // Core state for the chat interface
    const [messages, setMessages] = useState([]); // All messages in the current chat
    const [inputValue, setInputValue] = useState(() => {
        // Load input value from localStorage if available
        try {
            return localStorage.getItem('chatInputValue') || '';
        } catch (e) {
            return '';
        }
    }); // Text in the message input field
    const [showSendPromptBanner, setShowSendPromptBanner] = useState(false); // Banner to highlight send button for new projects
    // Spec-Kit slash command menu state
    const [showSlashMenu, setShowSlashMenu] = useState(false);
    const [slashMenuIndex, setSlashMenuIndex] = useState(0);
    const [catalogSpecCommands, setCatalogSpecCommands] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Controls loading spinners and disables inputs
    const [abortController, setAbortController] = useState(null); // For canceling ongoing requests
    const [conversationStatus, setConversationStatus] = useState(''); // Status message while LLM is thinking
    const [toast, setToast] = useState(null); // Toast notification state

    // URL parameter validation state
    const [urlValidationError, setUrlValidationError] = useState(null);
    const [showProjectSelector, setShowProjectSelector] = useState(false);
    const [tenantProjects, setTenantProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [isInlineCreateProjectModalOpen, setIsInlineCreateProjectModalOpen] = useState(false);
    // Compose → Code transfer picker
    const [showComposeToCodePicker, setShowComposeToCodePicker] = useState(false);
    const [aiMode, setAiMode] = useState(() => {
        // Load AI mode from localStorage, default to 'agent'
        return localStorage.getItem('aiMode') || 'agent';
    }); // 'agent' or 'plan' mode
    const [isAiModeDropdownOpen, setIsAiModeDropdownOpen] = useState(false);

    // AI Model state
    const [selectedModel, setSelectedModel] = useState(() => {
        return localStorage.getItem('selectedModel') || 'vertex_ai/claude-haiku-4-5';
    });
    const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);

    const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
    const [showSendOptions, setShowSendOptions] = useState(false);
    const [messageQueue, setMessageQueue] = useState([]); // Queue of {inputValue, attachedFiles}

    const [isMobileCanvasDrawerOpen, setIsMobileCanvasDrawerOpen] = useState(false);
    const [isMobileCodePageOpen, setIsMobileCodePageOpen] = useState(false);
    const [isMobilePreviewPageOpen, setIsMobilePreviewPageOpen] = useState(false);
    const [mobileActiveView, setMobileActiveView] = useState('chat'); // Track active view for mobile nav highlighting
    const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen modal state

    // File attachment state
    const [attachedFiles, setAttachedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false); // Track drag over state

    // AI Instructions state
    const [customAIInstructions, setCustomAIInstructions] = useState('');
    const [customAIInstructionsName, setCustomAIInstructionsName] = useState('');
    const [customAIInstructionsPlaybookData, setCustomAIInstructionsPlaybookData] = useState(null); // Playbook data when instructions come from a playbook
    const [playbookLocksTools, setPlaybookLocksTools] = useState(false); // True only when the active playbook defines tools
    const [isAIInstructionsModalOpen, setIsAIInstructionsModalOpen] = useState(false);
    const prePlaybookSelectedToolsRef = useRef(undefined); // Saved selectedTools before a playbook was applied (for reset)
    const miaFlowToolIdsRef = useRef([]); // Latest computed Mia Flow Tools default IDs
    const playbookToolIdsRef = useRef([]); // Tool IDs set by the active playbook (locked from user deselection)
    const availableToolsRef = useRef([]); // Always-current mirror of availableTools for stale-closure-safe access
    const selectedToolsRef = useRef(null); // Always-current mirror of selectedTools for stale-closure-safe access

    // Save Prompt Modal state
    const [isSavePromptModalOpen, setIsSavePromptModalOpen] = useState(false);

    // Save Message Modal state
    const [isSaveMessageModalOpen, setIsSaveMessageModalOpen] = useState(false);
    const [messageToSave, setMessageToSave] = useState('');

    // Conversation History Modal state
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    // Script Execution Confirmation Modal state
    const [scriptExecutionModal, setScriptExecutionModal] = useState({ isOpen: false });

    // Context breadcrumb state
    const [aiContext, setAiContext] = useState({items: []});

    // MCP Tools state
    const [availableTools, setAvailableTools] = useState([]);
    const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
    const [toolsLoadError, setToolsLoadError] = useState(null);
    const [toolsAuthRequired, setToolsAuthRequired] = useState(false);
    const [toolSearchQuery, setToolSearchQuery] = useState('');
    const [selectedTools, setSelectedTools] = useState(() => {
        // Load selected tools from localStorage
        try {
            const saved = localStorage.getItem('selectedTools');
            if (saved) {
                return JSON.parse(saved);
            }
            // If no saved selection (first login), return null to trigger default selection after tools load
            return null;
        } catch {
            return null;
        }
    });
    const [expandedCategories, setExpandedCategories] = useState(() => {
        // Load expanded categories from localStorage, default Mia Flow Tools expanded
        try {
            const saved = localStorage.getItem('expandedToolCategories');
            if (saved) {
                return JSON.parse(saved);
            }
            // Default: expand Mia Flow Tools
            return { 'Mia Flow Tools': true };
        } catch {
            return { 'Mia Flow Tools': true };
        }
    });

    // Dynamic prompt suggestions for the empty state
    const [promptSuggestions, setPromptSuggestions] = useState(null);
    const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

    // Track tools that were saved with the current session
    const [sessionTools, setSessionTools] = useState(null);
    const [showToolChangeNotice, setShowToolChangeNotice] = useState(false);
    const toolsModalOpenRef = useRef(null);  // Track tools when modal opens for comparison
    const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);

    // Utility function to convert tool names to friendly display names
    const getToolFriendlyName = (toolName) => {
        // Handle specific patterns
        if (toolName.includes('azure_devops')) {
            return toolName.replace(/.*azure_devops[_-]?/i, '').split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ').replace(/oauth/i, 'OAuth');
        }
        if (toolName.includes('atlassian')) {
            return toolName.replace(/.*atlassian[_-]?/i, '').split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
        }
        // Generic conversion: remove prefixes and convert snake_case to Title Case
        return toolName
            .replace(/^(agents\.hub\.tools\.|get_|list_|create_|update_|delete_)/i, '')
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Canvas ref for triggering refresh
    const canvasRef = useRef(null);

    // Track last project tracked to prevent duplicate POST calls
    const lastTrackedProjectRef = useRef(null);

    // Token tracking state
    const [totalTokens, setTotalTokens] = useState(0); // Total tokens consumed in session
    const [inputTokens, setInputTokens] = useState(0); // Input/prompt tokens
    const [outputTokens, setOutputTokens] = useState(0); // Output/response tokens

    // State for Canvas mode (always enabled)
    const [isCanvasMode] = useState(true); // Canvas mode for full-screen document/code viewing (always on)
    const [canvasContent, setCanvasContent] = useState(() => {
        // Load Canvas content from localStorage if available
        const savedContent = localStorage.getItem('canvasContent');
        return savedContent ? JSON.parse(savedContent) : null;
    }); // Content to display in Canvas mode
    const [canvasProjectContext, setCanvasProjectContext] = useState(null); // Project context from Canvas (loaded from IndexedDB)
    const [filesModifiedInCanvas, setFilesModifiedInCanvas] = useState(false); // Track if files were modified in Canvas mode
    const [enableMcpApps, setEnableMcpApps] = useState(() => {
        const saved = localStorage.getItem('ENABLE_MCP_APPS');
        return saved === 'true';
    }); // Enable MCP Apps feature flag
    const [chatSidebarWidth, setChatSidebarWidth] = useState(() => {
        // Load sidebar width from localStorage or default to 400px
        return parseInt(localStorage.getItem('chatSidebarWidth') || '400', 10);
    }); // Width of chat sidebar when in canvas mode

    // Refs for DOM elements
    const chatMessagesRef = useRef(null); // For scrolling the message list
    const resizeHandleRef = useRef(null); // For resize handle
    const shouldAutoScrollRef = useRef(true); // Track if auto-scroll should happen (user hasn't scrolled up)
    const lastScrollHeightRef = useRef(0); // Track last scroll height to detect new content
    const inputRef = useRef(null); // For focusing the text input
    const isSavingRef = useRef(false); // Track if save is in progress to prevent duplicates
    const restoredMessageCountRef = useRef(0); // Track how many messages were restored from localStorage (not new)
    const manualCanvasSetRef = useRef(false); // Track if canvas was manually set (e.g., for images)
    const lastLoadedProjectRef = useRef(null); // Track last loaded project to prevent redundant fetches

    /**
     * Load selectedResource from localStorage
     * This contains the full service data including name, type, and all metadata
     * @returns {object|null} - Parsed selectedResource object or null if not found
     */
    const loadSelectedResourceFromStorage = useCallback(() => {
        try {
            const savedResourceStr = localStorage.getItem('mia_selectedResource');
            if (savedResourceStr) {
                const selectedResource = JSON.parse(savedResourceStr);
                // eslint-disable-next-line no-console
                console.log('📌 Loaded selectedResource from localStorage:', selectedResource?.data?.name);
                return selectedResource;
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Failed to parse selectedResource from localStorage:', err);
        }
        return null;
    }, []);

    // Register script execution modal callback
    useEffect(() => {
        registerModalStateCallback(setScriptExecutionModal);
    }, []);

    // Load Canvas project context from IndexedDB on mount
    useEffect(() => {
        const loadCanvasContext = async () => {
            // ONLY load from IndexedDB if we're on /chat with NO parameters
            // If URL has project info, let the URL validation effect handle it
            if (location.pathname !== '/chat' && location.pathname !== '/chat/') {
                return;
            }

            try {
                // Try to migrate from localStorage first
                await indexedDbStorage.migrateFromLocalStorage('canvasProjectContext');

                // Load from IndexedDB
                const savedContext = await indexedDbStorage.getItem('canvasProjectContext');
                if (savedContext && savedContext.projectId) {
                    // Redirect to the saved project URL instead of loading context directly
                    const tenantId = savedContext.tenant?.tenantId || savedContext.tenant?.id;
                    const projectId = savedContext.projectId;

                    if (tenantId && projectId) {
                        // Preserve conversationId query parameter if it exists
                        const searchParams = createSearchParams(location.search);
                        const conversationIdParam = searchParams.get('conversationId');
                        const path = `/chat/${tenantId}/${projectId}`;
                        const finalPath = conversationIdParam ? `${path}?conversationId=${conversationIdParam}` : path;

                        console.log('🔗 Redirecting to saved project from IndexedDB');
                        navigate(finalPath, { replace: true });
                    }
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load Canvas project context:', error);
            }
        };
        loadCanvasContext();
    }, [location.pathname, navigate]);

    // Initialize real-time collaboration if feature toggle is enabled
    useEffect(() => {
        const initCollaboration = async () => {
            const enableCollaboration = localStorage.getItem('enableCollaboration') === 'true';

            if (!enableCollaboration) {
                return;
            }

            const projectId = canvasProjectContext?.projectId;
            const userId = user?.id || user?.sub || user?.email;
            const username = user?.name || user?.email || 'Anonymous';

            if (!projectId || !userId) {
                return;
            }

            try {
                const syncService = (await import('./services/syncService')).default;
                await syncService.initialize(userId, projectId, username);
                console.log('✅ Real-time collaboration initialized');
            } catch (error) {
                console.error('❌ Failed to initialize collaboration:', error);
            }
        };

        initCollaboration();

        // Cleanup on unmount
        return () => {
            const enableCollaboration = localStorage.getItem('enableCollaboration') === 'true';
            if (enableCollaboration) {
                import('./services/syncService').then(module => {
                    module.default.disconnect();
                });
            }
        };
    }, [canvasProjectContext?.projectId, user]);

    // State for conversation management
    const [convName, setConvName] = useState(''); // The user-defined name of the conversation
    const [conversationId, setConversationId] = useState(null); // The unique ID of the saved conversation
    const [sessionId, setSessionId] = useState(null); // The backend session ID for this chat instance

    // Track which sessions have already received full file content to avoid sending it multiple times
    const sessionFilesSent = useRef(new Set()).current;

    // Auto diff applier hook - enables automatic diff application from backend merge requests
    const { isApplyingDiff, diffProgress } = useAutoDiffApplier({
        conversationId: conversationId,
        projectId: canvasProjectContext?.projectId,
        serviceName: canvasProjectContext?.selectedMicroservice?.name,
        sessionId: sessionId,
        enabled: true,
        onDiffApplied: (result) => {
            console.log('✅ [AutoDiffApplier] Diff applied successfully:', result.filePath);

            // Add green success message to chat with clickable file link
            const fileName = result.filePath.split('/').pop();
            const successMsg = Messages.createInfoMessage(
                `✅ Successfully applied changes to [${fileName}](file://${result.filePath})\n\n` +
                `Click the filename to view the changes in the editor.`
            );
            setMessages(prevMessages => Messages.addMessage(prevMessages, successMsg));
        },
        onDiffError: (error) => {
            console.error('❌ [AutoDiffApplier] Diff application failed:', error);
            // Extract error message as string
            let errorMessage = 'Unknown error';
            if (typeof error === 'string') {
                errorMessage = error;
            } else if (error && typeof error === 'object') {
                errorMessage = error.error || error.message || JSON.stringify(error);
            }
            showToast(`Failed to apply diff: ${errorMessage}`, 'error');
        }
    });

    // Helper function to show toast notifications
    const showToast = useCallback((message, type = 'error') => {
        setToast({message, type});
    }, []);

    const hideToast = useCallback(() => {
        setToast(null);
    }, []);

    // Persist session state to localStorage (per project)
    useEffect(() => {
        if (sessionId && canvasProjectContext?.projectId) {
            try {
                const projectId = canvasProjectContext.projectId;
                const sessionState = {
                    sessionId,
                    conversationId: conversationId || null,
                    timestamp: Date.now(),
                    wasStreaming: isLoading // Track if we're currently streaming
                };
                localStorage.setItem(`chatSessionState_${projectId}`, JSON.stringify(sessionState));
            } catch (e) {
                console.error('Failed to save session state to localStorage:', e);
            }
        }
    }, [sessionId, conversationId, isLoading, canvasProjectContext?.projectId]);

    // Persist messages to localStorage (per project)
    useEffect(() => {
        if (messages.length > 0 && canvasProjectContext?.projectId) {
            try {
                const projectId = canvasProjectContext.projectId;
                localStorage.setItem(`chatMessagesBackup_${projectId}`, JSON.stringify(messages));
            } catch (e) {
                console.error('Failed to save messages to localStorage:', e);
            }
        }
    }, [messages, canvasProjectContext?.projectId]);

    // Track previous projectId to detect actual project switches
    const prevProjectIdRef = useRef(null);
    const hasRestoredRef = useRef(false);

    // Effect to restore session when project is loaded or changes
    useEffect(() => {
        const currentProjectId = canvasProjectContext?.projectId;
        const previousProjectId = prevProjectIdRef.current;

        if (currentProjectId) {
            try {
                const savedSession = localStorage.getItem(`chatSessionState_${currentProjectId}`);
                const savedMessages = localStorage.getItem(`chatMessagesBackup_${currentProjectId}`);

                if (savedSession && savedMessages) {
                    const parsed = JSON.parse(savedSession);
                    const messagesParsed = JSON.parse(savedMessages);

                    // Check if session is recent (less than 24 hours old)
                    const isRecent = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;

                    if (isRecent) {
                        // Always restore if we haven't restored yet (initial load from IndexedDB)
                        // OR if we're switching to a different project
                        const shouldRestore = !hasRestoredRef.current ||
                                             (previousProjectId && previousProjectId !== currentProjectId);

                        if (shouldRestore && parsed.sessionId !== sessionId) {
                            setSessionId(parsed.sessionId);
                            setMessages(messagesParsed);
                            // Restore conversationId to prevent auto-save from treating
                            // the restored messages as a new unsaved conversation
                            if (parsed.conversationId) {
                                setConversationId(parsed.conversationId);
                            }
                            // Record how many messages were restored so auto-save
                            // won't fire unless new messages are added this session
                            restoredMessageCountRef.current = messagesParsed.length;
                            console.log('📥 Restored session for project:', currentProjectId, 'Session:', parsed.sessionId);
                            hasRestoredRef.current = true;
                        }
                    }
                } else {
                    // Only clear if we're switching FROM another project TO a different project
                    // Don't clear on initial mount
                    if (previousProjectId && previousProjectId !== currentProjectId && sessionId) {
                        console.log('🆕 Switching to new project, creating new session');
                        setSessionId(null);
                        setMessages([]);
                        hasRestoredRef.current = false;
                    }
                }
            } catch (e) {
                console.error('Failed to restore session for project:', e);
            }
        }

        // Update the ref with current projectId
        prevProjectIdRef.current = currentProjectId;
    }, [canvasProjectContext?.projectId, sessionId]); // Trigger when projectId or sessionId changes

    // Check if we need to reconnect to a streaming session (per project)
    // Only run once when project or sessionId changes, not on every message
    useEffect(() => {
        if (canvasProjectContext?.projectId && sessionId) {
            try {
                const projectId = canvasProjectContext.projectId;
                const savedSession = localStorage.getItem(`chatSessionState_${projectId}`);

                if (savedSession && messages.length > 0) {
                    const parsed = JSON.parse(savedSession);

                    // Check if we were streaming AND the session hasn't been checked yet
                    const lastMessage = messages[messages.length - 1];
                    const wasStreaming = parsed.wasStreaming || false;
                    const lastCheckedTimestamp = parsed.lastCheckedTimestamp || 0;

                    // Only show the message if:
                    // 1. We were streaming
                    // 2. Last message is received
                    // 3. We haven't checked this session yet (prevent multiple triggers)
                    if (wasStreaming && lastMessage && lastMessage.type === 'received' &&
                        parsed.timestamp > lastCheckedTimestamp) {
                        console.log('🔄 Detected interrupted stream, session can continue...');
                        // Set a flag that we recovered from interruption
                        setConversationStatus('Session restored - ready to continue');
                        setTimeout(() => setConversationStatus(''), 3000);

                        // Mark this session as checked to prevent repeated triggers
                        parsed.lastCheckedTimestamp = Date.now();
                        localStorage.setItem(`chatSessionState_${projectId}`, JSON.stringify(parsed));
                    }
                }
            } catch (e) {
                console.error('Failed to check for reconnection:', e);
            }
        }
    }, [canvasProjectContext?.projectId, sessionId]); // Only re-check when project or session changes

    // Persist canvas content to localStorage
    useEffect(() => {
        if (canvasContent) {
            localStorage.setItem('canvasContent', JSON.stringify(canvasContent));
        } else {
            localStorage.removeItem('canvasContent');
        }
    }, [canvasContent]);

    // Persist AI mode to localStorage
    useEffect(() => {
        localStorage.setItem('aiMode', aiMode);

        // Canvas is always active, notify parent
        window.dispatchEvent(new CustomEvent('canvasToggled', {
            detail: {isActive: true}
        }));

        // Auto-populate canvas with content when switching modes
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.type === 'received') {
            setCanvasContent({
                type: 'document',
                content: lastMessage.text,
                title: 'Generated Content'
            });
        } else if (!canvasContent) {
            // Provide placeholder content only if no content exists
            setCanvasContent({
                type: 'document',
                content: '# Ready to Flow!?! Create your first service or load a project to get started.',
                title: 'Canvas'
            });
        }
    }, [aiMode, messages]);

    // Fetch available MCP tools on mount
    useEffect(() => {
        const fetchTools = async () => {
            try {
                // Scope comes from x-mia-acl-context (getBffHeaders), not a tenantId query param.
                const baseUrl = config.bffUrl || window.location.origin;
                const response = await authorizedFetch(`${baseUrl}/${AI_API_PREFIX}/ai-tools`, {
                    headers: getBffHeaders()
                });

                if (!response.ok) {
                    console.error('❌ Failed to fetch tools, status:', response.status);
                    setToolsLoadError(`HTTP ${response.status}`);
                    return;
                }

                const data = await response.json();
                if (!data.success || !data.data?.items) {
                    console.warn('⚠️ Failed to load tools:', data.error || 'Unknown error');
                    setToolsLoadError(data.details || data.error || 'Failed to load tools');
                    return;
                }

                const allItems = data.data.items;
                console.log('📦 Catalog Tools — fetched', allItems.length, 'tools');
                setAvailableTools(allItems);
                setToolsLoadError(null);
                setToolsAuthRequired(false);

                // Rimuovi dai selected tools quelli che sono disabilitati o che non esistono più (ID format mismatch)
                setSelectedTools(prev => {
                    if (!prev) return prev;
                    // Skip filtering/reset if a playbook is being applied via URL - applyPlaybook will handle tool selection
                    const urlPlaybookId = new URLSearchParams(window.location.search).get('playbookId');
                    if (urlPlaybookId) return prev;
                    const allToolIds = allItems.map(tool => tool.id);
                    const enabledToolIds = allItems
                        .filter(tool => tool.enabled !== false)
                        .map(tool => tool.id);
                    const filtered = prev.filter(toolId => enabledToolIds.includes(toolId));
                    // If all stored IDs are stale (none match any known tool ID),
                    // return null to trigger the default selection logic
                    const anyKnown = prev.some(toolId => allToolIds.includes(toolId));
                    if (!anyKnown && prev.length > 0) {
                        console.warn('⚠️ Stored tool selection is stale (ID format mismatch), resetting to defaults');
                        return null;
                    }
                    return filtered;
                });
            } catch (error) {
                console.error('❌ Error fetching catalog tools:', error);
                setToolsLoadError(error.message || 'Network error');
            }
        };

        fetchTools();
    }, []);

    // Keep availableToolsRef in sync so callbacks with empty dep arrays can always read the latest list
    useEffect(() => { availableToolsRef.current = availableTools; }, [availableTools]);

    // Keep selectedToolsRef in sync so handleSendMessage always reads the latest selection
    useEffect(() => { selectedToolsRef.current = selectedTools; }, [selectedTools]);

    // Set default tool selection: always ensure Mia Flow Tools are selected for each new browser session
    useEffect(() => {
        if (availableTools.length === 0) return;

        const miaFlowToolIds = availableTools
            .filter(tool => tool.enabled !== false && tool.type === 'built-in' && isMiaFlowTool(tool) && tool.category === 'Mia Flow Tools')
            .map(tool => tool.id);
        miaFlowToolIdsRef.current = miaFlowToolIds;

        // Normalize stale/mismatched tool IDs using the now-available tool list.
        // Strips any legacy "category:name" prefix and handles name→id migration.
        const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
        const normalizeIds = (ids) => {
            if (!ids || ids.length === 0) return ids;
            const knownIds = new Set(availableTools.map(t => t.id));
            const nameToId = new Map(availableTools.map(t => [t.name, t.id]));
            if (ids.every(id => knownIds.has(id))) return ids; // all valid, no-op
            return [...new Set(ids.flatMap(rawId => {
                if (knownIds.has(rawId)) return [rawId];
                // Map from display name (old format) to id
                if (nameToId.has(rawId)) return [nameToId.get(rawId)];
                // Strip legacy "category:name" format
                const sep = rawId.indexOf(':');
                const stripped = sep >= 0 ? rawId.substring(sep + 1) : rawId;
                if (knownIds.has(stripped)) return [stripped];
                if (nameToId.has(stripped)) return [nameToId.get(stripped)];
                // Try kebab-case variant (e.g. snake_case from old ADK)
                const kebab = toKebab(stripped);
                if (knownIds.has(kebab)) return [kebab];
                return []; // unknown ID — drop it
            }))];
        };

        const sessionApplied = sessionStorage.getItem('miaFlowToolsDefaultApplied');

        if (!sessionApplied) {
            // New browser session: normalize + merge Mia Flow Tools into whatever was persisted
            setSelectedTools(prev => {
                const base = normalizeIds(prev || []);
                const merged = [...new Set([...miaFlowToolIds, ...base])];
                console.log('🎯 New session - ensuring Mia Flow Tools selected:', miaFlowToolIds.length, 'tools added to selection');
                return merged;
            });
            sessionStorage.setItem('miaFlowToolsDefaultApplied', '1');
        } else if (selectedTools === null) {
            // Tools loaded but no selection at all (edge case): fall back to Mia Flow Tools
            console.log('🎯 No selection found - defaulting to Mia Flow Tools:', miaFlowToolIds.length, 'tools');
            setSelectedTools(miaFlowToolIds);
        } else {
            // Re-normalize any stale IDs (e.g. from a playbook applied before tools finished loading)
            setSelectedTools(prev => normalizeIds(prev));
        }
    }, [availableTools]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist selected tools to localStorage
    useEffect(() => {
        // Only persist if selectedTools is not null (i.e., it's been initialized)
        if (selectedTools !== null) {
            localStorage.setItem('selectedTools', JSON.stringify(selectedTools));
        }
    }, [selectedTools]);

    // Fetch dynamic suggestions whenever the empty state becomes active (no messages),
    // or when mode/tools/project context changes.
    useEffect(() => {
        if (messages.length > 0) return; // Only needed for the empty state
        if (!config.agentUrl) return;

        let cancelled = false;
        const fetchSuggestions = async () => {
            setIsSuggestionsLoading(true);
            try {
                const toolNames = Array.isArray(selectedTools)
                    ? selectedTools.slice(0, 40) // cap to avoid huge payloads
                    : [];
                const body = {
                    mode: hideCanvas ? 'compose' : 'code',
                    tools: toolNames,
                    project: canvasProjectContext?.name || canvasProjectContext?.projectId || null,
                    service: hideCanvas ? null : (canvasProjectContext?.selectedMicroservice?.name || null),
                    intent: customAIInstructionsName || null,
                };
                const response = await authorizedFetch(`${config.agentUrl}/api/suggestions`, {
                    method: 'POST',
                    headers: { ...getBeHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!response.ok) throw new Error(`suggestions: ${response.status}`);
                const data = await response.json();
                if (!cancelled && Array.isArray(data.suggestions)) {
                    setPromptSuggestions(data.suggestions);
                }
            } catch (err) {
                // Silently fall back to null — hardcoded fallbacks render instead
                if (!cancelled) setPromptSuggestions(null);
            } finally {
                if (!cancelled) setIsSuggestionsLoading(false);
            }
        };
        fetchSuggestions();
        return () => { cancelled = true; };
    // Re-fetch when mode, tools selection, project context, or active playbook change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length, hideCanvas, selectedTools, canvasProjectContext?.projectId, canvasProjectContext?.selectedMicroservice?.name, customAIInstructionsName]);

    // Apply playbook pre-configuration when playbookId is in the URL -----------
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const playbookId = searchParams.get('playbookId');
        if (!playbookId) return;

        // Try sessionStorage first (set by PlaybookLauncher just before navigating)
        let playbookData = null;
        try {
            const stored = sessionStorage.getItem('mia_active_playbook');
            if (stored) {
                playbookData = JSON.parse(stored);
                if (playbookData?.itemId !== playbookId) playbookData = null; // stale
            }
        } catch (_) { /* ignore */ }

        const applyPlaybook = async (playbook) => {
            sessionStorage.removeItem('mia_active_playbook');

            // Build custom instructions from agents + prompts
            const agentNames = getPlaybookAgentRefs(playbook).map(a => a.name || a.itemId).filter(Boolean);
            const promptNames = resolveRefNames(playbook.prompts);
            const specNames = resolveRefNames(playbook.specs);
            const skillNames = resolveRefNames(playbook.skills);

            let instructions = `[PLAYBOOK: ${playbook.name}]\n`;
            if (playbook.description) instructions += `${playbook.description}\n\n`;
            if (agentNames.length) instructions += `Active agents: ${agentNames.join(', ')}\n`;
            if (promptNames.length) instructions += `Loaded prompts: ${promptNames.join(', ')}\n`;
            if (specNames.length) instructions += `Loaded specs: ${specNames.join(', ')}\n`;
            if (skillNames.length) instructions += `Active skills: ${skillNames.join(', ')}\n`;

            setCustomAIInstructions(instructions);
            setCustomAIInstructionsName(playbook.name);
            setCustomAIInstructionsPlaybookData(playbook);
            // Playbooks always run in Agent mode — force it regardless of the previous setting
            setAiMode('agent');
            window.dispatchEvent(new CustomEvent('activePlaybookChanged', { detail: { name: playbook.name, mode: playbook.mode || 'chat' } }));

            const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
            const baseUrl = config.bffUrl || window.location.origin;

            // Set scoped ADK appName when the playbook has agents.
            // Use the root agent (no incoming edges) as the entry point.
            const rootAgent = getRootAgentRef(playbook);
            if (rootAgent) {
                const rootRef = rootAgent.itemId || rootAgent.id || rootAgent.name;
                AdkClient.setOverrideAppName(_scopedName(playbook.name, rootRef));
            } else {
                AdkClient.setOverrideAppName(null);
            }

            // Collect tools from all agents in the playbook, then union with playbook-level tools
            const agentRefs = getPlaybookAgentRefs(playbook);
            let allAgentTools = [];
            if (agentRefs.length > 0 && tenantId) {
                const toolsPerAgent = await Promise.all(
                    agentRefs.map(async (agentRef) => {
                        const agentItemId = agentRef.itemId || agentRef.id;
                        if (!agentItemId) return [];
                        try {
                            const resp = await authorizedFetch(
                                `${baseUrl}/${AI_API_PREFIX}/ai-agents/${tenantId}/${agentItemId}/latest`,
                                { headers: getBffHeaders() }
                            );
                            if (resp.ok) {
                                const result = await resp.json();
                                const fetchedAgent = result.agent || result.data || result;
                                return fetchedAgent.resources?.tools || [];
                            }
                        } catch (e) {
                            console.error('Failed to fetch agent tools for playbook:', e);
                        }
                        return [];
                    })
                );
                allAgentTools = toolsPerAgent.flat();
            }

            const playbookDirectTools = (playbook.tools || [])
                .map(t => t.itemId || t.name || t)
                .filter(Boolean);
            const rawTools = [...new Set([...allAgentTools, ...playbookDirectTools])];

            // Normalize tool IDs: agents store tools by metadata.name slug (e.g. "list-flow-types").
            // Map each raw ID to a known tool.id, also handling legacy category:name and snake_case.
            const resolveToolIds = (rawIds) => {
                const tools = availableToolsRef.current;
                if (tools.length === 0) return rawIds;
                const knownIds = new Set(tools.map(t => t.id));
                const nameToId = new Map(tools.map(t => [t.name, t.id]));
                const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
                return [...new Set(rawIds.flatMap(rawId => {
                    if (knownIds.has(rawId)) return [rawId];
                    if (nameToId.has(rawId)) return [nameToId.get(rawId)];
                    // Strip legacy "category:name" format
                    const colonIdx = rawId.indexOf(':');
                    const stripped = colonIdx >= 0 ? rawId.substring(colonIdx + 1) : rawId;
                    if (knownIds.has(stripped)) return [stripped];
                    if (nameToId.has(stripped)) return [nameToId.get(stripped)];
                    // Try kebab-case variant (snake_case from old ADK / playbooks)
                    const kebab = toKebab(stripped);
                    if (knownIds.has(kebab)) return [kebab];
                    const kebabRaw = toKebab(rawId);
                    if (knownIds.has(kebabRaw)) return [kebabRaw];
                    return [];
                }))];
            };
            const allTools = resolveToolIds(rawTools);
            playbookToolIdsRef.current = allTools;

            if (allTools.length > 0) {
                setPlaybookLocksTools(true);
                setSelectedTools(prev => {
                    if (prePlaybookSelectedToolsRef.current === undefined) {
                        prePlaybookSelectedToolsRef.current = prev;
                    }
                    return allTools;
                });
            } else {
                setPlaybookLocksTools(true);
                setSelectedTools(prev => {
                    if (prePlaybookSelectedToolsRef.current === undefined) {
                        prePlaybookSelectedToolsRef.current = prev;
                    }
                    return [];
                });
            }

            console.log('🎯 Applied playbook configuration:', playbook.name);
        };

        if (playbookData) {
            applyPlaybook(playbookData);
        } else {
            // Fetch from API — scope comes from x-mia-acl-context (getBffHeaders), not tenantId.
            const baseUrl = config.bffUrl || window.location.origin;
            const qs = new URLSearchParams({ page: '1', perPage: '100' });
            fetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, {
                headers: getBffHeaders()
            })
                .then(r => r.json())
                .then(result => {
                    if (result.success && result.data) {
                        const found = (result.data.items || []).find(i => i.itemId === playbookId);
                        if (found) applyPlaybook(found);
                    }
                })
                .catch(() => {});
        }
    // Only run once when playbookId first appears in the URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);
    // -------------------------------------------------------------------------

    // Auto-apply the default playbook for this mode (chat vs code) if:
    //   1. No ?playbookId in the current URL (URL-based playbook takes priority)
    //   2. No playbook is already active (customAIInstructionsPlaybookData is null)
    //   3. A default playbook ID is saved in localStorage for this mode
    // Runs once on mount so it doesn't interfere with later navigation.
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('playbookId')) return; // URL-based playbook wins

        const localStorageKey = hideCanvas ? 'DEFAULT_CHAT_PLAYBOOK_ID' : 'DEFAULT_CODE_PLAYBOOK_ID';
        const defaultPlaybookId = localStorage.getItem(localStorageKey);
        if (!defaultPlaybookId) return;

        const baseUrl = config.bffUrl || window.location.origin;
        const qs = new URLSearchParams({ page: '1', perPage: '100' });

        authorizedFetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, { headers: getBffHeaders() })
            .then(r => r.json())
            .then(result => {
                if (result.success && result.data) {
                    const found = (result.data.items || []).find(i => i.itemId === defaultPlaybookId);
                    if (found) {
                        // Build instructions the same way as the URL-based applyPlaybook
                        const agentNames = getPlaybookAgentRefs(found).map(a => a.name || a.itemId).filter(Boolean);
                        const promptNames = resolveRefNames(found.prompts);
                        const specNames = resolveRefNames(found.specs);
                        const skillNames = resolveRefNames(found.skills);

                        //TODO here mus the passed to ADK and ADK decide what to use and not all in plaintext!
                        let instructions = `[PLAYBOOK: ${found.name}]\n`;
                        if (found.description) instructions += `${found.description}\n\n`;
                        if (agentNames.length) instructions += `Active agents: ${agentNames.join(', ')}\n`;
                        if (promptNames.length) instructions += `Loaded prompts: ${promptNames.join(', ')}\n`;
                        if (specNames.length) instructions += `Loaded specs: ${specNames.join(', ')}\n`;
                        if (skillNames.length) instructions += `Active skills: ${skillNames.join(', ')}\n`;

                        handleApplyAIInstructions(instructions, found.name, null, found);
                    }
                }
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount
    // -------------------------------------------------------------------------

    // Hide banner when user clicks anywhere on screen
    useEffect(() => {
        const handleClickAnywhere = () => {
            if (showSendPromptBanner) {
                setShowSendPromptBanner(false);
            }
        };

        if (showSendPromptBanner) {
            document.addEventListener('click', handleClickAnywhere);
        }

        return () => {
            document.removeEventListener('click', handleClickAnywhere);
        };
    }, [showSendPromptBanner]);

    // Re-resolve tool IDs when availableTools becomes available while a playbook is active.
    // This handles the timing case where the playbook was applied (or loaded from a saved
    // conversation) before the tools list was fetched from the server, causing resolveToolIds
    // to fall back to storing the raw catalog names (e.g. "execute-bash-script") instead of
    // the canonical tool names used by the UI.
    useEffect(() => {
        if (!customAIInstructionsPlaybookData || availableTools.length === 0) return;
        const knownIds = new Set(availableTools.map(t => t.id));
        const nameToId = new Map(availableTools.map(t => [t.name, t.id]));
        const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
        const resolveIds = (ids) => [...new Set(ids.flatMap(rawId => {
            if (knownIds.has(rawId)) return [rawId];
            if (nameToId.has(rawId)) return [nameToId.get(rawId)];
            const colonIdx = rawId.indexOf(':');
            const stripped = colonIdx >= 0 ? rawId.substring(colonIdx + 1) : rawId;
            if (knownIds.has(stripped)) return [stripped];
            if (nameToId.has(stripped)) return [nameToId.get(stripped)];
            const kebab = toKebab(stripped);
            if (knownIds.has(kebab)) return [kebab];
            const kebabRaw = toKebab(rawId);
            if (knownIds.has(kebabRaw)) return [kebabRaw];
            return [];
        }))];
        // Also re-resolve the playbook-locked IDs so isPlaybookLocked remains accurate
        if (playbookToolIdsRef.current.length > 0) {
            playbookToolIdsRef.current = resolveIds(playbookToolIdsRef.current);
        }
        setSelectedTools(prev => {
            if (!prev || prev.length === 0) return prev;
            const resolved = resolveIds(prev);
            // Skip state update if nothing changed
            if (resolved.length === prev.length && resolved.every((id, i) => id === prev[i])) return prev;
            return resolved;
        });
    }, [availableTools, customAIInstructionsPlaybookData]);

    // Detect when tools change after session has started
    useEffect(() => {
        if (sessionTools && sessionTools.length > 0 && selectedTools) {
            // Check if current selection is different from session tools
            const currentSet = new Set(selectedTools);
            const sessionSet = new Set(sessionTools);

            const isDifferent =
                currentSet.size !== sessionSet.size ||
                ![...currentSet].every(tool => sessionSet.has(tool));

            if (isDifferent) {
                setShowToolChangeNotice(true);
            } else {
                setShowToolChangeNotice(false);
            }
        }
    }, [selectedTools, sessionTools]);

    // Persist expanded categories to localStorage
    useEffect(() => {
        localStorage.setItem('expandedToolCategories', JSON.stringify(expandedCategories));
    }, [expandedCategories]);

    // Toggle category expansion
    const toggleCategoryExpansion = useCallback((category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    }, []);

    // Handle tool selection
    const handleToolSelection = useCallback((tool, isEnabled = true) => {
        // Non permettere la selezione di tool disabilitati
        if (!isEnabled) {
            return;
        }

        // Use tool.id (metadata.name slug) for unique identification
        const toolId = tool.id;
        setSelectedTools(prev => {
            // Handle case when prev is null (shouldn't happen after initialization, but be safe)
            const current = prev || [];
            const toolIsSelected = current.includes(toolId);
            // If playbook is active and this tool was locked by the playbook, block deselection
            if (customAIInstructionsPlaybookData && playbookLocksTools && playbookToolIdsRef.current.includes(toolId)) {
                return prev;
            }
            if (toolIsSelected) {
                return current.filter(id => id !== toolId);
            } else {
                return [...current, toolId];
            }
        });
    }, [customAIInstructionsPlaybookData, playbookLocksTools]);

    // Handle category selection (select/deselect all tools in category)
    const handleCategorySelection = useCallback((category, tools) => {
        // Filtra solo i tool abilitati
        const enabledTools = tools.filter(tool => tool.enabled !== false);
        const categoryToolIds = enabledTools.map(t => t.id);

        setSelectedTools(prev => {
            // Handle case when prev is null (shouldn't happen after initialization, but be safe)
            const current = prev || [];
            // Locked = tools explicitly set by the active playbook (not just any selected tool)
            const lockedIds = (customAIInstructionsPlaybookData && playbookLocksTools)
                ? categoryToolIds.filter(id => playbookToolIdsRef.current.includes(id))
                : [];
            const unlockableIds = categoryToolIds.filter(id => !lockedIds.includes(id));

            if (unlockableIds.length === 0) return prev; // all tools in this category are playbook-locked

            const allSelected = unlockableIds.every(id => current.includes(id));
            if (allSelected) {
                // Deselect all unlockable tools in this category
                return current.filter(id => !unlockableIds.includes(id));
            } else {
                // Select all unlockable tools in this category
                const newSelected = new Set([...current, ...unlockableIds]);
                return Array.from(newSelected);
            }
        });
    }, [customAIInstructionsPlaybookData, playbookLocksTools]);

    // Cleanup browser sessions
    const cleanedSessionsRef = useRef(new Set());
    const messagesRef = useRef(messages);
    const prevUrlConversationIdRef = useRef(null); // Track previous conversationId from URL

    // Keep messagesRef in sync with messages
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const cleanupBrowserSessions = useCallback(() => {
        // Find all browser session IDs in current messages
        const browserSessionIds = messagesRef.current
            .filter(msg => msg.browserSessionId)
            .map(msg => msg.browserSessionId)
            .filter(sessionId => !cleanedSessionsRef.current.has(sessionId)); // Only cleanup uncleaned sessions

        if (browserSessionIds.length > 0) {
            console.log('🧹 Cleaning up browser sessions:', browserSessionIds);

            // Call cleanup API for each session
            browserSessionIds.forEach(async (sessionId) => {
                // Mark as cleaned immediately to prevent duplicate calls
                cleanedSessionsRef.current.add(sessionId);

                try {
                    const response = await authorizedFetch(`${config.bffUrl}/api/browser/${sessionId}/cleanup`, {
                        method: 'POST',
                        headers: getBffHeaders()
                    });

                    if (response.ok) {
                        console.log(`✅ Browser session ${sessionId} cleaned up`);
                    } else {
                        console.warn(`⚠️ Failed to cleanup browser session ${sessionId}`);
                        // Remove from cleaned set on failure so it can be retried
                        cleanedSessionsRef.current.delete(sessionId);
                    }
                } catch (error) {
                    console.error(`❌ Error cleaning up browser session ${sessionId}:`, error);
                    // Remove from cleaned set on error so it can be retried
                    cleanedSessionsRef.current.delete(sessionId);
                }
            });
        }
    }, []);

    // Close AI mode dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isAiModeDropdownOpen && !event.target.closest('.ai-mode-selector')) {
                setIsAiModeDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isAiModeDropdownOpen]);

    // Close More Options dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMoreOptionsOpen && !event.target.closest('.more-options-dropdown-container')) {
                setIsMoreOptionsOpen(false);
            }
            if (showSendOptions && !event.target.closest('.send-options-container')) {
                setShowSendOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMoreOptionsOpen, showSendOptions]);

    // Effect to monitor token usage
    useEffect(() => {
        const {maxTokens, warningThreshold} = config.tokenLimits;
        const warningLimit = maxTokens * warningThreshold; // 80% = 25.6K

        if (totalTokens >= warningLimit) {
            // 80% limit reached - show warning
            // This warning only shows once per threshold crossing
            const warningShown = sessionStorage.getItem('tokenWarningShown');
            if (!warningShown) {
                showToast(`⚠️ Approaching token limit: ${totalTokens.toLocaleString()}/${maxTokens.toLocaleString()} tokens used`, 'info');
                sessionStorage.setItem('tokenWarningShown', 'true');
            }
        }
    }, [totalTokens, messages.length, showToast]);

    // Effect to load a conversation from the URL when the component mounts or URL changes.
    useEffect(() => {
        // Subscribe to context manager updates
        const unsubscribeContext = contextManager.subscribe((newContext) => {
            setAiContext(newContext);
        });

        // Listen for force new chat event
        const handleForceNewChat = () => {
            // Cleanup browser sessions before resetting chat
            cleanupBrowserSessions();

            // Force reset regardless of current state
            setMessages([]);
            setConvName('');
            setConversationId(null);
            setSessionId(null); // ✅ CRITICAL: Clear session ID to force new session creation
            setSessionTools(null); // Reset session tools for new chat
            setShowToolChangeNotice(false); // Clear tool change notice
            setInputValue('');
            setIsLoading(false);
            setConversationStatus('');
            setTotalTokens(0); // Reset token count
            setInputTokens(0);
            setOutputTokens(0);
            sessionStorage.removeItem('tokenWarningShown'); // Reset warning flag
            manualCanvasSetRef.current = false; // Reset canvas manual flag
            contextManager.clearContext(); // Clear AI context
            if (abortController) {
                abortController.abort();
                setAbortController(null);
            }
            // Clear AdkClient's session file tracking to force resending files on next message
            if (sessionId) {
                AdkClient.clearSessionFiles(sessionId);
                // eslint-disable-next-line no-console
                console.log('🔄 Cleared session file tracking for new chat');
            }
            // Clear localStorage
            try {
                localStorage.removeItem('chatInputValue');
                // Clear all project-specific sessions
                const keys = Object.keys(localStorage);
                keys.forEach(key => {
                    if (key.startsWith('chatSessionState_') || key.startsWith('chatMessagesBackup_')) {
                        localStorage.removeItem(key);
                    }
                });
            } catch (e) {
                console.error('Failed to clear localStorage:', e);
            }
        };

        window.addEventListener('forceNewChat', handleForceNewChat);

        // Listen for MCP Apps feature flag changes
        const handleFeatureToggleChange = (event) => {
            if (event.detail?.enableMcpApps !== undefined) {
                setEnableMcpApps(event.detail.enableMcpApps);
            }
        };

        window.addEventListener('featureToggleChanged', handleFeatureToggleChange);

        const fetchConversation = async () => {
            const result = await Conversations.fetchConversation(conversationIdFromParams);
            if (result.success) {
                setConvName(result.conversationName);

                // Load session tools from conversation
                if (result.selectedTools && Array.isArray(result.selectedTools)) {
                    setSessionTools(result.selectedTools);
                }

                // Convert ADK native event format → ChatMessage format expected by the renderer
                const mapAdkEventsToMessages = (events) => {
                    const mapped = [];
                    for (const event of events) {
                        const parts = (event.content?.parts) || [];
                        const isUser = event.author === 'user';

                        if (isUser) {
                            // Strip the canvas-context block injected by the app before sending
                            let text = parts.filter(p => p.text).map(p => p.text).join('\n');
                            if (text.includes('[USER REQUEST]')) {
                                const afterTag = text.split('[USER REQUEST]')[1] || '';
                                const beforeCanvas = afterTag.split('\n\n---\n\n[CANVAS MODE CONTEXT]')[0];
                                text = beforeCanvas.trim();
                            }
                            if (text) {
                                mapped.push({ id: event.id, text, type: 'sent' });
                            }
                        } else {
                            const textParts = parts.filter(p => p.text);
                            const funcCallParts = parts.filter(p => p.function_call);

                            if (textParts.length > 0) {
                                mapped.push({
                                    id: event.id,
                                    text: textParts.map(p => p.text).join('\n'),
                                    type: 'received'
                                });
                            }
                            for (const part of funcCallParts) {
                                const fc = part.function_call;
                                const functionId = fc.id || fc.name;
                                mapped.push({
                                    id: `function-${functionId}-${event.id}`,
                                    text: `🛠️ Using **${fc.name}**... ✅`,
                                    type: 'received',
                                    isFunctionCall: true,
                                    functionName: fc.name,
                                    functionId: functionId,
                                    functionArgs: fc.args
                                });
                            }
                        }
                    }
                    return mapped;
                };

                // Parse structured content from loaded messages
                const parsedMessages = mapAdkEventsToMessages(result.messages || []).map(msg => {
                    if (msg.type === 'received' && msg.text) {
                        const structuredData = Messages.parseStructuredContent(msg.text);
                        if (structuredData) {
                            return {
                                ...msg,
                                text: structuredData.cleanText,
                                contentType: structuredData.contentType,
                                structuredContent: structuredData.structuredContent,
                                rawStructuredText: structuredData.rawStructuredText
                            };
                        }
                    }
                    return msg;
                });

                setMessages(parsedMessages);
                // Restore the backend session so subsequent messages continue the existing session
                if (result.sessionId) {
                    setSessionId(result.sessionId);
                }
                // Restore playbook if this conversation was created with one
                const resolvedTenantId = result.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId;
                if (result.playbookItemId && resolvedTenantId && !customAIInstructionsPlaybookData) {
                    try {
                        const baseUrl = config.bffUrl || window.location.origin;
                        const pbResp = await authorizedFetch(
                            `${baseUrl}/${AI_API_PREFIX}/ai-playbooks/${resolvedTenantId}/${result.playbookItemId}/latest`,
                            { headers: getBffHeaders() }
                        );
                        if (pbResp.ok) {
                            const pbData = await pbResp.json();
                            const playbook = pbData.playbook || pbData;
                            if (playbook?.name) {
                                const agentNames = getPlaybookAgentRefs(playbook).map(a => a.name || a.itemId).filter(Boolean);
                                const promptNames = resolveRefNames(playbook.prompts);
                                const specNames = resolveRefNames(playbook.specs);
                                const skillNames = resolveRefNames(playbook.skills);
                                let instructions = `[PLAYBOOK: ${playbook.name}]\n`;
                                if (playbook.description) instructions += `${playbook.description}\n\n`;
                                if (agentNames.length) instructions += `Active agents: ${agentNames.join(', ')}\n`;
                                if (promptNames.length) instructions += `Loaded prompts: ${promptNames.join(', ')}\n`;
                                if (specNames.length) instructions += `Loaded specs: ${specNames.join(', ')}\n`;
                                if (skillNames.length) instructions += `Active skills: ${skillNames.join(', ')}\n`;
                                handleApplyAIInstructions(instructions, playbook.name, null, playbook);
                            }
                        }
                    } catch (e) {
                        console.error('Failed to restore playbook for conversation:', e);
                    }
                }
                // Mark these as restored so auto-save won't treat them as new
                restoredMessageCountRef.current = parsedMessages.length;
                setTimeout(() => {
                    if (chatMessagesRef.current) {
                        chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
                        lastScrollHeightRef.current = chatMessagesRef.current.scrollHeight;
                        shouldAutoScrollRef.current = true; // Enable auto-scroll for new messages
                    }
                }, 100);
            } else {
                showToast(result.message || 'Failed to load conversation', 'error');
            }
        }
        // Read conversationId from URL and hydrate state
        const searchParams = createSearchParams(location.search);
        const conversationIdFromParams = searchParams.get('conversationId')

        if (conversationIdFromParams) {
            // There's a conversationId in URL - load it only if it's different from current.
            // Also guard against the effect re-firing mid-navigation (e.g. sync-URL redirect)
            // by comparing against prevUrlConversationIdRef which is updated synchronously.
            if (conversationIdFromParams !== conversationId && conversationIdFromParams !== prevUrlConversationIdRef.current) {
                setConversationId(conversationIdFromParams);
                prevUrlConversationIdRef.current = conversationIdFromParams;
                fetchConversation();
            } else if (conversationIdFromParams !== prevUrlConversationIdRef.current) {
                // Update the ref to track this conversationId
                prevUrlConversationIdRef.current = conversationIdFromParams;
            }
        } else if (prevUrlConversationIdRef.current !== null && !isSavingRef.current && !isLoading) {
            // URL conversationId changed from something to null
            // Only clear if user explicitly navigated to "New Chat", NOT when navigating to tabs
            // Check if we're navigating to a project tab (service/dashboard/architecture/resources)
            const isNavigatingToTab = params.tab || params.microservice;

            if (!isNavigatingToTab) {
                // User clicked "New Chat" or navigated away from project - clear conversation
                // Only clear if:
                // 1. We had a conversationId in URL before (prevUrlConversationIdRef.current !== null)
                // 2. We're not in the middle of saving (to avoid race condition)
                // 3. We're not loading a response (to avoid clearing during SSE)
                // 4. We're not navigating to a project tab (new check)
                // This ensures conversation history persists when navigating within the project
                // eslint-disable-next-line no-console
                console.log('🧹 Clearing conversation - User navigated away', {
                    prevUrlConversationId: prevUrlConversationIdRef.current,
                    currentUrlConversationId: conversationIdFromParams,
                    messagesCount: messages.length
                });
                setMessages([]);
                setConvName('');
                setConversationId(null);
                setSessionTools(null); // Reset session tools
                setShowToolChangeNotice(false); // Clear tool change notice
                setTotalTokens(0); // Reset token count
                setInputTokens(0);
                setOutputTokens(0);
                sessionStorage.removeItem('tokenWarningShown'); // Reset warning flag
                // DON'T reset sessionId - keep the same session across conversations
                setInputValue('');
                setIsLoading(false);
                setConversationStatus('');
                if (abortController) {
                    abortController.abort();
                    setAbortController(null);
                }
                // Update ref to reflect no conversationId in URL
                prevUrlConversationIdRef.current = null;
            } else {
                // Navigating to a tab - keep conversation but don't update the ref
                // This allows the conversation to persist while viewing different tabs
                // eslint-disable-next-line no-console
                console.log('📌 Preserving conversation - navigating to tab', {
                    tab: params.tab,
                    microservice: params.microservice,
                    conversationId: prevUrlConversationIdRef.current,
                    messagesCount: messages.length
                });
            }
        }

        return () => {
            window.removeEventListener('forceNewChat', handleForceNewChat);
            window.removeEventListener('featureToggleChanged', handleFeatureToggleChange);
            unsubscribeContext();

            // Cleanup browser sessions on unmount
            cleanupBrowserSessions();
        };

    }, [location, showToast, abortController, cleanupBrowserSessions, isLoading]);

    // Effect to sync URL with stored tenant and project on initial load
    useEffect(() => {
        if (location.pathname === '/chat' || location.pathname === '/chat/') {
            const storedTenant = miaFlowAuth.getSelectedTenant();
            const storedProject = miaFlowAuth.getSelectedProject();

            if (storedTenant && storedProject) {
                // Redirect to URL with tenant and project
                navigate(`/chat/${storedTenant.tenantId}/${storedProject.projectId}`, { replace: true });
            } else if (storedTenant) {
                // Redirect to URL with just tenant
                navigate(`/chat/${storedTenant.tenantId}`, { replace: true });
            } else {
                // No tenant or project selected - redirect to projects page
                navigate('/projects', { replace: true });
            }
        }
    }, [location.pathname, navigate]);

    // Effect to validate URL parameters (tenantId and projectId)
    useEffect(() => {
        // Only validate on /chat route
        if (!location.pathname.startsWith('/chat')) {
            return;
        }

        // Get tenant and project from path params or stored state
        const urlTenantId = params.tenantId;
        const projectId = params.projectId;
        const storedTenant = miaFlowAuth.getSelectedTenant();
        const storedProject = miaFlowAuth.getSelectedProject();

        // Check both URL parameter and app state for tenant
        const tenantId = urlTenantId || storedTenant?.tenantId;

        // Skip if we already loaded this exact project (prevent re-fetching)
        const projectKey = `${tenantId}-${projectId}`;
        if (lastLoadedProjectRef.current === projectKey) {
            console.log('⏭️ Skipping URL validation - project already loaded:', projectKey);
            return;
        }

        // Check if tenantId is missing from both URL and state
        if (!tenantId) {
            setUrlValidationError({
                type: 'missing_tenant',
                message: 'You must be added to a tenant to access this application.',
                action: 'Go to Home'
            });
            return;
        }

        // Clear any previous error if tenantId is present
        if (urlValidationError?.type === 'missing_tenant') {
            setUrlValidationError(null);
        }

        // Check if projectId is missing
        if (!projectId) {
            // Check if there's a previously selected project
            if (storedProject && storedProject.projectId) {
                // Auto-navigate to the stored project instead of showing selector
                console.log('📂 Auto-loading stored project:', storedProject.name || storedProject.projectId);
                navigate(`/chat/${tenantId}/${storedProject.projectId}`, { replace: true });
                return;
            }

            setShowProjectSelector(true);
            // Load projects for this tenant
            const loadProjects = async () => {
                try {
                    setIsLoadingProjects(true);
                    const projectsData = await miaPlatformDataService.getProjects(tenantId);
                    setTenantProjects(projectsData.data || []);
                } catch (error) {
                    console.error('Failed to load projects:', error);
                    showToast('Failed to load projects for tenant', 'error');
                } finally {
                    setIsLoadingProjects(false);
                }
            };
            loadProjects();
        } else {
            setShowProjectSelector(false);
            setUrlValidationError(null); // Clear any stale error from previous navigation
            // Both tenantId and projectId are present, validate and load project
            const validateAndLoadProject = async () => {
                try {
                    const projectConfig = await miaPlatformDataService.getFullProjectConfiguration(projectId);

                    // Verify project exists (if config has tenant info, check it)
                    if (projectConfig.info?.tenantId && projectConfig.info.tenantId !== tenantId) {
                        setUrlValidationError({
                            type: 'tenant_mismatch',
                            message: `Project does not belong to the specified tenant.`,
                            action: 'Go to Home'
                        });
                        return;
                    }

                    // Fetch revisions for the project
                    const revisions = await miaPlatformDataService.getRevisions(projectId);

                    // Use the first revision in the list
                    if (!revisions || revisions.length === 0) {
                        setUrlValidationError({
                            type: 'project_not_found',
                            message: `No revisions found for this project.`,
                            action: 'Choose Another Project'
                        });
                        return;
                    }

                    const selectedRevision = revisions[0];
                    console.log('📝 Using first revision:', selectedRevision);

                    // Extract revision identifier (revision objects have 'name' property, e.g., 'main', 'master')
                    const revisionId = selectedRevision.name || selectedRevision.revision || selectedRevision._id;
                    console.log('📝 Extracted revision ID:', revisionId);

                    // Fetch microservices/configuration for the selected revision
                    const configuration = await miaPlatformDataService.getMicroservices(
                        projectId,
                        revisionId
                    );

                    // Set up canvas project context with full data
                    // Clear selected resource from previous project
                    localStorage.removeItem('mia_selectedResource');

                    const newContext = {
                        projectId: projectId,
                        project: {
                            _id: projectId,
                            id: projectId,  // Add explicit id field for Canvas
                            projectId: projectId,
                            name: projectConfig.info?.name || projectConfig.name || 'Project',
                            description: projectConfig.info?.description || projectConfig.description || '',
                            tenantId: tenantId,
                            environments: projectConfig.environments || [],
                            repositoryUrl: projectConfig.configurationGitPath || projectConfig.repositoryUrl || null,
                            configurationGitPath: projectConfig.configurationGitPath || null
                        },
                        tenant: {
                            id: tenantId,
                            name: projectConfig.info?.tenantName || projectConfig.tenantName || tenantId,
                            tenantId: tenantId
                        },
                        revision: {
                            revision: revisionId,
                            revisionId: selectedRevision._id || revisionId,
                            name: selectedRevision.name || revisionId,
                            ...selectedRevision
                        },
                        selectedResource: null,
                        selectedMicroservice: null,
                        configuration: configuration
                    };

                    console.log('📦 Created canvas context:', {
                        projectId: newContext.projectId,
                        projectName: newContext.project.name,
                        revisionId: newContext.revision.revisionId
                    });

                    setCanvasProjectContext(newContext);
                    await indexedDbStorage.setItem('canvasProjectContext', newContext);
                    setUrlValidationError(null); // Ensure no stale error remains after successful load

                    // Mark this project as loaded to prevent re-fetching
                    lastLoadedProjectRef.current = `${tenantId}-${projectId}`;

                    // DON'T clear the flag here - Canvas needs to finish initializing
                    // The flag will be cleared by Canvas after it's done with initial setup

                    console.log('✅ Loaded project from URL parameters:', newContext.project);

                    // Check for pending brainstorming prompt - auto-trigger after config is pulled
                    const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
                    const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');

                    if (pendingPrompt && pendingMode === 'brainstorming') {
                        const configRepoUrl = projectConfig.configurationGitPath || projectConfig.repositoryUrl;

                        if (!configRepoUrl) {
                            // No config repo — fire immediately (gitPullCompleted won't come)
                            sessionStorage.removeItem('mia_pending_initial_prompt');
                            sessionStorage.removeItem('mia_pending_initial_mode');

                            const brainstormingPrompt = `Create a file named brainstorming.md that answers the following user request: "${pendingPrompt}"\n\nIn brainstorming.md, explore what is available in the Mia Platform catalog/marketplace to satisfy the request. Structure the document with:\n1. **Request Summary**: What the user wants to achieve\n2. **Available Mia Platform Components**: Relevant services, plugins, templates from the Mia catalog that can be used\n3. **Suggested Architecture**: How to combine these components to fulfill the request\n4. **Next Steps**: Concrete actions to implement the solution using Mia Platform`;

                            console.log('🧠 [Brainstorming] No config repo – firing prompt directly');

                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('populate-chat-input', {
                                    detail: { message: brainstormingPrompt, autoSend: true }
                                }));
                            }, 1500);
                        }
                        // else: gitPullCompleted listener in MainChat will fire the prompt after config is pulled
                    }

                } catch (error) {
                    console.error('Failed to load project from URL:', error);
                    setUrlValidationError({
                        type: 'project_not_found',
                        message: `Project not found or you don't have access.`,
                        action: 'Choose Another Project'
                    });
                }
            };

            // Always validate and load project from URL - URL is source of truth
            validateAndLoadProject();
        }
    }, [location.pathname, params.tenantId, params.projectId, showToast]);

    // Effect to handle project context from localStorage and enable canvas when opening a project
    useEffect(() => {
        const loadProjectFromLocalStorage = async () => {
            // Only load from localStorage if we're on /chat WITHOUT URL parameters
            // If there are URL params (tenantId/projectId), let the URL validation effect handle it
            if (location.pathname === '/chat' && !params.tenantId && !params.projectId) {
                // Check if there's a project context in localStorage (from project selection)
                const projectContextStr = localStorage.getItem('canvasProjectContext');
                const canvasModeStr = localStorage.getItem('canvasMode');

                if (projectContextStr && canvasModeStr === 'true') {
                    try {
                        const projectContext = JSON.parse(projectContextStr);

                        // Set the project context
                        setCanvasProjectContext(projectContext);

                        // Canvas is always enabled, no need to set it

                        // Save to IndexedDB for persistence
                        await indexedDbStorage.setItem('canvasProjectContext', projectContext);

                        console.log('📋 Loaded project from localStorage:', projectContext);

                        // Clean up localStorage flag immediately to prevent re-processing
                        localStorage.removeItem('canvasProjectContext');
                    } catch (error) {
                        console.error('Failed to load project context:', error);
                        localStorage.removeItem('canvasProjectContext');
                    }
                }
            }

            // Always in agent mode with canvas for code editing (for all /chat routes)
            if (location.pathname.startsWith('/chat') && !location.search.includes('conversationId')) {
                setAiMode('agent');
            }
        };

        loadProjectFromLocalStorage();
    }, [location.pathname, location.search, params.tenantId, params.projectId]); // Added params to dependencies

    // Fetch available spec commands from the catalog whenever the tenant changes
    const effectiveTenantIdForSpecs = params.tenantId
        || canvasProjectContext?.tenant?.tenantId
        || miaFlowAuth.getSelectedTenant()?.tenantId
        || '';
    useEffect(() => {
        if (!effectiveTenantIdForSpecs) return;
        let active = true;

        const loadCatalogSpecs = async () => {
            try {
                const baseUrl = config.bffUrl || window.location.origin;
                const qs = new URLSearchParams({ page: '1', perPage: '100' });
                const res = await authorizedFetch(`${baseUrl}/${AI_API_PREFIX}/ai-specs?${qs}`, { headers: getBffHeaders() });
                if (!active || !res.ok) return;
                const result = await res.json();
                if (active && result.success && result.data?.items) {
                    setCatalogSpecCommands(buildCommandsFromSpecs(result.data.items));
                }
            } catch { /* silently ignore */ }
        };

        loadCatalogSpecs();

        const handleSpecsChanged = () => loadCatalogSpecs();
        window.addEventListener('mia-flow-spec-commands-changed', handleSpecsChanged);
        return () => {
            active = false;
            window.removeEventListener('mia-flow-spec-commands-changed', handleSpecsChanged);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveTenantIdForSpecs]);

    // Effect to track recent project visits
    // This runs when a project is loaded into canvas context AND we have user details
    useEffect(() => {
        // Only track if we have a project loaded and a user (or at least user object defined)
        if (canvasProjectContext?.project?._id && user) {
            const projectId = canvasProjectContext.project._id;
            const userEmail = user.email || user.sub || 'demo-user';

            // Create a unique key for this session/project combination to prevent duplicate tracking
            const projectKey = `${projectId}-${userEmail}`;

            // Skip if already tracked for this session (unless user changed?)
            if (lastTrackedProjectRef.current === projectKey) {
                return;
            }

            const trackVisit = async () => {
                try {
                    const baseUrl = config.bffUrl || window.location.origin;

                    console.log('🔵 [POST recent-projects] Project Active - tracking visit:', {
                        url: `${baseUrl}/api/recent-projects`,
                        userEmail,
                        projectId
                    });

                    await authorizedFetch(`${baseUrl}/api/recent-projects`, {
                        method: 'POST',
                        credentials: 'include',
                        keepalive: true,
                        headers: getBffHeaders({
                            'Content-Type': 'application/json',
                            'x-user-id': userEmail,
                        }),
                        body: JSON.stringify(canvasProjectContext.project),
                    });

                    console.log('✅ [POST recent-projects] Project Active - visit tracked successfully');

                    // Mark as tracked
                    lastTrackedProjectRef.current = projectKey;
                } catch (error) {
                    console.warn('❌ [POST recent-projects] Project Active - Failed to track:', error);
                    // We don't mark as tracked so it might retry if dependencies change
                }
            };

            trackVisit();
        }
    }, [canvasProjectContext?.project?._id, user]);

    // Effect to persist Canvas mode state to localStorage
    useEffect(() => {
        localStorage.setItem('canvasMode', isCanvasMode.toString());
    }, [isCanvasMode]);

    // Effect to persist chat sidebar width to localStorage
    useEffect(() => {
        localStorage.setItem('chatSidebarWidth', chatSidebarWidth.toString());
    }, [chatSidebarWidth]);

    // Effect to handle resize drag
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (resizeHandleRef.current && resizeHandleRef.current.dataset.dragging === 'true') {
                const newWidth = e.clientX;
                if (newWidth >= 300 && newWidth <= window.innerWidth - 400) {
                    setChatSidebarWidth(newWidth);
                }
            }
        };

        const handleMouseUp = () => {
            if (resizeHandleRef.current) {
                resizeHandleRef.current.dataset.dragging = 'false';
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    // Listen for project selection from Home page
    useEffect(() => {
        const handleSelectProject = async (event) => {
            const { project, projectId } = event.detail || {};

            // Get project ID from either project object or direct projectId parameter
            const selectedProjectId = projectId || project?._id || project?.projectId;

            if (!selectedProjectId) {
                console.error('No project ID provided in select-project event');
                return;
            }

            try {
                console.log('🎯 Selecting project:', selectedProjectId);

                // Show loading toast
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { message: '🔄 Loading project...', type: 'info' }
                }));

                // Fetch full project info from API
                const projectInfo = await miaPlatformDataService.getProjectInfo(selectedProjectId);

                if (!projectInfo) {
                    throw new Error('Failed to fetch project information');
                }

                // Track project visit in recent projects
                try {
                    const baseUrl = config.bffUrl || window.location.origin;
                    const userEmail = user?.email || user?.sub || 'demo-user';
                    console.log('🔵 [POST recent-projects] Project selector - tracking visit:', {
                        url: `${baseUrl}/api/recent-projects`,
                        userEmail,
                        projectId: projectInfo._id
                    });
                    await authorizedFetch(`${baseUrl}/api/recent-projects`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: getBffHeaders({
                            'Content-Type': 'application/json',
                            'x-user-id': userEmail,
                        }),
                        body: JSON.stringify(projectInfo),
                    });
                    console.log('✅ [POST recent-projects] Project selector - visit tracked successfully');
                } catch (trackError) {
                    console.warn('❌ [POST recent-projects] Project selector - Failed to track project visit:', trackError);
                    // Don't block project opening if tracking fails
                }

                // Set up canvas project context
                const newContext = {
                    projectId: selectedProjectId,
                    project: projectInfo,
                    tenant: {
                        id: projectInfo.tenantId,
                        name: projectInfo.tenantName || projectInfo.tenantId
                    }
                };

                // Update canvas project context
                setCanvasProjectContext(newContext);

                // Canvas mode is always enabled, just ensure localStorage is set
                localStorage.setItem('canvasMode', 'true');

                // Store in localStorage for persistence
                localStorage.setItem('canvasProjectContext', JSON.stringify(newContext));
                localStorage.setItem('currentProjectId', selectedProjectId);

                // Success notification
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                        message: `✅ Loaded project: ${projectInfo.name || selectedProjectId}`,
                        type: 'success'
                    }
                }));

                console.log('✅ Project loaded successfully:', projectInfo);

            } catch (error) {
                console.error('Failed to load project:', error);
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                        message: `❌ Failed to load project: ${error.message}`,
                        type: 'error'
                    }
                }));
            }
        };

        window.addEventListener('select-project', handleSelectProject);

        return () => {
            window.removeEventListener('select-project', handleSelectProject);
        };
    }, [setCanvasProjectContext]);

    // Listen for file changes in Canvas mode
    useEffect(() => {
        const handleCanvasFileChange = (event) => {
            const {projectId, serviceName, state, deleted} = event.detail || {};

            // Only track changes if we're in Canvas mode and have a project context
            if (isCanvasMode && canvasProjectContext &&
                projectId === canvasProjectContext.projectId &&
                serviceName === canvasProjectContext.selectedMicroservice?.name) {

                // Check if this is a file addition or deletion
                const isNewFile = state === GIT_STATE.NEW;
                const isDeleted = deleted === true;

                if (isNewFile || isDeleted) {
                    // File was added or removed - clear session tracking to resend all files
                    if (sessionId) {
                        AdkClient.clearSessionFiles(sessionId);
                        // eslint-disable-next-line no-console
                        console.log(`📂 File ${isNewFile ? 'added' : 'deleted'} - cleared session tracking. All files will be resent on next message.`);
                    }
                    // Also clear chat history on next message
                    setFilesModifiedInCanvas(true);
                } else {
                    // Regular file modification
                    // eslint-disable-next-line no-console
                    console.log('📝 File modified in Canvas mode - will clear chat history on next message');
                    setFilesModifiedInCanvas(true);
                }
            }
        };

        window.addEventListener('monacoFileChanged', handleCanvasFileChange);

        return () => {
            window.removeEventListener('monacoFileChanged', handleCanvasFileChange);
        };
    }, [isCanvasMode, canvasProjectContext]);

    // Handle ESC key to close fullscreen mode
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    // Effect to persist Canvas project context to IndexedDB
    useEffect(() => {
        const saveCanvasContext = async () => {
            try {
                if (canvasProjectContext && canvasProjectContext.projectId) {
                    await indexedDbStorage.setItem('canvasProjectContext', canvasProjectContext);
                    // eslint-disable-next-line no-console
                    console.log('💾 Saved Canvas project context to IndexedDB');
                }
                // Don't remove from IndexedDB when context is null - keep the saved context
                // This allows restoration when navigating back from home
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to save Canvas project context:', error);
                // Show user-friendly error message
                // eslint-disable-next-line no-console
                console.warn('Canvas project context could not be saved. Your selections may not persist.');
            }
        };
        saveCanvasContext();
    }, [canvasProjectContext]);

    // Ref to track last processed message ID for context updates
    const lastContextMessageIdRef = useRef(null);

    // Effect to update AI context when messages change
    useEffect(() => {
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            // Update context from assistant messages (only final messages, not intermediate)
            if (lastMessage.type === 'received' && !lastMessage.isIntermediate) {
                // Only update if this is a new message (not already processed)
                if (lastMessage.id !== lastContextMessageIdRef.current) {
                    lastContextMessageIdRef.current = lastMessage.id;
                    contextManager.updateContextFromMessage(lastMessage);
                }
            }
        }
    }, [messages]);



    // Smart auto-scroll: scroll automatically unless user has manually scrolled up
    useEffect(() => {
        if (!chatMessagesRef.current) return;

        const container = chatMessagesRef.current;
        const hasNewContent = container.scrollHeight !== lastScrollHeightRef.current;

        // Auto-scroll if enabled and there's new content
        if (shouldAutoScrollRef.current && hasNewContent) {
            // Use requestAnimationFrame to batch scroll updates and reduce flicker
            requestAnimationFrame(() => {
                if (container) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'auto' // Use 'auto' for instant scroll during streaming to avoid lag
                    });
                    lastScrollHeightRef.current = container.scrollHeight;
                }
            });
        }
    }, [messages, conversationStatus]);

    // Track user scroll to detect manual scrolling away from bottom
    useEffect(() => {
        const container = chatMessagesRef.current;
        if (!container) return;

        const handleScroll = () => {
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
            // Only disable auto-scroll if user has scrolled up (not near bottom)
            // If they scroll back down near bottom, re-enable auto-scroll
            shouldAutoScrollRef.current = isNearBottom;
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto-detect Canvas-worthy content in new messages
    // Memoize the last message to prevent unnecessary effect triggers
    const lastMessage = useMemo(() => {
        return messages.length > 0 ? messages[messages.length - 1] : null;
    }, [messages]);

    // Use a ref to track the last processed message ID to avoid redundant updates
    const lastProcessedMessageIdRef = useRef(null);

    useEffect(() => {
        if (!lastMessage) return;

        // Skip if we already processed this message
        if (lastProcessedMessageIdRef.current === lastMessage.id) {
            return;
        }

        // Skip auto-detection if canvas was manually set (e.g., for direct image generation)
        if (manualCanvasSetRef.current) {
            return;
        }

        // Mark this message as processed
        lastProcessedMessageIdRef.current = lastMessage.id;

        // Update Canvas content in real-time, even for intermediate messages
        // Canvas is always active, so update it for every received message
        if (lastMessage?.type === 'received') {
            // Check for special [IMAGE_DATA] markers first
            const imageDataMatch = lastMessage.text.match(/\[IMAGE_DATA\](data:image\/[^[]+)\[\/IMAGE_DATA\]/);
            if (imageDataMatch) {
                const imageUrl = imageDataMatch[1];
                // Extract prompt and aspect ratio from the message
                const promptMatch = lastMessage.text.match(/\*\*Prompt:\*\*\s*(.+)/);
                const aspectRatioMatch = lastMessage.text.match(/\*\*Aspect Ratio:\*\*\s*(\S+)/);

                setCanvasContent({
                    type: 'image',
                    content: imageUrl,
                    title: 'Generated Image',
                    prompt: promptMatch ? promptMatch[1].trim() : null,
                    aspect_ratio: aspectRatioMatch ? aspectRatioMatch[1].trim() : '1:1'
                });
            } else {
                // Fallback to old detection method
                const imageUrlMatch = lastMessage.text.match(/data:image\/[^;]+;base64,[^\s)]+/);
                if (imageUrlMatch) {
                    const imageUrl = imageUrlMatch[0];
                    const promptMatch = lastMessage.text.match(/(?:prompt|description):\s*["']?([^"'\n]+)["']?/i);
                    setCanvasContent({
                        type: 'image',
                        content: imageUrl,
                        title: 'Generated Image',
                        prompt: promptMatch ? promptMatch[1] : null,
                        aspect_ratio: '1:1'
                    });
                } else {
                    setCanvasContent({
                        type: 'document',
                        content: lastMessage.text,
                        title: lastMessage.isIntermediate ? 'Generating...' : 'Generated Content'
                    });
                }
            }
            // Don't continue to the auto-trigger logic if canvas is already active
            return;
        }

        // Auto-trigger Canvas content for finalized messages
        if (lastMessage?.type === 'received' && !lastMessage.isIntermediate) {
            const content = lastMessage.text;

            // Check for special [IMAGE_DATA] markers first (highest priority)
            const imageDataMatch = content.match(/\[IMAGE_DATA\](data:image\/[^[]+)\[\/IMAGE_DATA\]/);
            if (imageDataMatch) {
                const imageUrl = imageDataMatch[1];
                // Extract prompt and aspect ratio from the message
                const promptMatch = content.match(/\*\*Prompt:\*\*\s*(.+)/);
                const aspectRatioMatch = content.match(/\*\*Aspect Ratio:\*\*\s*(\S+)/);

                // Canvas is always enabled, just set content
                setCanvasContent({
                    type: 'image',
                    content: imageUrl,
                    title: 'Generated Image',
                    prompt: promptMatch ? promptMatch[1].trim() : null,
                    aspect_ratio: aspectRatioMatch ? aspectRatioMatch[1].trim() : '1:1'
                });

                // Dispatch event to hide sidebar
                window.dispatchEvent(new CustomEvent('canvasToggled', {
                    detail: {isActive: true}
                }));

                showToast('Canvas mode activated for image viewing', 'info');
                return;  // Skip document/code detection for image messages
            }

            // Fallback: Check for image data URLs (old format)
            const imageUrlMatch = content.match(/data:image\/[^;]+;base64,[^\s)]+/);
            if (imageUrlMatch) {
                const imageUrl = imageUrlMatch[0];
                // Extract prompt if available
                const promptMatch = content.match(/(?:prompt|description):\s*["']?([^"'\n]+)["']?/i);

                // Canvas is always enabled, just set content
                setCanvasContent({
                    type: 'image',
                    content: imageUrl,
                    title: 'Generated Image',
                    prompt: promptMatch ? promptMatch[1] : null,
                    aspect_ratio: '1:1'  // Default
                });

                // Dispatch event to hide sidebar
                window.dispatchEvent(new CustomEvent('canvasToggled', {
                    detail: {isActive: true}
                }));

                showToast('Canvas mode activated for image viewing', 'info');
                return;  // Skip document/code detection for image messages
            }

            // Define patterns that should trigger Canvas mode
            const canvasPatterns = [
                // Code patterns
                /```[\s\S]+```/g, // Code blocks
                /function\s+\w+\s*\(/g, // Function declarations
                /class\s+\w+/g, // Class declarations
                /import\s+.*from/g, // ES6 imports
                /export\s+(default\s+)?/g, // ES6 exports
                /<\/?\w+[^>]*>/g, // HTML tags (multiple)
                /\{\s*"[\w\s:,]+"\s*:/g, // JSON objects

                // Document patterns
                /^#{1,6}\s+.+$/m, // Multiple markdown headers
                /^\*\s+.+(\n\*\s+.+){2,}/m, // Multiple bullet points
                /^\d+\.\s+.+(\n\d+\.\s+.+){2,}/m, // Multiple numbered lists
                /\n\n.+(\n.+){5,}/g, // Long structured content
            ];

            // Check if content matches Canvas patterns
            const shouldAutoTriggerCanvas = canvasPatterns.some(pattern => {
                const matches = content.match(pattern);
                return matches && matches.length > 0;
            });

            // Auto-trigger Canvas for substantial content (>500 chars with structure)
            const isSubstantialContent = content.length > 500 && (
                content.includes('\n\n') || // Has paragraphs
                content.includes('```') || // Has code blocks
                content.match(/^#+\s/m) // Has headers
            );

            if (shouldAutoTriggerCanvas || isSubstantialContent) {
                // Canvas is always enabled, just set content
                setCanvasContent({
                    type: 'document',
                    content: content,
                    title: 'Generated Content'
                });

                // Dispatch event to hide sidebar
                window.dispatchEvent(new CustomEvent('canvasToggled', {
                    detail: {isActive: true}
                }));

                // Show a subtle notification
                showToast('Canvas mode activated for better viewing', 'info');
            }
        }
    }, [lastMessage, showToast]);

    /**
     * Extract code update from AI response for Monaco editor
     * Uses CodeExtractor utility for unified diff detection
     */
    const extractCodeUpdate = useCallback((responseText, currentFilePath) => {
        // First priority: Check for unified diff format (handles file creation, deletion, modification)
        if (CodeExtractor.containsUnifiedDiff(responseText)) {
            const diffContent = CodeExtractor.extractUnifiedDiff(responseText);
            if (diffContent) {
                // eslint-disable-next-line no-console
                console.log('📊 Detected unified diff format in AI response');
                return {
                    filePath: currentFilePath || 'unknown.txt',
                    updateType: 'diff',
                    diffContent: diffContent,
                    isDiff: true,
                    isPartialUpdate: true
                };
            }
        }

        // Second priority: Check for replace operations
        const replaceOp = CodeExtractor.extractReplaceOperation(responseText);
        if (replaceOp) {
            return {
                filePath: currentFilePath || 'unknown.txt',
                updateType: 'replace',
                oldString: replaceOp.oldString,
                newString: replaceOp.newString,
                isPartialUpdate: true
            };
        }

        // Third priority: Try to extract file path from common patterns
        const filePathPatterns = [
            /(?:content for|create|update|modify)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?\s+(?:is|file)?:?/i,
            /(?:here'?s?\s+(?:the\s+)?(?:updated\s+)?content\s+for)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?:?/i,
            /^([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+):?\s*$/m,
            /```\w*\n\/\/\s*([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)/
        ];

        // Extract all code blocks with their context
        const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
        const matches = [...responseText.matchAll(codeBlockRegex)];

        if (matches.length === 0) return null;

        const results = [];

        // Try to match each code block with a file path
        for (const match of matches) {
            const code = match[1].trim();
            if (code.length <= 50) continue; // Skip trivial blocks

            // Get the text before this code block (up to 200 chars)
            const beforeText = responseText.substring(Math.max(0, match.index - 200), match.index);

            let detectedPath = null;

            // Try to find file path in the text before the code block
            for (const pattern of filePathPatterns) {
                const pathMatch = beforeText.match(pattern);
                if (pathMatch && pathMatch[1]) {
                    detectedPath = pathMatch[1];
                    break;
                }
            }

            // Also check inside the code block for file path comments
            const firstLine = code.split('\n')[0];
            if (!detectedPath && (firstLine.includes('//') || firstLine.includes('#') || firstLine.includes('/*'))) {
                const inlinePathMatch = firstLine.match(/[/\\]?([a-zA-Z0-9_-]+\/[a-zA-Z0-9_./-]+)/);
                if (inlinePathMatch) {
                    detectedPath = inlinePathMatch[1];
                }
            }

            results.push({
                filePath: detectedPath || currentFilePath || 'unknown.txt',
                content: code,
                updateType: 'full',
                isNewFile: detectedPath && detectedPath !== currentFilePath
            });
        }

        // Return the first result (or null if no valid blocks found)
        return results.length > 0 ? results[0] : null;
    }, []);

    /**
     * Extract ALL code updates from an AI response (handles multiple diffs/files)
     * This is used when AI returns multiple file modifications in a single response
     */
    const extractAllCodeUpdates = useCallback((responseText, currentFilePath) => {
        const updates = [];

        // Helper function to check if response is actually requesting code changes
        // vs. just showing code as an example or explanation
        const isActualModificationRequest = (text) => {
            // PRIORITY 1: If there are actual unified diff blocks present, always process them
            // This handles cases where AI provides code AND asks a follow-up question
            const hasUnifiedDiff = /---\s+(?:a\/)?[^\n]+\n\+\+\+\s+(?:b\/)?[^\n]+\n@@/m.test(text);
            if (hasUnifiedDiff) {
                return true; // Always process if valid diffs are present
            }

            // PRIORITY 2: Check for explicit modification indicators
            const modificationIndicators = [
                /(?:here(?:'s| is)|i(?:'ve| have)) (?:updated|modified|changed|fixed|added|refactored)/i,
                /(?:let me|i'll|i will) (?:update|modify|change|fix|add|refactor)/i,
                /replace.*with/i,
                /(?:updated|modified|changed|fixed) (?:code|file|implementation)/i,
                /apply (?:this|these) change/i,
                /make (?:this|these) change/i
            ];

            const hasModificationIndicators = modificationIndicators.some(pattern => pattern.test(text));
            if (hasModificationIndicators) {
                return true;
            }

            // PRIORITY 3: Only check for questions if no diffs/modifications detected
            const questionIndicators = [
                /^(?:what|how|why|when|where|can you explain)/i,
                /\bexplain\b/i,
                /\bshow me\b/i,
                /\btell me\b/i,
                /\bwhat (?:does|is|are)\b/i,
                /\bhow (?:does|is|can)\b/i,
                /\?\s*$/m  // Ends with a question mark
            ];

            const hasQuestionIndicators = questionIndicators.some(pattern => pattern.test(text));
            if (hasQuestionIndicators) {
                return false; // Skip only if it's purely a question
            }

            // Default: process if uncertain
            return true;
        };

        // Check if this response is actually a modification request
        if (!isActualModificationRequest(responseText)) {
            // eslint-disable-next-line no-console
            console.log('📖 Response appears to be a question/explanation, not a modification request. Skipping code updates.');
            return [];
        }

        // First priority: Extract ALL unified diffs
        // CRITICAL: Capture all content until next file header or end
        const unifiedDiffRegex = /---\s+(?:a\/)?([^\n]+)\n\+\+\+\s+(?:b\/)?([^\n]+)\n([\s\S]*?)(?=\n---\s+(?:a\/)?[^\n]+\n\+\+\+|$)/g;
        let diffMatch;

        while ((diffMatch = unifiedDiffRegex.exec(responseText)) !== null) {
            const oldFilePath = diffMatch[1].trim();
            const newFilePath = diffMatch[2].trim();
            let diffBody = diffMatch[3];

            // Clean up the diff body - remove any trailing commentary or explanations
            // Keep all hunks (@@) but stop at non-diff content
            const lines = diffBody.split('\n');
            const cleanedLines = [];
            let inHunk = false;
            let consecutiveNonDiffLines = 0;

            for (const line of lines) {
                // Start of a hunk - reset counter
                if (line.startsWith('@@')) {
                    inHunk = true;
                    cleanedLines.push(line);
                    consecutiveNonDiffLines = 0;
                    continue;
                }

                // Valid diff line (starts with space, +, -, or \ for "no newline" marker)
                if (inHunk && (line.startsWith(' ') || line.startsWith('+') || line.startsWith('-') || line.startsWith('\\'))) {
                    cleanedLines.push(line);
                    consecutiveNonDiffLines = 0;
                    continue;
                }

                // Empty line could be between hunks or part of context - keep it
                if (line.trim() === '') {
                    cleanedLines.push(line);
                    consecutiveNonDiffLines++;
                    // Stop if we have 2+ consecutive empty lines (likely end of diff)
                    if (consecutiveNonDiffLines >= 2) {
                        break;
                    }
                    continue;
                }

                // Non-diff content (commentary, explanations) - stop here
                if (inHunk) {
                    // Check if it looks like commentary (bullets, checkmarks, plain text)
                    if (line.match(/^[\s-]*[✓✅❌📝🔧]/)) {
                        break;
                    }
                    // Stop at lines that don't follow diff format
                    break;
                }
            }

            diffBody = cleanedLines.join('\n').trim();

            // Skip if we didn't get any valid diff content
            if (!diffBody || !diffBody.includes('@@')) {
                continue;
            }

            // Reconstruct the full diff for this file WITH proper a/ and b/ prefixes
            // The diffParser expects standard unified diff format
            // Handle /dev/null specially to avoid double slashes
            const oldPath = oldFilePath === '/dev/null' ? '/dev/null' : `a/${oldFilePath}`;
            const newPath = newFilePath === '/dev/null' ? '/dev/null' : `b/${newFilePath}`;
            const fullDiff = `--- ${oldPath}\n+++ ${newPath}\n${diffBody}`;

            // Determine the target file path
            let targetPath = newFilePath;
            if (newFilePath === '/dev/null') {
                targetPath = oldFilePath; // File deletion
            } else if (oldFilePath === '/dev/null') {
                targetPath = newFilePath; // File creation
            }

            // Remove path prefixes (a/ or b/) from target path only
            targetPath = targetPath.replace(/^[ab]\//, '');

            updates.push({
                filePath: targetPath,
                updateType: 'diff',
                diffContent: fullDiff,
                isDiff: true,
                isPartialUpdate: true
            });

            // eslint-disable-next-line no-console
            console.log(`📊 Extracted unified diff for: ${targetPath}`);
            // eslint-disable-next-line no-console
            console.log(`📄 Diff preview (first 300 chars):`, fullDiff.substring(0, 300));
        }

        // If we found unified diffs, return them
        if (updates.length > 0) {
            return updates;
        }

        // Second priority: Check for replace operations
        const replaceOp = CodeExtractor.extractReplaceOperation(responseText);
        if (replaceOp) {
            return [{
                filePath: currentFilePath || 'unknown.txt',
                updateType: 'replace',
                oldString: replaceOp.oldString,
                newString: replaceOp.newString,
                isPartialUpdate: true
            }];
        }

        // Third priority: Extract all code blocks
        // Patterns to detect file paths in AI responses
        const filePathPatterns = [
            /(?:content for|create|update|modify)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?\s+(?:is|file)?:?/i,
            /(?:here'?s?\s+(?:the\s+)?(?:updated\s+)?content\s+for)\s+[`']?([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)[`']?:?/i,
            /^([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+):?\s*$/m,
            /```\w*\n\/\/\s*([a-zA-Z0-9_\-./]+\.[a-zA-Z0-9]+)/
        ];

        const codeBlockRegex = /```(?:\w+)?\n?([\s\S]*?)```/g;
        const matches = [...responseText.matchAll(codeBlockRegex)];

        for (const match of matches) {
            const code = match[1].trim();
            if (code.length <= 50) continue;

            const beforeText = responseText.substring(Math.max(0, match.index - 200), match.index);
            let detectedPath = null;

            for (const pattern of filePathPatterns) {
                const pathMatch = beforeText.match(pattern);
                if (pathMatch && pathMatch[1]) {
                    detectedPath = pathMatch[1];
                    break;
                }
            }

            const firstLine = code.split('\n')[0];
            if (!detectedPath && (firstLine.includes('//') || firstLine.includes('#') || firstLine.includes('/*'))) {
                const inlinePathMatch = firstLine.match(/[/\\]?([a-zA-Z0-9_-]+\/[a-zA-Z0-9_./-]+)/);
                if (inlinePathMatch) {
                    detectedPath = inlinePathMatch[1];
                }
            }

            updates.push({
                filePath: detectedPath || currentFilePath || 'unknown.txt',
                content: code,
                updateType: 'full',
                isNewFile: detectedPath && detectedPath !== currentFilePath
            });
        }

        return updates.length > 0 ? updates : null;
    }, []);

    /**
     * Stops/cancels the current ongoing request
     */
    const handleStopGeneration = useCallback(() => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setIsLoading(false);
            setConversationStatus('');
            // Note: The stop message will be added by the AbortError handler in handleSendMessage
        }
    }, [abortController]);

    /**
     * Helper function to build file context with fresh content from IndexedDB
     * This ensures we always send the latest modified code to the AI, not stale versions
     */
    const buildFileContextWithFreshContent = useCallback(async (file, projectId, serviceName, includeLineCount = true) => {
        let fileContent = file.content;

        // If we have projectId and serviceName, try to load fresh content from IndexedDB
        if (projectId && serviceName) {
            const storedFile = await indexedDbFileStorage.getFile(projectId, serviceName, file.path);
            if (storedFile && storedFile.content) {
                fileContent = storedFile.content;
            }
        }

        // Build context string with optional line numbers
        const lines = fileContent.split('\n');
        const contextLines = lines.map((line, idx) =>
            includeLineCount ? `${idx + 1}: ${line}` : line
        ).join('\n');

        return `File: ${file.path}\n\`\`\`\n${contextLines}\n\`\`\`\n`;
    }, []);

    /**
     * Handles sending a new message to the ADK backend.
     * 1. Ensures an ADK session exists (or creates one).
     * 2. Adds the user's message to the messages array.
     * 3. Sends the message to the backend and begins processing the streamed response (SSE).
     * 4. Parses events to handle text, function calls, and errors incrementally.
     * 5. Updates the UI with the assistant's response as it arrives.
     */
    const handleSendMessage = useCallback(async () => {
        if (inputValue.trim()) {
            // Check if this is an image generation request
            const imageRequest = detectImageGenerationRequest(inputValue);
            if (imageRequest) {
                // Handle image generation directly via API endpoint
                const userMessage = Messages.createUserMessage(inputValue);
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
                setInputValue('');
                setIsLoading(true);
                setConversationStatus('Generating Image...');

                const result = await generateImage(
                    imageRequest.prompt,
                    imageRequest.aspectRatio,
                    imageRequest.numberOfImages
                );

                setIsLoading(false);
                setConversationStatus('');

                /* eslint-disable no-console */
                console.log('Image generation complete. Result:', result);
                /* eslint-enable no-console */

                if (result.success && result.data && result.data.images && result.data.images.length > 0) {
                    // Create assistant message with image data
                    const imageData = result.data.images[0]; // Use first image

                    /* eslint-disable no-console */
                    console.log('Image generation result:', result);
                    console.log('Image data:', imageData);
                    console.log('Image URL:', imageData.url);
                    /* eslint-enable no-console */

                    const assistantMessage = Messages.createInfoMessage(
                        `I've generated an image based on your prompt: "${imageRequest.prompt}"`
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, assistantMessage));

                    // Automatically open Canvas with the image
                    const canvasData = {
                        type: 'image',
                        content: imageData.url,  // Canvas expects 'content' not 'data'
                        title: 'Generated Image',
                        prompt: imageData.prompt,
                        aspect_ratio: imageData.aspect_ratio,
                    };

                    /* eslint-disable no-console */
                    console.log('Setting canvas content:', canvasData);
                    console.log('Current isCanvasMode before:', isCanvasMode);
                    /* eslint-enable no-console */

                    // Mark that canvas was manually set to prevent auto-detection from overriding
                    manualCanvasSetRef.current = true;

                    setCanvasContent(canvasData);
                    // Canvas is always enabled, no need to set it

                    /* eslint-disable no-console */
                    console.log('Canvas content set for image');
                    /* eslint-enable no-console */

                    // Dispatch event to hide sidebar
                    window.dispatchEvent(new CustomEvent('canvasToggled', {
                        detail: {isActive: true}
                    }));
                } else {
                    const errorMessage = Messages.createErrorMessage(
                        result.message || 'Failed to generate image. Please try again.'
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                }
                return;
            }

            // Check if this is a video generation request
            const videoRequest = detectVideoGenerationRequest(inputValue);
            if (videoRequest) {
                // Handle video generation directly via API endpoint
                const userMessage = Messages.createUserMessage(inputValue);
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
                setInputValue('');
                setIsLoading(true);
                setConversationStatus('Generating Video (this may take 2-3 minutes)...');

                const result = await generateVideo(
                    videoRequest.prompt,
                    videoRequest.aspectRatio,
                    videoRequest.durationSeconds
                );

                setIsLoading(false);
                setConversationStatus('');

                /* eslint-disable no-console */
                console.log('Video generation complete. Result:', result);
                /* eslint-enable no-console */

                if (result.success && result.data && result.data.video) {
                    // Create assistant message with video data
                    const videoData = result.data.video;

                    /* eslint-disable no-console */
                    console.log('Video generation result:', result);
                    console.log('Video data:', videoData);
                    console.log('Video URL:', videoData.url);
                    /* eslint-enable no-console */

                    const assistantMessage = Messages.createInfoMessage(
                        `I've generated a video based on your prompt: "${videoRequest.prompt}"`
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, assistantMessage));

                    // Automatically open Canvas with the video
                    const canvasData = {
                        type: 'video',
                        content: videoData.url,  // Canvas expects 'content' not 'data'
                        title: 'Generated Video',
                        prompt: videoData.prompt,
                        aspect_ratio: videoData.aspect_ratio,
                        duration_seconds: videoData.duration_seconds,
                    };

                    /* eslint-disable no-console */
                    console.log('Setting canvas content:', canvasData);
                    console.log('Current isCanvasMode before:', isCanvasMode);
                    /* eslint-enable no-console */

                    // Mark that canvas was manually set to prevent auto-detection from overriding
                    manualCanvasSetRef.current = true;

                    setCanvasContent(canvasData);
                    // Canvas is always enabled, no need to set it

                    /* eslint-disable no-console */
                    console.log('Canvas content set for video');
                    /* eslint-enable no-console */

                    // Dispatch event to hide sidebar
                    window.dispatchEvent(new CustomEvent('canvasToggled', {
                        detail: {isActive: true}
                    }));
                } else {
                    const errorMessage = Messages.createErrorMessage(
                        result.message || 'Failed to generate video. Please try again.'
                    );
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                }
                return;
            }

            // Ensure we have an ADK session; create one on first user message
            let currentSessionId = sessionId;
            if (!currentSessionId) {
                // eslint-disable-next-line no-console
                console.log('🆕 No session ID - creating new session...');
                const sessionResult = await AdkClient.createSession();
                if (sessionResult.success) {
                    currentSessionId = sessionResult.sessionId;
                    setSessionId(currentSessionId);
                    sessionFilesSent.clear(); // Reset file tracking for new session
                    // eslint-disable-next-line no-console
                    console.log(`✅ New session created: ${currentSessionId}`);
                } else {
                    const errorMessage = Messages.createErrorMessage(sessionResult.message);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                    return;
                }
            } else {
                // eslint-disable-next-line no-console
                console.log(`♻️  Reusing existing session: ${currentSessionId}`);
            }

            const userMessage = Messages.createUserMessage(inputValue);

            // Track AI interaction
            if (window.analytics?.trackAIInteraction) {
                window.analytics.trackAIInteraction('chat_message', {
                    messageLength: inputValue.length,
                    hasProject: !!canvasProjectContext,
                    isCanvasMode: isCanvasMode,
                    aiMode: aiMode
                });
            }

            // Check if files were modified in Canvas mode - if so, clear chat history
            if (isCanvasMode && filesModifiedInCanvas) {
                // eslint-disable-next-line no-console
                console.log('🔄 Files modified in Canvas mode - clearing chat history and resending fresh source code');
                setMessages([userMessage]); // Clear history, keep only current message
                setFilesModifiedInCanvas(false); // Reset flag

                // Clear conversation context to force fresh start
                setConversationId(null);
                setConvName('');

                // CRITICAL: Clear session file tracking to force re-sending files with updated content
                AdkClient.clearSessionFiles(currentSessionId);
            } else {
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
                // NOTE: Don't mark session here - AdkClient.sendMessage will mark it after successfully sending files
                // AdkClient.markSessionInitialized(currentSessionId);
            }

            setInputValue('');
            // Clear localStorage input
            try {
                localStorage.removeItem('chatInputValue');
            } catch (e) {
                console.error('Failed to clear input from localStorage:', e);
            }
            setIsLoading(true);
            setConversationStatus('Deciphering User Intent');

            // Re-enable auto-scroll when user sends a new message
            shouldAutoScrollRef.current = true;

            // Create AbortController for this request
            const controller = new AbortController();
            setAbortController(controller);

            // Retry wrapper with exponential backoff for rate limit errors
            const maxRetries = 5;
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    // Send message and stream the assistant response via SSE
                    const userRequest = inputValue;
                    const filesToSend = attachedFiles.length > 0 ? [...attachedFiles] : null;
                    let messageToSend = '';

                    // Add project context if in canvas mode with selected project
                    let projectContextToSend = null;
                    if (isCanvasMode && canvasProjectContext && canvasProjectContext.project) {
                        projectContextToSend = {...canvasProjectContext};

                        // Try to load files from IndexedDB - check all services if no specific one is selected
                        let filesLoaded = false;
                        let serviceName = canvasProjectContext.selectedMicroservice?.name;

                        // Fallback: get service name from selectedResource (always load from localStorage)
                        if (!serviceName) {
                            const selectedResource = loadSelectedResourceFromStorage();
                            if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                serviceName = selectedResource.data.name;
                                // Also update the context with the loaded resource
                                projectContextToSend.selectedResource = selectedResource;
                                // eslint-disable-next-line no-console
                                console.log(`📌 Using service name from mia_selectedResource: ${serviceName}`);
                            }
                        }

                        if (serviceName) {
                            // We have a specific service selected, load its files
                            try {
                                // eslint-disable-next-line no-console
                                console.log(`🔍 Loading files for selected microservice: ${serviceName}`);
                                // eslint-disable-next-line no-console
                                console.log(`   Project ID: ${canvasProjectContext.projectId}`);

                                const filesFromDb = await indexedDbFileStorage.getAllFiles(
                                    canvasProjectContext.projectId,
                                    serviceName
                                );

                                // eslint-disable-next-line no-console
                                console.log(`📂 Retrieved ${filesFromDb?.length || 0} files from IndexedDB`);

                                if (filesFromDb && filesFromDb.length > 0) {
                                    // Filter out deleted files for run_sse context
                                    const activeFiles = filesFromDb.filter(file => file.state !== 'deleted');
                                    const allFilesWithContent = activeFiles.map(file => ({
                                        path: file.filePath,
                                        content: file.content
                                    }));
                                    projectContextToSend.allFiles = allFilesWithContent;

                                    // CRITICAL FIX: Update canvasProjectContext state with allFiles AND selectedMicroservice
                                    // so that file request handlers can access the files and service name
                                    // Include full service data with repository URLs for Pull/Push operations
                                    const fullServiceData = canvasProjectContext.configuration?.services?.[serviceName];
                                    const microserviceInfo = fullServiceData ? {
                                        ...fullServiceData,
                                        name: serviceName
                                    } : {
                                        name: serviceName,
                                        type: canvasProjectContext.configuration?.services?.[serviceName]?.type
                                    };

                                    setCanvasProjectContext(prev => ({
                                        ...prev,
                                        allFiles: allFilesWithContent,
                                        selectedMicroservice: microserviceInfo
                                    }));

                                    // Also update projectContextToSend for backend
                                    projectContextToSend.selectedMicroservice = microserviceInfo;

                                    filesLoaded = true;
                                    // eslint-disable-next-line no-console
                                    console.log(`📂 Loaded ${filesFromDb.length} files from IndexedDB for AI context`);
                                    // eslint-disable-next-line no-console
                                    console.log(`   Sample files: ${filesFromDb.slice(0, 3).map(f => f.filePath).join(', ')}`);
                                }
                            } catch (error) {
                                // eslint-disable-next-line no-console
                                console.error('Failed to load files from IndexedDB:', error);
                            }
                        }

                        // If no service selected or no files found, try to find ANY service with files
                        if (!filesLoaded && canvasProjectContext.configuration?.services) {
                            // eslint-disable-next-line no-console
                            console.log(`🔍 No service selected or no files found, searching all services...`);
                            const services = Object.values(canvasProjectContext.configuration.services);

                            for (const service of services) {
                                try {
                                    const filesFromDb = await indexedDbFileStorage.getAllFiles(canvasProjectContext.projectId, service.name);

                                    if (filesFromDb && filesFromDb.length > 0) {
                                        // Filter out deleted files for run_sse context
                                        const activeFiles = filesFromDb.filter(file => file.state !== 'deleted');
                                        const allFilesWithContent = activeFiles.map(file => ({
                                            path: file.filePath,
                                            content: file.content
                                        }));

                                        projectContextToSend.allFiles = allFilesWithContent;
                                        // Auto-set the microservice so file requests work
                                        // Include full service data with repository URLs for Pull/Push operations
                                        projectContextToSend.selectedMicroservice = {
                                            ...service,
                                            name: service.name,
                                            type: service.type
                                        };
                                        // Also set selectedResource as fallback for file requests
                                        projectContextToSend.selectedResource = {
                                            type: 'service',
                                            data: service
                                        };

                                        // CRITICAL FIX: Update canvasProjectContext state with allFiles, selectedMicroservice AND selectedResource
                                        // so that file request handlers can access the files and service name
                                        setCanvasProjectContext(prev => ({
                                            ...prev,
                                            allFiles: allFilesWithContent,
                                            selectedMicroservice: projectContextToSend.selectedMicroservice,
                                            selectedResource: projectContextToSend.selectedResource
                                        }));

                                        filesLoaded = true;
                                        // eslint-disable-next-line no-console
                                        console.log(`📂 Auto-loaded ${filesFromDb.length} files from service: ${service.name}`);
                                        // eslint-disable-next-line no-console
                                        console.log(`   Sample files: ${filesFromDb.slice(0, 3).map(f => f.filePath).join(', ')}`);
                                        break; // Stop after finding first service with files
                                    }
                                } catch (error) {
                                    // Continue to next service
                                }
                            }
                        }

                        if (!filesLoaded) {
                            // eslint-disable-next-line no-console
                            console.warn(`⚠️ No files found in IndexedDB for project ${canvasProjectContext.projectId}`);
                            // eslint-disable-next-line no-console
                            console.warn(`   Make sure to Pull files from the Canvas before chatting with AI`);
                        }

                        // Start with user request - MOST IMPORTANT
                        messageToSend = `[USER REQUEST]\n${userRequest}\n\n---\n\n`;

                        // Build condensed context
                        const canvasContext = [];
                        canvasContext.push('[CANVAS MODE CONTEXT]');

                        // Build context summary for display
                        const contextInfo = [];
                        if (canvasProjectContext.tenant) {
                            contextInfo.push(`Tenant: ${canvasProjectContext.tenant.name}`);
                            canvasContext.push(`Tenant: ${canvasProjectContext.tenant.name} (${canvasProjectContext.tenant.id})`);
                        }
                        if (canvasProjectContext.project) {
                            contextInfo.push(`Project: ${canvasProjectContext.project.name}`);
                            canvasContext.push(`Project: ${canvasProjectContext.project.name} (${canvasProjectContext.project.id})`);
                        }
                        if (canvasProjectContext.revision) {
                            contextInfo.push(`Revision: ${canvasProjectContext.revision.name}`);
                            canvasContext.push(`Revision: ${canvasProjectContext.revision.name}`);
                        }
                        canvasContext.push('');
                        canvasContext.push('🚫 FORBIDDEN: GitLab API tools (get_file_content, create_file, etc.)');
                        canvasContext.push('✅ REQUIRED: Use Mia-Platform MCP tools, generate unified diffs');
                        canvasContext.push('');

                        // Add selected resource context (service, endpoint, etc.)
                        if (canvasProjectContext.selectedResource) {
                            const resource = canvasProjectContext.selectedResource;
                            contextInfo.push(`Selected ${resource.type}: ${resource.name}`);

                            // For collections, include the full JSON definition
                            if (resource.type === 'collection' && resource.data) {
                                const collectionJson = JSON.stringify(resource.data, null, 2);
                                const collectionContext = `[CURRENT COLLECTION]\nName: ${resource.name}\nDefinition:\n\`\`\`json\n${collectionJson}\n\`\`\`\n`;
                                canvasContext.push(collectionContext);

                                // Also include list of all collections for reference
                                if (canvasProjectContext.collections && Object.keys(canvasProjectContext.collections).length > 1) {
                                    const allCollectionNames = Object.keys(canvasProjectContext.collections);
                                    canvasContext.push(`All Collections: ${allCollectionNames.join(', ')}`);
                                }
                            }
                        }

                        // If no specific resource selected, provide project overview with collections
                        if (!canvasProjectContext.selectedResource && canvasProjectContext.configuration) {
                            const config = canvasProjectContext.configuration;
                            const configSummary = [];
                            if (config.services) configSummary.push(`Services: ${Object.keys(config.services).length}`);
                            if (config.endpoints) configSummary.push(`Endpoints: ${Object.keys(config.endpoints).length}`);
                            if (canvasProjectContext.collections) {
                                const collectionCount = Object.keys(canvasProjectContext.collections).length;
                                configSummary.push(`Collections: ${collectionCount}`);

                                // Include collection names for context
                                if (collectionCount > 0) {
                                    const collectionNames = Object.keys(canvasProjectContext.collections);
                                    canvasContext.push(`Available Collections: ${collectionNames.join(', ')}`);
                                }
                            }
                            if (configSummary.length > 0) {
                                canvasContext.push(`Project Overview: ${configSummary.join(', ')}`);
                            }
                        }

                        // Add selected microservice context when viewing source code
                        if (canvasProjectContext.selectedMicroservice) {
                            const microservice = canvasProjectContext.selectedMicroservice;
                            contextInfo.push(`Microservice: ${microservice.name}`);

                            // Add microservice to context array
                            canvasContext.push('');
                            canvasContext.push('[CURRENT MICROSERVICE]');
                            canvasContext.push(`Name: ${microservice.name}`);
                            canvasContext.push(`Type: ${microservice.type || 'custom'}`);
                            if (microservice.repositoryUrl) {
                                canvasContext.push(`Repository: ${microservice.repositoryUrl}`);
                            }
                        }

                        // Add selected file context with content - HIGHLIGHTED AS SOURCE CODE TO MODIFY
                        if (canvasProjectContext.selectedFile) {
                            const file = canvasProjectContext.selectedFile;
                            contextInfo.push(`Viewing File: ${file.path || file.name}`);

                            // Use helper function to get fresh content from IndexedDB
                            const fileContext = await buildFileContextWithFreshContent(
                                file,
                                canvasProjectContext.projectId,
                                canvasProjectContext.selectedMicroservice?.name,
                                false // DON'T include line numbers - breaks diff application
                            );
                            if (fileContext) {
                                // Store for later - will be added in SOURCE CODE section
                                canvasContext.selectedFileContext = fileContext;
                            }
                        }

                        // Add complete file tree context (condensed)
                        if (canvasProjectContext.allFiles && canvasProjectContext.allFiles.length > 0) {
                            canvasContext.push('');
                            canvasContext.push(`[FILES - ${canvasProjectContext.allFiles.length} total]`);

                            // Group files by directory for more compact display
                            const filesByDir = {};
                            canvasProjectContext.allFiles.forEach(f => {
                                const parts = f.path.split('/');
                                const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : '.';
                                if (!filesByDir[dir]) filesByDir[dir] = [];
                                filesByDir[dir].push(parts[parts.length - 1]);
                            });

                            // Show condensed grouped structure
                            Object.keys(filesByDir).sort().forEach(dir => {
                                const files = filesByDir[dir];
                                if (dir === '.') {
                                    canvasContext.push(`Root: ${files.join(', ')}`);
                                } else {
                                    canvasContext.push(`${dir}/: ${files.join(', ')}`);
                                }
                            });

                            // Build list of key files to include full content
                            const keyFiles = [];

                            // ALWAYS include the currently selected file first (if any) - but ONLY if it's a code file
                            if (canvasProjectContext.selectedFile && canvasProjectContext.selectedFile.path) {
                                const selectedFilePath = canvasProjectContext.selectedFile.path;
                                // Skip binary files even if selected
                                if (isCodeFile(selectedFilePath)) {
                                    const selectedFileData = canvasProjectContext.allFiles.find(f => f.path === selectedFilePath);
                                    if (selectedFileData && selectedFileData.content) {
                                        keyFiles.push(selectedFileData);
                                    }
                                }
                            }

                            // Then add other important files (up to 15 total, excluding node_modules, lock files, and binary files)
                            const otherFiles = canvasProjectContext.allFiles
                                .filter(f =>
                                    !f.path.includes('node_modules') &&
                                    isCodeFile(f.path) &&  // Filters out binary files, lock files, and other excluded files
                                    f.content &&
                                    f.path !== (canvasProjectContext.selectedFile?.path || '')  // Don't duplicate selected file
                                )
                                .slice(0, 14);  // Leave room for selected file

                            keyFiles.push(...otherFiles);

                            // Count files that were filtered out
                            const filteredFiles = canvasProjectContext.allFiles.filter(f => !isCodeFile(f.path)).length;
                            if (filteredFiles > 0) {
                                // eslint-disable-next-line no-console
                                console.log(`🚫 Filtered out ${filteredFiles} files (binary files, lock files, etc.) from AI context`);
                            }

                            // DON'T send file contents upfront - AI will request them on-demand via tools
                            // This dramatically reduces initial token usage

                            // Include ALL code files in the available list (not just the 15 key files)
                            // This gives AI complete visibility of what files exist and their metadata
                            canvasContext.keyFilesAvailable = canvasProjectContext.allFiles
                                .filter(f => isCodeFile(f.path) && f.content)
                                .map(f => ({
                                    path: f.path,
                                    lines: f.content.split('\n').length,
                                    extension: f.path.split('.').pop()
                                }));

                            // eslint-disable-next-line no-console
                            console.log(`📦 Project context prepared: ${canvasProjectContext.allFiles.length} files total, ${canvasContext.keyFilesAvailable.length} code files available (AI can request content on-demand)`);
                        } else if (canvasProjectContext.project && canvasProjectContext.selectedMicroservice) {
                            // WARN: No files loaded - AI will have no file context
                            // eslint-disable-next-line no-console
                            console.warn(`⚠️ NO FILES IN PROJECT CONTEXT! Project: ${canvasProjectContext.project.name}, Service: ${canvasProjectContext.selectedMicroservice.name}. AI will have NO file context. User needs to click Pull to fetch files first.`);
                        }

                        // Build final message structure with clear sections
                        const contextSections = [];

                        // 1. Configuration and project info (non-code)
                        if (canvasContext.length > 1) {
                            contextSections.push(canvasContext.join('\n'));
                        }

                        // 2. SOURCE CODE section - clearly separated and highlighted
                        const sourceCodeSections = [];

                        // Add selected file (primary focus) - ONLY if user is viewing it
                        if (canvasContext.selectedFileContext) {
                            sourceCodeSections.push('[🎯 SOURCE CODE TO MODIFY]');
                            sourceCodeSections.push(canvasContext.selectedFileContext);
                        }

                        // Add available files summary (AI can request content via tools)
                        if (canvasContext.keyFilesAvailable && canvasContext.keyFilesAvailable.length > 0) {
                            if (sourceCodeSections.length === 0) {
                                sourceCodeSections.push('[📝 AVAILABLE SOURCE FILES]');
                                sourceCodeSections.push('Use tools to request file content when needed:');
                            } else {
                                sourceCodeSections.push('');
                                sourceCodeSections.push('[📚 ADDITIONAL FILES AVAILABLE]');
                                sourceCodeSections.push('Request content via tools when needed:');
                            }
                            sourceCodeSections.push('');
                            canvasContext.keyFilesAvailable.forEach(f => {
                                sourceCodeSections.push(`- ${f.path} (${f.lines} lines, .${f.extension})`);
                            });
                        }

                        // Append context sections
                        if (contextSections.length > 0) {
                            messageToSend += contextSections.join('\n');
                        }

                        // Append source code section with clear separator
                        if (sourceCodeSections.length > 0) {
                            messageToSend += '\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n';
                            messageToSend += sourceCodeSections.join('\n');
                        }
                    } else {
                        // No canvas mode - just use the user request
                        // In Compose mode, include tenant context so the AI knows which tenant to operate in
                        if (hideCanvas) {
                            const composeTenant = miaFlowAuth.getSelectedTenant();
                            if (composeTenant) {
                                const tenantLine = `Tenant: ${composeTenant.name || composeTenant.tenantId} (id: ${composeTenant.tenantId})`;
                                messageToSend = `[CONTEXT]\n${tenantLine}\nNote: No project is selected. Operate on the tenant level or provide general guidance.\n\n[USER REQUEST]\n${userRequest}`;
                            } else {
                                messageToSend = userRequest;
                            }
                        } else {
                            messageToSend = userRequest;
                        }
                    }

                    // Add AI mode instruction at the beginning of the message
                    if (aiMode === 'plan') {
                        messageToSend = `[PLAN MODE ACTIVE]\n⚠️ CRITICAL INSTRUCTION: You are currently in PLAN MODE. In this mode, you MUST NOT create, modify, deploy, or execute anything. You should ONLY provide information, explanations, suggestions, and planning guidance. Do not use any tools that would create or modify resources (projects, services, files, deployments, etc.). Only use read-only tools to gather information and provide helpful guidance to the user. You MAY use the curl_request_tool to make read-only HTTP requests (e.g. GET) to inspect or query APIs and retrieve information.\n\n${messageToSend}`;
                    }

                    // Add custom AI instructions at the very top (after all mode instructions)
                    // Only inject as text when there is NO active playbook — when a playbook is active
                    // the BE fetches all resources (agents, skills, specs, prompts) from the catalog
                    // via intentId + intentInstructionsOnly, so we must not duplicate them here.
                    if (customAIInstructions && !customAIInstructionsPlaybookData) {
                        messageToSend = `[CUSTOM AI INSTRUCTIONS]\n${customAIInstructions}\n\n${messageToSend}`;
                    }

                    const activeTenantId = miaFlowAuth.getSelectedTenant()?.tenantId || null;

                    // Map selected tool IDs (tool.id = metadata.name slug) to runtimeName for the backend
                    // Use ref to avoid stale closure — selectedTools is not in the useCallback dep array
                    const runtimeNameById = new Map(availableToolsRef.current.map(t => [t.id, t.runtimeName]));
                    const selectedToolRuntimeNames = (selectedToolsRef.current || [])
                        .map(id => runtimeNameById.get(id) || id)
                        .filter(Boolean);

                    const sendResult = await AdkClient.sendMessage(
                        currentSessionId,
                        messageToSend,
                        controller.signal,
                        projectContextToSend,
                        isCanvasMode,
                        aiMode,
                        filesToSend,
                        selectedToolRuntimeNames,
                        selectedModel,
                        null,
                        activeTenantId
                    );

                    // Clear attached files after sending
                    setAttachedFiles([]);

                    if (!sendResult.success) {
                    setIsLoading(false);
                    setAbortController(null);
                    const errorMessage = Messages.createErrorMessage(sendResult.message);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                    return;
                }

                const response = sendResult.response;
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let accumulatedText = '';
                let stopProcessingEvents = false;
                let intermediateMessageCreated = false;
                // Track whether any partial=true streaming chunks have been received.
                // Used to detect ADK-style responses where the final partial=false event
                // contains the COMPLETE text (not just the last token), which would
                // otherwise cause the response to appear doubled.
                let hasSeenPartialTrue = false;

                // Store browser session metadata when detected from start_browser tool
                let pendingBrowserSession = null;

                // Don't create an intermediate message yet - wait until we start receiving content
                // This prevents replacing/hiding previous messages that contain diagrams or content

                // Queue for pending interception promises
                const pendingInterceptions = [];

                try {
                    // Parse SSE frames and update chat incrementally (text, functionCall, functionResponse, error)
                    const parser = createParser({
                        onEvent: (event) => {
                            try {
                                const eventData = JSON.parse(event.data);

                                // Handle diffspec events for automatic code merging
                                if (eventData.type === 'diffspec') {
                                    // eslint-disable-next-line no-console
                                    console.log('📝 [SSE-Main] Diffspec event received:', {
                                        file: eventData.file_path,
                                        diffLength: eventData.diff_content?.length,
                                        conversationId: eventData.conversation_id
                                    });

                                    // Dispatch custom event for useAutoDiffApplier hook
                                    window.dispatchEvent(new CustomEvent('diffspec', {
                                        detail: eventData
                                    }));

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request events (AI requesting files from IndexedDB)
                                if (eventData.type === 'file_request') {
                                    // eslint-disable-next-line no-console
                                    console.log('📂 [SSE-Main] File request event received:', {
                                        file: eventData.file_path,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        requestedServiceName: eventData.service_name,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load file from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            // eslint-disable-next-line no-console
                                            console.log('🔄 [File Request] Starting async handler for:', eventData.file_path);

                                            const projectId = canvasProjectContext?.projectId;
                                            // Use service_name from the event if specified, otherwise use current context
                                            let serviceName = eventData.service_name || canvasProjectContext?.selectedMicroservice?.name;

                                            // eslint-disable-next-line no-console
                                            console.log('🔍 [File Request] Context check:', {
                                                projectId,
                                                serviceName,
                                                requestedServiceName: eventData.service_name,
                                                hasCanvasContext: !!canvasProjectContext
                                            });

                                            // Fallback to selectedResource if no serviceName yet
                                            if (!serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.log('🔍 [File Request] No serviceName from canvas context, trying selectedResource...');
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                    // eslint-disable-next-line no-console
                                                    console.log('✅ [File Request] Found serviceName from selectedResource:', serviceName);
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [File Request] Cannot load file - missing project or service context', {
                                                    projectId,
                                                    serviceName
                                                });

                                                // eslint-disable-next-line no-console
                                                console.log('📤 [File Request] Posting error response to backend...');

                                                // POST error back to backend
                                                const errorResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName || '',
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                console.log('✅ [File Request] Error response posted, status:', errorResponse.status);
                                                return;
                                            }

                                            // eslint-disable-next-line no-console
                                            console.log('🗄️ [File Request] Loading file from IndexedDB:', {
                                                projectId,
                                                serviceName,
                                                filePath: eventData.file_path
                                            });

                                            // Load file from IndexedDB
                                            const fileData = await indexedDbFileStorage.getFile(
                                                projectId,
                                                serviceName,
                                                eventData.file_path
                                            );

                                            // eslint-disable-next-line no-console
                                            console.log('📊 [File Request] IndexedDB query result:', {
                                                found: !!fileData,
                                                hasContent: !!(fileData?.content),
                                                contentLength: fileData?.content?.length || 0
                                            });

                                            if (fileData && fileData.content) {
                                                // eslint-disable-next-line no-console
                                                console.log(`✅ [File Request] Loaded file from IndexedDB: ${eventData.file_path} (${fileData.content.length} chars)`);

                                                // eslint-disable-next-line no-console
                                                console.log(`📤 [File Request] Posting file content to backend: ${config.agentUrl}/api/canvas-files/submit`);

                                                // POST file content back to backend
                                                const postResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: fileData.content,
                                                        service_name: serviceName,
                                                        success: true
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                console.log(`✅ [File Request] Posted file content, status: ${postResponse.status}, ok: ${postResponse.ok}`);

                                                // eslint-disable-next-line no-console
                                                console.log(`📤 Posted file content back to backend for request ${eventData.request_id}`);
                                            } else {
                                                // eslint-disable-next-line no-console
                                                console.error(`❌ [File Request] File not found in IndexedDB: ${eventData.file_path}`);

                                                // eslint-disable-next-line no-console
                                                console.log('📤 [File Request] Posting error response (file not found) to backend...');

                                                // POST error back to backend
                                                const errorResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName,
                                                        success: false,
                                                        error: `File '${eventData.file_path}' not found in IndexedDB`
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                console.log(`✅ [File Request] Error response posted, status: ${errorResponse.status}`);
                                            }
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [File Request] Error loading file from IndexedDB:', error);

                                            // POST error back to backend
                                            try {
                                                // eslint-disable-next-line no-console
                                                console.log('📤 [File Request] Posting error response (exception) to backend...');

                                                const errorResponse = await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: eventData.service_name || '',
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                console.log(`✅ [File Request] Exception error response posted, status: ${errorResponse.status}`);
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [File Request] Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle bash_script_execution events (execute bash commands on IndexedDB files)
                                if (eventData.type === 'bash_script_execution') {
                                    // eslint-disable-next-line no-console
                                    console.log('🖥️ [Bash Execution] Received bash script execution request:', {
                                        executionId: eventData.execution_id,
                                        script: eventData.script,
                                        conversationId: eventData.conversation_id
                                    });

                                    // Execute script asynchronously
                                    (async () => {
                                        try {
                                            // Get project context - try multiple sources
                                            let projectId = null;
                                            let serviceName = null;

                                            // 1. Try from event data (if service_name provided by backend)
                                            if (eventData.service_name) {
                                                serviceName = eventData.service_name;
                                            }

                                            // 2. Try from canvasProjectContext state
                                            if (canvasProjectContext) {
                                                projectId = canvasProjectContext.projectId;
                                                if (!serviceName) {
                                                    serviceName = canvasProjectContext.selectedMicroservice?.name;
                                                }
                                            }

                                            // 3. Fallback to IndexedDB (in case state not updated yet)
                                            if (!projectId || !serviceName) {
                                                try {
                                                    const savedContext = await indexedDbStorage.getItem('canvasProjectContext');
                                                    if (savedContext) {
                                                        projectId = projectId || savedContext.projectId;
                                                        serviceName = serviceName || savedContext.selectedMicroservice?.name;
                                                        // eslint-disable-next-line no-console
                                                        console.log('📦 [Bash Execution] Loaded context from IndexedDB:', {
                                                            projectId,
                                                            serviceName
                                                        });
                                                    }
                                                } catch (dbError) {
                                                    // eslint-disable-next-line no-console
                                                    console.warn('⚠️ [Bash Execution] Could not load from IndexedDB:', dbError);
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                throw new Error('Missing project or service context');
                                            }

                                            // eslint-disable-next-line no-console
                                            console.log('🔧 [Bash Execution] Initializing VirtualFileSystem...');

                                            // Create virtual filesystem and load files from IndexedDB
                                            const vfs = new VirtualFileSystem();
                                            await vfs.loadFromIndexedDB(projectId, serviceName);

                                            // eslint-disable-next-line no-console
                                            console.log('▶️ [Bash Execution] Executing script...');

                                            // Execute the script
                                            const result = await vfs.executeScript(eventData.script);

                                            // eslint-disable-next-line no-console
                                            console.log('✅ [Bash Execution] Script executed:', {
                                                exitCode: result.exit_code,
                                                modifiedFilesCount: result.modifiedFiles.length,
                                                stdout: result.stdout.substring(0, 100) + '...'
                                            });

                                            let userApproved = true;

                                            // If files were modified, ask for user confirmation
                                            if (result.modifiedFiles.length > 0) {
                                                // eslint-disable-next-line no-console
                                                console.log('⚠️ [Bash Execution] Files modified, requesting user confirmation...');

                                                userApproved = await showScriptExecutionConfirmation(
                                                    result.modifiedFiles,
                                                    eventData.script,
                                                    result.stdout
                                                );

                                                // eslint-disable-next-line no-console
                                                console.log(`👤 [Bash Execution] User ${userApproved ? 'approved' : 'rejected'} changes`);

                                                // If approved, sync changes to IndexedDB
                                                if (userApproved) {
                                                    // eslint-disable-next-line no-console
                                                    console.log('💾 [Bash Execution] Syncing changes to IndexedDB...');

                                                    await vfs.syncChangesToIndexedDB(result.modifiedFiles);

                                                    // eslint-disable-next-line no-console
                                                    console.log('✅ [Bash Execution] Changes synced to IndexedDB');
                                                }
                                            }

                                            // Cleanup
                                            vfs.cleanup();

                                            // POST results back to backend
                                            // eslint-disable-next-line no-console
                                            console.log('📤 [Bash Execution] Posting results to backend...');

                                            const postResponse = await authorizedFetch(`${config.agentUrl}/api/bash-execution/submit`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    execution_id: eventData.execution_id,
                                                    conversation_id: eventData.conversation_id,
                                                    success: true,
                                                    stdout: result.stdout,
                                                    stderr: result.stderr,
                                                    exit_code: result.exit_code,
                                                    files_modified: result.modifiedFiles.map(f => f.path),
                                                    user_approved: userApproved
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            console.log(`✅ [Bash Execution] Results posted, status: ${postResponse.status}`);

                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [Bash Execution] Error:', error);

                                            // Show error to user
                                            showScriptExecutionError(error.message);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/bash-execution/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        execution_id: eventData.execution_id,
                                                        conversation_id: eventData.conversation_id,
                                                        success: false,
                                                        error: error.message,
                                                        stdout: '',
                                                        stderr: error.message,
                                                        exit_code: 1,
                                                        files_modified: [],
                                                        user_approved: false
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Bash Execution] Failed to POST error to backend:', postError);
                                            }
                                        }
                                    })();

                                    return;
                                }

                                // Handle git_clone_completed events (repository cloned to workspace)
                                if (eventData.type === 'git_clone_completed') {
                                    // eslint-disable-next-line no-console
                                    console.log('📦 [SSE-Main] Git clone completed event received:', {
                                        owner: eventData.owner,
                                        repo: eventData.repo,
                                        clonePath: eventData.clone_path,
                                        filesCount: eventData.files_count,
                                        filePathsCount: eventData.file_paths?.length,
                                        requestId: eventData.request_id
                                    });

                                    // Dispatch custom event for file tree to refresh
                                    window.dispatchEvent(new CustomEvent('git_clone_completed', {
                                        detail: eventData
                                    }));

                                    // Show initial toast notification
                                    showToast(
                                        `✅ Repository ${eventData.owner}/${eventData.repo} cloned (${eventData.files_count} files). Indexing to project...`,
                                        'info'
                                    );

                                    // Start indexing files to IndexedDB in the background
                                    if (eventData.file_paths && eventData.file_paths.length > 0 && canvasProjectContext) {
                                        (async () => {
                                            try {
                                                const projectId = canvasProjectContext?.projectId;
                                                let serviceName = canvasProjectContext?.selectedMicroservice?.name;

                                                // Fallback to selectedResource if no selectedMicroservice
                                                if (!serviceName) {
                                                    const selectedResource = loadSelectedResourceFromStorage();
                                                    if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                        serviceName = selectedResource.data.name;
                                                    }
                                                }

                                                if (!projectId || !serviceName) {
                                                    // eslint-disable-next-line no-console
                                                    console.error('❌ Cannot index cloned files - missing project or service context');
                                                    showToast('⚠️ Cannot index files: No active project/service', 'warning');
                                                    return;
                                                }

                                                // Use source-code-for-analysis prefix so files appear under a dedicated folder
                                                const pathPrefix = `source-code-for-analysis/${eventData.repo}`;
                                                const clonePath = eventData.clone_path;
                                                const filePaths = eventData.file_paths;

                                                // eslint-disable-next-line no-console
                                                console.log(`📂 Indexing ${filePaths.length} files to IndexedDB with prefix: ${pathPrefix}`);

                                                // Batch process files (10 at a time to avoid overwhelming the backend)
                                                const batchSize = 10;
                                                let indexed = 0;

                                                for (let i = 0; i < filePaths.length; i += batchSize) {
                                                    const batch = filePaths.slice(i, i + batchSize);

                                                    await Promise.all(batch.map(async (filePath) => {
                                                        try {
                                                            // Fetch file content from backend
                                                            const response = await authorizedFetch(
                                                                `${config.agentUrl}/api/canvas-files/github-clone/${encodeURIComponent(clonePath)}?file_path=${encodeURIComponent(filePath)}`,
                                                                {
                                                                    method: 'GET',
                                                                    headers: getBeHeaders()
                                                                }
                                                            );

                                                            if (!response.ok) {
                                                                // eslint-disable-next-line no-console
                                                                console.warn(`⚠️  Failed to fetch ${filePath}: ${response.statusText}`);
                                                                return;
                                                            }

                                                            const data = await response.json();

                                                            // Skip binary files (images, videos, etc.) - not useful for code analysis
                                                            if (data.is_binary) {
                                                                // eslint-disable-next-line no-console
                                                                console.log(`⏭️  Skipping binary file: ${filePath}`);
                                                                return;
                                                            }

                                                            if (data.success && data.content) {
                                                                // Save to IndexedDB with prefixed path
                                                                const indexedPath = `${pathPrefix}/${filePath}`;
                                                                await indexedDbFileStorage.saveFile(
                                                                    projectId,
                                                                    serviceName,
                                                                    indexedPath,
                                                                    data.content,
                                                                    {
                                                                        state: 'original',
                                                                        isGitHubClone: true,
                                                                        sourceRepo: `${eventData.owner}/${eventData.repo}`
                                                                    }
                                                                );
                                                                indexed++;
                                                            }
                                                        } catch (error) {
                                                            // eslint-disable-next-line no-console
                                                            console.error(`❌ Error indexing ${filePath}:`, error);
                                                        }
                                                    }));

                                                    // Show progress
                                                    // eslint-disable-next-line no-console
                                                    console.log(`📊 Indexed ${indexed}/${filePaths.length} files...`);
                                                }

                                                // eslint-disable-next-line no-console
                                                console.log(`✅ Successfully indexed ${indexed} files from ${eventData.repo}`);

                                                // Show completion toast
                                                showToast(
                                                    `✅ Repository ${eventData.repo} indexed! ${indexed} files available in source-code-for-analysis/`,
                                                    'success'
                                                );

                                                // Trigger file tree refresh
                                                window.dispatchEvent(new CustomEvent('git_clone_indexed', {
                                                    detail: { ...eventData, indexed_count: indexed }
                                                }));

                                                // Check if there's a pending initial prompt from new project creation
                                                const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
                                                const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');
                                                const pendingFiles = sessionStorage.getItem('mia_pending_initial_files');

                                                if (pendingPrompt) {
                                                    console.log('🚀 Git clone indexed, triggering initial prompt:', pendingPrompt);

                                                    // Clear the pending flags
                                                    sessionStorage.removeItem('mia_pending_initial_prompt');
                                                    sessionStorage.removeItem('mia_pending_initial_mode');
                                                    sessionStorage.removeItem('mia_pending_initial_files');

                                                    // Wait for UI to settle, then trigger the coding prompt
                                                    setTimeout(() => {
                                                        const chatInput = document.querySelector('.message-input');
                                                        if (chatInput) {
                                                            // Attach files if any
                                                            if (pendingFiles) {
                                                                try {
                                                                    const filesArray = JSON.parse(pendingFiles);
                                                                    window.dispatchEvent(new CustomEvent('attach-files-to-chat', {
                                                                        detail: { files: filesArray }
                                                                    }));
                                                                } catch (e) {
                                                                    console.warn('Failed to parse pending files:', e);
                                                                }
                                                            }

                                                            // Set input and trigger submit
                                                            chatInput.value = pendingPrompt;
                                                            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                                                            chatInput.closest('form')?.dispatchEvent(submitEvent);

                                                            // Set flag to ask about Mia components after this response
                                                            sessionStorage.setItem('mia_ask_about_components_next', 'true');
                                                        }
                                                    }, 1000);
                                                }

                                            } catch (error) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Error indexing cloned files:', error);
                                                showToast('❌ Failed to index cloned repository', 'error');
                                            }
                                        })();
                                    }

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request_multiple events (AI requesting multiple files from IndexedDB)
                                if (eventData.type === 'file_request_multiple') {
                                    // eslint-disable-next-line no-console
                                    console.log('📂 [SSE-Main] Multiple files request event received:', {
                                        files: eventData.file_paths,
                                        fileCount: eventData.file_paths?.length,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        requestedServiceName: eventData.service_name,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load files from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            const projectId = canvasProjectContext?.projectId;
                                            // Use service_name from the event if specified, otherwise use current context
                                            let serviceName = eventData.service_name || canvasProjectContext?.selectedMicroservice?.name;

                                            // Fallback to selectedResource if no serviceName yet
                                            if (!serviceName) {
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Cannot load files - missing project or service context');
                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        service_name: serviceName || '',
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Load all files from IndexedDB
                                            const filesData = [];
                                            for (const filePath of eventData.file_paths) {
                                                const fileData = await indexedDbFileStorage.getFile(
                                                    projectId,
                                                    serviceName,
                                                    filePath
                                                );

                                                if (fileData && fileData.content) {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: fileData.content,
                                                        success: true
                                                    });
                                                    // eslint-disable-next-line no-console
                                                    console.log(`✅ Loaded file from IndexedDB: ${filePath} (${fileData.content.length} chars)`);
                                                } else {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: '',
                                                        success: false,
                                                        error: `File not found in IndexedDB`
                                                    });
                                                    // eslint-disable-next-line no-console
                                                    console.error(`❌ File not found in IndexedDB: ${filePath}`);
                                                }
                                            }

                                            // POST all files back to backend
                                            await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    files: filesData,
                                                    service_name: serviceName,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            console.log(`📤 Posted ${filesData.length} file(s) back to backend for request ${eventData.request_id}`);
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ Error loading files from IndexedDB:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        service_name: eventData.service_name || '',
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request_lines events (AI requesting specific line ranges from IndexedDB)
                                if (eventData.type === 'file_request_lines') {
                                    // eslint-disable-next-line no-console
                                    console.log('📂 [SSE-Main] File lines request event received:', {
                                        file: eventData.file_path,
                                        startLine: eventData.start_line,
                                        endLine: eventData.end_line,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        requestedServiceName: eventData.service_name,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load file lines from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            const projectId = canvasProjectContext?.projectId;
                                            let serviceName = eventData.service_name || canvasProjectContext?.selectedMicroservice?.name;

                                            // Fallback to selectedResource if no serviceName yet
                                            if (!serviceName) {
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                }
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [File Lines Request] Cannot load file - missing project or service context');

                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName || '',
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: eventData.start_line,
                                                        actual_end: eventData.end_line,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Load file from IndexedDB
                                            const fileData = await indexedDbFileStorage.getFile(
                                                projectId,
                                                serviceName,
                                                eventData.file_path
                                            );

                                            if (fileData && fileData.content) {
                                                // Extract requested line range
                                                const allLines = fileData.content.split('\n');
                                                const totalLines = allLines.length;

                                                // Adjust bounds silently if out of range (1-indexed)
                                                let actualStart = Math.max(1, eventData.start_line);
                                                let actualEnd = Math.min(totalLines, eventData.end_line);
                                                actualStart = Math.min(actualStart, totalLines);

                                                const wasAdjusted = (actualStart !== eventData.start_line) || (actualEnd !== eventData.end_line);

                                                // Extract lines (convert to 0-indexed for array)
                                                const extractedLines = allLines.slice(actualStart - 1, actualEnd);

                                                // Format with line numbers in diffspec style
                                                const numberedContent = extractedLines
                                                    .map((line, idx) => `${actualStart + idx}: ${line}`)
                                                    .join('\n');

                                                // eslint-disable-next-line no-console
                                                console.log(`✅ [File Lines Request] Extracted lines ${actualStart}-${actualEnd} from ${eventData.file_path}${wasAdjusted ? ' (adjusted)' : ''}`);

                                                // POST file lines back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: numberedContent,
                                                        service_name: serviceName,
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: actualStart,
                                                        actual_end: actualEnd,
                                                        total_lines: totalLines,
                                                        was_adjusted: wasAdjusted,
                                                        success: true
                                                    })
                                                });

                                                // eslint-disable-next-line no-console
                                                console.log(`📤 Posted file lines back to backend for request ${eventData.request_id}`);
                                            } else {
                                                // eslint-disable-next-line no-console
                                                console.error(`❌ [File Lines Request] File not found in IndexedDB: ${eventData.file_path}`);

                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: serviceName,
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: eventData.start_line,
                                                        actual_end: eventData.end_line,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        success: false,
                                                        error: `File '${eventData.file_path}' not found in IndexedDB`
                                                    })
                                                });
                                            }
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [File Lines Request] Error processing file lines:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        file_path: eventData.file_path,
                                                        content: '',
                                                        service_name: eventData.service_name || '',
                                                        start_line: eventData.start_line,
                                                        end_line: eventData.end_line,
                                                        actual_start: eventData.start_line,
                                                        actual_end: eventData.end_line,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle file_request_multiple_lines events (AI requesting multiple line ranges from IndexedDB)
                                if (eventData.type === 'file_request_multiple_lines') {
                                    // eslint-disable-next-line no-console
                                    console.log('📂 [SSE-Main] Multiple file lines request event received:', {
                                        fileRequests: eventData.file_requests,
                                        requestCount: eventData.file_requests?.length,
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        currentCanvasProjectId: canvasProjectContext?.projectId,
                                        currentServiceName: canvasProjectContext?.selectedMicroservice?.name
                                    });

                                    // Load file lines from IndexedDB and POST back to backend
                                    (async () => {
                                        try {
                                            const projectId = canvasProjectContext?.projectId;
                                            const defaultServiceName = canvasProjectContext?.selectedMicroservice?.name;

                                            if (!projectId) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Cannot load files - missing project context');
                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: 'Missing project context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Process all file line requests
                                            const filesData = [];
                                            for (const fileReq of eventData.file_requests) {
                                                const filePath = fileReq.file_path;
                                                const startLine = fileReq.start_line;
                                                const endLine = fileReq.end_line;
                                                let serviceName = fileReq.service_name || defaultServiceName;

                                                // Fallback to selectedResource if no serviceName
                                                if (!serviceName) {
                                                    const selectedResource = loadSelectedResourceFromStorage();
                                                    if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                        serviceName = selectedResource.data.name;
                                                    }
                                                }

                                                if (!serviceName) {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: '',
                                                        start_line: startLine,
                                                        end_line: endLine,
                                                        actual_start: startLine,
                                                        actual_end: endLine,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        service_name: '',
                                                        success: false,
                                                        error: 'Missing service context'
                                                    });
                                                    continue;
                                                }

                                                // Load file from IndexedDB
                                                const fileData = await indexedDbFileStorage.getFile(
                                                    projectId,
                                                    serviceName,
                                                    filePath
                                                );

                                                if (fileData && fileData.content) {
                                                    // Extract requested line range
                                                    const allLines = fileData.content.split('\n');
                                                    const totalLines = allLines.length;

                                                    // Adjust bounds silently if out of range (1-indexed)
                                                    let actualStart = Math.max(1, startLine);
                                                    let actualEnd = Math.min(totalLines, endLine);
                                                    actualStart = Math.min(actualStart, totalLines);

                                                    const wasAdjusted = (actualStart !== startLine) || (actualEnd !== endLine);

                                                    // Extract lines (convert to 0-indexed for array)
                                                    const extractedLines = allLines.slice(actualStart - 1, actualEnd);

                                                    // Format with line numbers in diffspec style
                                                    const numberedContent = extractedLines
                                                        .map((line, idx) => `${actualStart + idx}: ${line}`)
                                                        .join('\n');

                                                    filesData.push({
                                                        path: filePath,
                                                        content: numberedContent,
                                                        start_line: startLine,
                                                        end_line: endLine,
                                                        actual_start: actualStart,
                                                        actual_end: actualEnd,
                                                        total_lines: totalLines,
                                                        was_adjusted: wasAdjusted,
                                                        service_name: serviceName,
                                                        success: true
                                                    });

                                                    // eslint-disable-next-line no-console
                                                    console.log(`✅ Extracted lines ${actualStart}-${actualEnd} from ${filePath}${wasAdjusted ? ' (adjusted)' : ''}`);
                                                } else {
                                                    filesData.push({
                                                        path: filePath,
                                                        content: '',
                                                        start_line: startLine,
                                                        end_line: endLine,
                                                        actual_start: startLine,
                                                        actual_end: endLine,
                                                        total_lines: 0,
                                                        was_adjusted: false,
                                                        service_name: serviceName,
                                                        success: false,
                                                        error: 'File not found in IndexedDB'
                                                    });
                                                    // eslint-disable-next-line no-console
                                                    console.error(`❌ File not found in IndexedDB: ${filePath}`);
                                                }
                                            }

                                            // POST all file lines back to backend
                                            await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple-lines`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    files: filesData,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            console.log(`📤 Posted ${filesData.length} file line range(s) back to backend for request ${eventData.request_id}`);
                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ Error loading file lines from IndexedDB:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-multiple-lines`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle syntax_check_request events (AI requesting Monaco syntax problems)
                                if (eventData.type === 'syntax_check_request') {
                                    // eslint-disable-next-line no-console
                                    console.log('🔍 [SSE-Main] Syntax check request received:', {
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id
                                    });

                                    (async () => {
                                        try {
                                            let projectId = canvasProjectContext?.projectId;
                                            let serviceName = canvasProjectContext?.selectedMicroservice?.name;

                                            if (!serviceName) {
                                                const selectedResource = loadSelectedResourceFromStorage();
                                                if (selectedResource?.type === 'service' && selectedResource?.data?.name) {
                                                    serviceName = selectedResource.data.name;
                                                }
                                            }

                                            // Fallback to values provided by the agent in the event (from project context)
                                            if (!projectId && eventData.project_id) {
                                                projectId = eventData.project_id;
                                                // eslint-disable-next-line no-console
                                                console.log('🔍 [Syntax Check] Using project_id from event:', projectId);
                                            }
                                            if (!serviceName && eventData.service_name) {
                                                serviceName = eventData.service_name;
                                                // eslint-disable-next-line no-console
                                                console.log('🔍 [Syntax Check] Using service_name from event:', serviceName);
                                            }

                                            if (!projectId || !serviceName) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Syntax Check] Missing project or service context');
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: 'Missing project or service context'
                                                    })
                                                });
                                                return;
                                            }

                                            // Get all modified files from IndexedDB
                                            const modifiedFiles = await indexedDbFileStorage.getAllFiles(projectId, serviceName);

                                            if (!modifiedFiles || modifiedFiles.length === 0) {
                                                // eslint-disable-next-line no-console
                                                console.log('📭 [Syntax Check] No modified files to check');
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: true
                                                    })
                                                });
                                                return;
                                            }

                                            // Map file extension to Monaco language identifier
                                            const getMonacoLanguage = (filePath) => {
                                                const ext = (filePath.split('.').pop() || '').toLowerCase();
                                                const langMap = {
                                                    ts: 'typescript', tsx: 'typescript',
                                                    js: 'javascript', jsx: 'javascript',
                                                    mjs: 'javascript', cjs: 'javascript',
                                                    py: 'python', json: 'json', jsonc: 'json',
                                                    md: 'markdown', mdx: 'markdown',
                                                    css: 'css', scss: 'scss', less: 'less',
                                                    html: 'html', xml: 'xml', svg: 'xml',
                                                    go: 'go', java: 'java', rs: 'rust', kt: 'kotlin',
                                                    sh: 'shell', bash: 'shell', yaml: 'yaml', yml: 'yaml',
                                                    toml: 'toml', graphql: 'graphql', gql: 'graphql',
                                                    cs: 'csharp', cpp: 'cpp', c: 'c', rb: 'ruby',
                                                    php: 'php', swift: 'swift', dart: 'dart'
                                                };
                                                return langMap[ext] || 'plaintext';
                                            };

                                            // Get Monaco instance (cached after initial load by @monaco-editor/react)
                                            const { loader: monacoLoader } = await import('@monaco-editor/react');
                                            const monaco = await monacoLoader.init();

                                            // Brief pause to allow any in-flight diffspec patches to be fully applied
                                            await new Promise(resolve => setTimeout(resolve, 800));

                                            // Create/update Monaco models for all modified files
                                            // Track URIs of models we create so we can clean them up later
                                            const createdModelUris = [];

                                            for (const fileData of modifiedFiles) {
                                                const filePath = fileData.path;
                                                if (!filePath) continue;
                                                const content = fileData.content || '';
                                                const language = getMonacoLanguage(filePath);
                                                // Use file:/// URI so TypeScript language service resolves paths correctly
                                                const uri = monaco.Uri.parse(`file:///${filePath}`);

                                                let model = monaco.editor.getModel(uri);
                                                if (!model) {
                                                    model = monaco.editor.createModel(content, language, uri);
                                                    createdModelUris.push(uri.toString());
                                                } else if (model.getValue() !== content) {
                                                    // Update model content to reflect latest IndexedDB state
                                                    model.setValue(content);
                                                }
                                            }

                                            // Wait for Monaco language services to compute diagnostics
                                            // TypeScript/JavaScript services need a moment to analyse all models
                                            await new Promise(resolve => setTimeout(resolve, 2500));

                                            // Collect markers (errors/warnings) for each modified file
                                            const filesWithProblems = [];

                                            for (const fileData of modifiedFiles) {
                                                const filePath = fileData.path;
                                                if (!filePath) continue;
                                                const uri = monaco.Uri.parse(`file:///${filePath}`);
                                                const model = monaco.editor.getModel(uri);
                                                if (!model) continue;

                                                const markers = monaco.editor.getModelMarkers({ resource: uri });
                                                const problems = markers
                                                    .filter(m =>
                                                        m.severity === monaco.MarkerSeverity.Error ||
                                                        m.severity === monaco.MarkerSeverity.Warning
                                                    )
                                                    .map(m => ({
                                                        line: m.startLineNumber,
                                                        column: m.startColumn,
                                                        endLine: m.endLineNumber,
                                                        endColumn: m.endColumn,
                                                        message: m.message,
                                                        severity: m.severity === monaco.MarkerSeverity.Error ? 'error' : 'warning',
                                                        code: m.code ? String(m.code) : undefined
                                                    }));

                                                if (problems.length > 0) {
                                                    filesWithProblems.push({ file: filePath, problems });
                                                }
                                            }

                                            // Dispose only the temp models we created (not existing editor models)
                                            for (const uriStr of createdModelUris) {
                                                const tempModel = monaco.editor.getModel(monaco.Uri.parse(uriStr));
                                                if (tempModel) {
                                                    tempModel.dispose();
                                                }
                                            }

                                            // eslint-disable-next-line no-console
                                            console.log(`📊 [Syntax Check] ${filesWithProblems.length} file(s) with problems out of ${modifiedFiles.length} modified`);

                                            // POST syntax problems back to backend
                                            await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    files: filesWithProblems,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            console.log(`📤 [Syntax Check] Posted results to backend for request ${eventData.request_id}`);

                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [Syntax Check] Error during syntax check:', error);

                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/canvas-files/submit-syntax-problems`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        files: [],
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Syntax Check] Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle project_context_request events (AI requesting project context)
                                if (eventData.type === 'project_context_request') {
                                    // eslint-disable-next-line no-console
                                    console.log('🌐 [SSE-Main] Project context request event received:', {
                                        requestId: eventData.request_id,
                                        conversationId: eventData.conversation_id,
                                        currentCanvasProjectId: canvasProjectContext?.projectId
                                    });

                                    // Send project context back to backend
                                    (async () => {
                                        try {
                                            // eslint-disable-next-line no-console
                                            console.log('🔄 [Project Context Request] Starting async handler');

                                            if (!canvasProjectContext) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Project Context Request] No project context available');

                                                // POST error back to backend
                                                await authorizedFetch(`${config.agentUrl}/api/project-context/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        project_context: {},
                                                        success: false,
                                                        error: 'No project context available'
                                                    })
                                                });
                                                return;
                                            }

                                            // Build the project context to send
                                            const projectContextToSend = {
                                                tenant: canvasProjectContext.tenant ? {
                                                    id: canvasProjectContext.tenant.id,
                                                    name: canvasProjectContext.tenant.name
                                                } : null,
                                                project: canvasProjectContext.project ? {
                                                    id: canvasProjectContext.project.id,
                                                    name: canvasProjectContext.project.name
                                                } : null,
                                                revision: canvasProjectContext.revision ? {
                                                    id: canvasProjectContext.revision.id,
                                                    name: canvasProjectContext.revision.name
                                                } : null,
                                                selectedMicroservice: canvasProjectContext.selectedMicroservice || null,
                                                selectedFile: canvasProjectContext.selectedFile || null,
                                                allFiles: canvasProjectContext.allFiles ?
                                                    canvasProjectContext.allFiles.map(file => ({
                                                        path: file.path,
                                                        lines: file.content ? file.content.split('\n').length : 0,
                                                        extension: file.path.split('.').pop()
                                                    })) : [],
                                                selectedResource: canvasProjectContext.selectedResource || null,
                                                configuration: canvasProjectContext.configuration || null,
                                                collections: canvasProjectContext.collections || null
                                            };

                                            // eslint-disable-next-line no-console
                                            console.log('📤 [Project Context Request] Posting project context to backend');

                                            // POST project context back to backend
                                            const postResponse = await authorizedFetch(`${config.agentUrl}/api/project-context/submit`, {
                                                method: 'POST',
                                                headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                body: JSON.stringify({
                                                    conversation_id: eventData.conversation_id,
                                                    request_id: eventData.request_id,
                                                    project_context: projectContextToSend,
                                                    success: true
                                                })
                                            });

                                            // eslint-disable-next-line no-console
                                            console.log(`✅ [Project Context Request] Posted project context, status: ${postResponse.status}`);

                                        } catch (error) {
                                            // eslint-disable-next-line no-console
                                            console.error('❌ [Project Context Request] Error sending project context:', error);

                                            // POST error back to backend
                                            try {
                                                await authorizedFetch(`${config.agentUrl}/api/project-context/submit`, {
                                                    method: 'POST',
                                                    headers: getBeHeaders({'Content-Type': 'application/json'}),
                                                    body: JSON.stringify({
                                                        conversation_id: eventData.conversation_id,
                                                        request_id: eventData.request_id,
                                                        project_context: {},
                                                        success: false,
                                                        error: error.message
                                                    })
                                                });
                                            } catch (postError) {
                                                // eslint-disable-next-line no-console
                                                console.error('❌ [Project Context Request] Failed to POST error back to backend:', postError);
                                            }
                                        }
                                    })();

                                    // Continue processing other events
                                    return;
                                }

                                // Handle usage metadata if present
                                if (eventData.usageMetadata) {
                                    const usage = eventData.usageMetadata;
                                    // eslint-disable-next-line no-console
                                    console.log('📊 [SSE-Main] Token usage metadata received:', usage);

                                    // Track token usage in analytics
                                    if (usage.promptTokenCount || usage.candidatesTokenCount) {
                                        analyticsService.trackEvent('token_usage', {
                                            tokenUsage: {
                                                inputTokens: usage.promptTokenCount || 0,
                                                outputTokens: usage.candidatesTokenCount || 0,
                                                totalTokens: usage.totalTokenCount || ((usage.promptTokenCount || 0) + (usage.candidatesTokenCount || 0))
                                            },
                                            conversationId,
                                            sessionId
                                        });
                                    }

                                    // Update token counts
                                    if (usage.promptTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Main] Updating inputTokens by', usage.promptTokenCount);
                                        setInputTokens(prev => {
                                            const newValue = prev + usage.promptTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Main] inputTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                    if (usage.candidatesTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Main] Updating outputTokens by', usage.candidatesTokenCount);
                                        setOutputTokens(prev => {
                                            const newValue = prev + usage.candidatesTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Main] outputTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                    if (usage.totalTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Main] Setting totalTokens to', usage.totalTokenCount);
                                        // FIXED: Should accumulate, not replace, to track total session usage
                                        setTotalTokens(prev => {
                                            const newValue = prev + usage.totalTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Main] totalTokens accumulated:', prev, '+', usage.totalTokenCount, '=', newValue);
                                            return newValue;
                                        });
                                    } else if (usage.promptTokenCount !== undefined && usage.candidatesTokenCount !== undefined) {
                                        // Calculate total if not provided
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Main] Calculating totalTokens from input+output');
                                        setTotalTokens(prev => {
                                            const newValue = prev + usage.promptTokenCount + usage.candidatesTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Main] totalTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                }

                                // Error frame from server -> surface and stop streaming
                                if (eventData.error) {
                                    setIsLoading(false);
                                    setConversationStatus('');

                                    // Handle rate limit errors with special formatting
                                    if (eventData.rate_limit_error) {
                                        const rateLimitMessage = Messages.createInfoMessage(
                                            `⏱️ ${eventData.error}\n\n${eventData.details || ''}\n\n${eventData.suggestion || 'Please wait 1 minute before trying again.'}`
                                        );
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, rateLimitMessage));
                                    }
                                    // Handle authentication errors differently
                                    else if (eventData.auth_required || eventData.error.toLowerCase().includes('authentication')) {
                                        const authMessage = Messages.createInfoMessage(
                                            `🔐 ${eventData.error}\n\n${eventData.details || ''}\n\n${eventData.suggestion || 'Please authenticate to access all features.'}`
                                        );
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, authMessage));
                                    } else {
                                        const errorMessage = Messages.createErrorMessage(eventData.error);
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                                    }

                                    stopProcessingEvents = true; // halt further SSE processing
                                    return;
                                }

                                // Content frame: may include parts for tool calls/results and/or text
                                if (eventData.content && eventData.content.parts && eventData.content.parts.length > 0) {
                                    for (const part of eventData.content.parts) {
                                        if (part.functionCall) {
                                            // Update status to show we're calling a tool
                                            const toolName = part.functionCall.name || 'Tool';
                                            setConversationStatus(`Calling ${toolName}`);

                                            // Create intermediate message if not created yet
                                            if (!intermediateMessageCreated) {
                                                intermediateMessageCreated = true;
                                                setMessages(prevMessages => {
                                                    const intermediateMessage = {
                                                        id: Date.now() + Math.random(),
                                                        text: '',
                                                        type: 'received',
                                                        isIntermediate: true,
                                                        reasoningSteps: [{
                                                            title: 'Understanding Your Request',
                                                            description: 'Analyzing your query to determine the best approach',
                                                            reasoning: 'Processing natural language input and identifying intent, entities, and required actions to provide an accurate response.',
                                                            startTime: Date.now()
                                                        }],
                                                        currentReasoningStep: 0
                                                    };
                                                    return Messages.addMessage(prevMessages, intermediateMessage);
                                                });
                                            }

                                            // Add reasoning step for function call with detailed information
                                            setMessages(prevMessages => {
                                                const args = part.functionCall.args || {};
                                                let description = `Executing ${toolName}`;
                                                let title = `Using ${toolName}`;
                                                if (toolName === 'curl_request') {
                                                    const method = (args.method || 'GET').toUpperCase();
                                                    const url = args.url || '';
                                                    try {
                                                        const parsed = new URL(url);
                                                        title = `Using ${toolName}: ${method} ${parsed.pathname}`;
                                                    } catch (_) {
                                                        title = `Using ${toolName}: ${method} ${url}`;
                                                    }
                                                    description = `${method} ${url}`;
                                                } else if (Object.keys(args).length > 0) {
                                                    const firstKey = Object.keys(args)[0];
                                                    const firstVal = String(args[firstKey]).substring(0, 80);
                                                    title = `Using ${toolName} — ${firstKey}: ${firstVal}`;
                                                    description = `${firstKey}: ${firstVal}`;
                                                }

                                                return Messages.addReasoningStep(prevMessages, {
                                                    title: title,
                                                    description: description,
                                                    reasoning: `The model determined that ${toolName} is the appropriate tool to handle this part of your request.`,
                                                    functionCall: {
                                                        name: part.functionCall.name,
                                                        args: part.functionCall.args || {}
                                                    }
                                                });
                                            });
                                        }

                                        if (part.functionResponse) {
                                            // Update status to show we're processing the tool response
                                            setConversationStatus('Processing Response');

                                            // Check if this is the start_browser tool - extract sessionId for browser viewer
                                            if (part.functionResponse.name === 'start_browser') {
                                                try {
                                                    let browserSessionId = null;
                                                    let browserQuality = 'medium';

                                                    // Parse the response to extract sessionId
                                                    const response = part.functionResponse.response;
                                                    // eslint-disable-next-line no-console
                                                    console.log('🔍 start_browser response structure:', response);

                                                    let result = null;

                                                    // Try multiple ways to extract the result
                                                    if (response && response.result) {
                                                        // Case 1: response.result exists
                                                        result = typeof response.result === 'string'
                                                            ? JSON.parse(response.result)
                                                            : response.result;
                                                    } else if (typeof response === 'string') {
                                                        // Case 2: response itself is a JSON string
                                                        result = JSON.parse(response);
                                                    } else if (response && typeof response === 'object') {
                                                        // Case 3: response is already the result object
                                                        result = response;
                                                    }

                                                    // eslint-disable-next-line no-console
                                                    console.log('🔍 Parsed result:', result);

                                                    if (result && result.success && result.sessionId) {
                                                        browserSessionId = result.sessionId;
                                                        browserQuality = result.quality || 'medium';

                                                        // eslint-disable-next-line no-console
                                                        console.log('🌐 Browser session detected:', browserSessionId, 'quality:', browserQuality);

                                                        // Store for attachment to message when it's created/updated
                                                        pendingBrowserSession = {
                                                            browserSessionId,
                                                            browserQuality
                                                        };
                                                        // eslint-disable-next-line no-console
                                                        console.log('✅ Stored pending browser session, will attach to message');
                                                    } else {
                                                        // eslint-disable-next-line no-console
                                                        console.warn('⚠️ Could not extract sessionId from result:', result);
                                                    }
                                                } catch (error) {
                                                    // eslint-disable-next-line no-console
                                                    console.error('❌ Error parsing browser session from functionResponse:', error);
                                                }
                                            }

                            // Check if this is a project-modifying MCP tool that requires canvas refresh
                            const shouldRefreshCanvas = isCanvasMode && canvasProjectContext && part.functionResponse.name;
                            const projectModifyingTools = [
                                'configuration_save',
                                'create_project_from_template',
                                'create_service_from_marketplace',
                                'create_collection',
                                'create_endpoints',
                                'deploy_project'
                            ];

                            const isProjectModifyingTool = projectModifyingTools.some(tool =>
                                part.functionResponse.name && part.functionResponse.name.includes(tool)
                            );                                            // Check if the response indicates success
                                            let isSuccessful = false;
                                            if (part.functionResponse.response) {
                                                const response = part.functionResponse.response;
                                                if (typeof response === 'string') {
                                                    // Check for explicit error/failure keywords
                                                    const hasError = response.toLowerCase().includes('error') ||
                                                                   response.toLowerCase().includes('failed') ||
                                                                   response.toLowerCase().includes('failure');
                                                    // Consider success if no explicit error (more lenient)
                                                    isSuccessful = !hasError;
                                                } else if (typeof response === 'object') {
                                                    // Check for success indicators or absence of error
                                                    isSuccessful = response.success !== false && !response.error;
                                                }
                                            } else {
                                                // If no response, assume success (tool executed without errors)
                                                isSuccessful = true;
                                            }

                                            // eslint-disable-next-line no-console
                                            console.log('🔍 Canvas refresh check:', {
                                                tool: part.functionResponse.name,
                                                isProjectModifying: isProjectModifyingTool,
                                                isSuccessful,
                                                shouldRefresh: shouldRefreshCanvas && isProjectModifyingTool && isSuccessful
                                            });

                                            // Trigger canvas refresh if it's a successful project-modifying operation
                                            if (shouldRefreshCanvas && isProjectModifyingTool && isSuccessful) {
                                                // Delay refresh slightly to allow backend to process
                                                setTimeout(() => {
                                                    if (canvasRef.current && canvasRef.current.handleRefresh) {
                                                        // eslint-disable-next-line no-console
                                                        console.log('🔄 Auto-refreshing canvas after MCP operation:', part.functionResponse.name);
                                                        canvasRef.current.handleRefresh();
                                                    }
                                                }, 1000);
                                            }

                                            // Dispatch event for service creation to trigger auto-pull
                                            if (part.functionResponse.name === 'create_service_from_marketplace' && isSuccessful) {
                                                // Extract project ID and service name from the response
                                                const response = part.functionResponse.response;
                                                let projectId = canvasProjectContext?.projectId;
                                                let serviceName = null;

                                                if (typeof response === 'string') {
                                                    try {
                                                        const parsed = JSON.parse(response);
                                                        serviceName = parsed.name || parsed.serviceName;
                                                    } catch (e) {
                                                        // Try to extract from text
                                                        const nameMatch = response.match(/name["\s:]+([a-z0-9-]+)/i);
                                                        if (nameMatch) serviceName = nameMatch[1];
                                                    }
                                                } else if (typeof response === 'object') {
                                                    serviceName = response.name || response.serviceName;
                                                }

                                                if (projectId && serviceName) {
                                                    // eslint-disable-next-line no-console
                                                    console.log(`📢 Dispatching mcpServiceCreated event for ${serviceName}`);
                                                    window.dispatchEvent(new CustomEvent('mcpServiceCreated', {
                                                        detail: {
                                                            projectId,
                                                            serviceName,
                                                            toolName: 'create_service_from_marketplace'
                                                        }
                                                    }));
                                                }
                                            }

                                            // Check if this is a canvas file request - mark for auto-response
                                            // Don't interrupt the stream, but prepare to auto-send files after completion
                                            if (pendingInterceptions.length > 0) {
                                                (async () => {
                                                    try {
                                                        // Wait for interceptions to complete (they fetch files from IndexedDB)
                                                        await Promise.all(pendingInterceptions);
                                                        pendingInterceptions.length = 0;

                                                        // Store the file content for auto-send after stream completes
                                                        const fileContent = part.functionResponse.response;

                                                        // eslint-disable-next-line no-console
                                                        console.log('📂 File request detected - will auto-send files after AI completes');

                                                        // Set a flag in the window object to trigger auto-send
                                                        window._pendingFileContent = fileContent;
                                                        window._pendingFileSessionId = sessionId;

                                                    } catch (error) {
                                                        // eslint-disable-next-line no-console
                                                        console.error('❌ Error preparing file content:', error);
                                                    }
                                                })();
                                            }

                                            // Check for MCP App metadata in function response
                                            if (part.functionResponse.response && typeof part.functionResponse.response === 'object') {
                                                const response = part.functionResponse.response;
                                                if (response._meta && response._meta.ui && response._meta.ui.resourceUri) {
                                                    // eslint-disable-next-line no-console
                                                    console.log('🎨 MCP App metadata detected:', response._meta.ui);

                                                    // Store MCP App metadata to attach to the message
                                                    if (!window._mcpAppMetadata) {
                                                        window._mcpAppMetadata = {};
                                                    }
                                                    window._mcpAppMetadata[sessionId] = {
                                                        resourceUri: response._meta.ui.resourceUri,
                                                        toolInput: part.functionCall || {},
                                                        toolResult: response,
                                                        hostContext: {
                                                            theme: document.documentElement.getAttribute('data-theme') || 'light',
                                                            locale: 'en'
                                                        }
                                                    };
                                                }
                                            }

                                            // Update reasoning step with result
                                            setMessages(prevMessages => {
                                                // Extract result summary
                                                let resultSummary = 'Tool executed successfully';
                                                if (part.functionResponse.response) {
                                                    const response = part.functionResponse.response;
                                                    if (typeof response === 'string') {
                                                        resultSummary = response.length > 100 ?
                                                            response.substring(0, 100) + '...' :
                                                            response;
                                                    } else if (typeof response === 'object') {
                                                        resultSummary = 'Received structured data response';
                                                    }
                                                }

                                                return Messages.updateCurrentReasoningStep(prevMessages, {
                                                    result: part.functionResponse.response || 'Success',
                                                    reasoning: `Tool completed. ${resultSummary}. Now synthesizing this information into the final response.`
                                                });
                                            });
                                        }

                                        let currentAccumulatedText = '';
                                        if (part.text) {
                            let textToProcess = part.text;
                                            if (!intermediateMessageCreated) {
                                                intermediateMessageCreated = true;
                                                setMessages(prevMessages => {
                                                    const intermediateMessage = {
                                                        id: Date.now() + Math.random(),
                                                        text: '',
                                                        type: 'received',
                                                        isIntermediate: true,
                                                        isStreaming: true,
                                                        reasoningSteps: [{
                                                            title: 'Understanding Your Request',
                                                            description: 'Analyzing your query to provide a response',
                                                            reasoning: 'Processing your request and preparing the response.',
                                                            startTime: Date.now()
                                                        }],
                                                        currentReasoningStep: 0,
                                                        // Attach browser session if it was detected from start_browser tool
                                                        ...(pendingBrowserSession || {})
                                                    };
                                                    // eslint-disable-next-line no-console
                                                    if (pendingBrowserSession) {
                                                        console.log('✅ Created intermediate message with browser session:', pendingBrowserSession);
                                                    }
                                                    return Messages.addMessage(prevMessages, intermediateMessage);
                                                });
                                            }

                                            // Update status to show we're generating response
                                            setConversationStatus('Generating Response');

                                            // Check if this is explicitly a final (non-partial) frame
                                            // Default to partial=true (streaming) if not specified
                                            const isPartial = eventData.partial !== false;

                                            // Accumulation strategy:
                                            // - partial=true events: always accumulate (streaming chunks).
                                            // - partial=false after partial=true: ADK/Claude style — the final
                                            //   event contains the COMPLETE text, not just the last token.
                                            //   We already have the full text from the streaming chunks, so
                                            //   skip the final event's text to avoid the response doubling.
                                            // - partial=false with no prior partial=true: Qwen/Ollama style —
                                            //   per-token partial=false events, so accumulate normally.
                                            if (isPartial) {
                                                hasSeenPartialTrue = true;
                                                accumulatedText += part.text;
                                            } else if (!hasSeenPartialTrue) {
                                                // Qwen/Ollama per-token mode: accumulate normally
                                                accumulatedText += part.text;
                                            }
                                            // else: ADK final event after streaming — skip to avoid doubling
                                            currentAccumulatedText = accumulatedText;

                                            if (!isPartial) {
                                                // Clear status when a final frame arrives.
                                                setConversationStatus('');
                                            }

                                        // Avoid UI updates during incomplete markdown structures to prevent poor rendering
                                        // Skip updates if we're inside an incomplete mermaid block or list item
                                        // BUT always update if this is the final frame (isPartial=false)
                                        const isIncompleteStructure = Messages.isInsideIncompleteMermaidBlock(currentAccumulatedText) ||
                                                                Messages.isInsideIncompleteListItem(currentAccumulatedText);

                                        const shouldSkipUpdate = isPartial && isIncompleteStructure;

                                        if (!shouldSkipUpdate) {
                                            setMessages(prevMessages => {
                                                // Always keep isIntermediate=true while the stream is active.
                                                // The message is finalised (isIntermediate→false, isStreaming→false)
                                                // only by the post-loop finalizer block.
                                                const updated = Messages.updateOrCreateIntermediateMessage(prevMessages, currentAccumulatedText, true);

                                                // Attach pending browser session if available (only once)
                                                if (pendingBrowserSession) {
                                                    // eslint-disable-next-line no-console
                                                    console.log('✅ Attaching browser session to message:', pendingBrowserSession);

                                                    const result = updated.map(msg => {
                                                        if (msg.isIntermediate || (!isPartial && msg.id === updated[updated.length - 1].id)) {
                                                            return {
                                                                ...msg,
                                                                ...pendingBrowserSession
                                                            };
                                                        }
                                                        return msg;
                                                    });

                                                    // Clear pending session after attaching to prevent re-attachment on every render
                                                    pendingBrowserSession = null;

                                                    return result;
                                                }

                                                // Attach MCP App metadata if available (when response is final)
                                                if (!isPartial && window._mcpAppMetadata && window._mcpAppMetadata[sessionId]) {
                                                    const mcpAppData = window._mcpAppMetadata[sessionId];
                                                    // eslint-disable-next-line no-console
                                                    console.log('✅ Attaching MCP App data to message:', mcpAppData);

                                                    return updated.map(msg => {
                                                        if (msg.isIntermediate) {
                                                            return {
                                                                ...msg,
                                                                mcpApp: mcpAppData
                                                            };
                                                        }
                                                        return msg;
                                                    });
                                                }

                                                return updated;
                                            });
                                        }

                                        // If canvas is open and this is the final frame, check for code updates
                                        if (isCanvasMode && !isPartial) {
                                            const codeUpdates = extractAllCodeUpdates(
                                                currentAccumulatedText,
                                                canvasProjectContext?.selectedFile?.path || null
                                            );

                                            if (codeUpdates && codeUpdates.length > 0) {
                                                // Collect modified files for summary display
                                                const modifiedFiles = [];

                                                // Process each code update
                                                for (const codeUpdate of codeUpdates) {
                                                    const eventDetail = {
                                                        filePath: codeUpdate.filePath,
                                                        projectId: canvasProjectContext?.projectId,
                                                        serviceName: canvasProjectContext?.selectedMicroservice?.name,
                                                        isStreaming: false
                                                    };

                                                        // Track modified file
                                                        modifiedFiles.push({
                                                            path: codeUpdate.filePath,
                                                            operation: codeUpdate.updateType || 'modified',
                                                            projectId: canvasProjectContext?.projectId,
                                                            serviceName: canvasProjectContext?.selectedMicroservice?.name
                                                        });

                                                        // Add appropriate fields based on update type
                                                        if (codeUpdate.isDiff && codeUpdate.updateType === 'diff') {
                                                            // Unified diff format - use custom diff provider
                                                            eventDetail.updateType = 'diff';
                                                            eventDetail.diffContent = codeUpdate.diffContent;
                                                            eventDetail.content = codeUpdate.diffContent;
                                                        } else if (codeUpdate.isPartialUpdate && codeUpdate.updateType === 'replace') {
                                                            // Partial replacement
                                                            eventDetail.updateType = 'replace';
                                                            eventDetail.oldString = codeUpdate.oldString;
                                                            eventDetail.newString = codeUpdate.newString;
                                                        } else {
                                                            // Full content replacement
                                                            eventDetail.updateType = 'full';
                                                            eventDetail.content = codeUpdate.content;
                                                        }

                                                        // Use setTimeout to ensure event is dispatched after current call stack
                                                        setTimeout(() => {
                                                            window.dispatchEvent(new CustomEvent('aiContentUpdate', {
                                                                detail: eventDetail,
                                                                bubbles: true
                                                            }));

                                                            // eslint-disable-next-line no-console
                                                            console.log(`✅ AI code ${codeUpdate.isDiff ? 'diff' : codeUpdate.isPartialUpdate ? 'partial' : 'full'} update dispatched:`, codeUpdate.filePath, {
                                                                updateType: eventDetail.updateType,
                                                                projectId: eventDetail.projectId,
                                                                serviceName: eventDetail.serviceName,
                                                                diffLength: eventDetail.diffContent?.length || 0
                                                            });
                                                        }, 0);
                                                    }

                                                    // Dispatch final event with all modified files for the loader to display
                                                    // Increased delay to ensure all diffs have time to process
                                                    if (modifiedFiles.length > 0) {
                                                        // eslint-disable-next-line no-console
                                                        console.log(`📊 Waiting for ${modifiedFiles.length} file(s) to process...`);

                                                        setTimeout(() => {
                                                            window.dispatchEvent(new CustomEvent('diffParserComplete', {
                                                                detail: {
                                                                    files: modifiedFiles,
                                                                    count: modifiedFiles.length
                                                                },
                                                                bubbles: true
                                                            }));

                                                            // eslint-disable-next-line no-console
                                                            console.log(`✅ All ${modifiedFiles.length} file update(s) dispatched and processing complete`);
                                                        }, 1000); // Increased delay to ensure all async diff applications complete
                                                    }

                                                    // Monaco editor will handle localStorage save via its event listener
                                                }

                                                // DON'T update canvas content when code updates were extracted
                                                // The files will be opened individually by MonacoFileEditor/RepositoryTree
                                                // Showing the full AI response in Monaco would be confusing

                                                // Only update canvas if NO code updates were found (pure documentation response)
                                                if (!codeUpdates || codeUpdates.length === 0) {
                                                    const hasCode = /```[\s\S]+```/g.test(currentAccumulatedText) ||
                                                                   /function\s+\w+\s*\(/g.test(currentAccumulatedText) ||
                                                                   /<\/?\w+[^>]*>/g.test(currentAccumulatedText);

                                                    setCanvasContent({
                                                        type: hasCode ? 'code' : 'document',
                                                        content: currentAccumulatedText,
                                                        title: 'Generated Content'
                                                    });
                                                }
                                            }
                                        }
                                    }
                                }
                            } catch (parseError) {
                                // Malformed SSE frame -> stop streaming and inform the user
                                if (event.data.trim()) {
                                    // Try to extract meaningful error information from the event data
                                    let errorMessage = 'Unable to process server response';
                                    let errorDetails = '';
                                    let isRateLimitError = false;

                                    try {
                                        const rawData = event.data.trim();

                                        // Log the full raw data for debugging (limit to 2000 chars)
                                        // eslint-disable-next-line no-console
                                        console.error('📋 Full error data:', rawData.substring(0, 2000));

                                        // Check if it's a rate limit error from the backend
                                        if (rawData.includes('RateLimitError') || rawData.includes('RESOURCE_EXHAUSTED') || rawData.includes('429')) {
                                            isRateLimitError = true;
                                            errorMessage = '⏱️ Rate Limit Exceeded';
                                            errorDetails = 'The AI service is currently experiencing high demand.';

                                            // Try to extract specific error message
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/)
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1];
                                            }
                                        }
                                        // Check for Vertex AI / model not found errors
                                        else if (rawData.includes('NOT_FOUND') || rawData.includes('404') || rawData.includes('not found')) {
                                            errorMessage = '🚫 Model Configuration Error';

                                            // Try to extract the full error message
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/)
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1];
                                            } else {
                                                // Show more of the raw error for 404s
                                                errorDetails = rawData.substring(0, 1000);
                                            }

                                            // Add helpful context
                                            if (!errorDetails.includes('fix')) {
                                                errorDetails += '\n\nThe AI model specified in the backend configuration may not be available. Please check the backend logs for details.';
                                            }
                                        }
                                        // Check for authentication errors
                                        else if (rawData.includes('AuthenticationError') || rawData.includes('auth') || rawData.includes('401') || rawData.includes('403')) {
                                            errorMessage = '🔐 Authentication Error';
                                            errorDetails = 'Authentication is required. Please check your credentials and try again.';

                                            // Try to extract more details
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/)
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1];
                                            }
                                        }
                                        // Check for other error types
                                        else if (rawData.includes('Error') || rawData.includes('Exception')) {
                                            // Try to extract error class name
                                            const errorTypeMatch = rawData.match(/(\w+Error|\w+Exception)/);
                                            if (errorTypeMatch && errorTypeMatch[1]) {
                                                errorMessage = `${errorTypeMatch[1].replace(/([A-Z])/g, ' $1').trim()}`;
                                            }

                                            // Try to extract error message - be more aggressive
                                            const messageMatch = rawData.match(/"message":\s*"([^"]+)"/) ||
                                                                rawData.match(/message:\s*([^,}\n]+)/);
                                            if (messageMatch && messageMatch[1]) {
                                                errorDetails = messageMatch[1].trim();
                                            }

                                            // If still no details, try to extract from error object
                                            if (!errorDetails) {
                                                const errorObjMatch = rawData.match(/"error":\s*\{([^}]+)\}/);
                                                if (errorObjMatch && errorObjMatch[1]) {
                                                    errorDetails = errorObjMatch[1].replace(/"/g, '').trim();
                                                }
                                            }
                                        }

                                        // If we couldn't parse anything useful, show more of the raw data
                                        if (!errorDetails && rawData.length < 1000) {
                                            errorDetails = rawData;
                                        } else if (!errorDetails) {
                                            errorDetails = rawData.substring(0, 1000) + '...';
                                        }
                                    } catch (e) {
                                        // If all parsing fails, stick with generic message
                                        // eslint-disable-next-line no-console
                                        console.error('Error parsing backend error:', e);
                                    }

                                    // If it's a rate limit error, trigger retry
                                    if (isRateLimitError) {
                                        // eslint-disable-next-line no-console
                                        console.log('🔄 Rate limit error detected - triggering retry');
                                        stopProcessingEvents = true;
                                        // Store error info for retry handler
                                        parseError.isRateLimitError = true;
                                        parseError.errorMessage = errorMessage;
                                        parseError.errorDetails = errorDetails;
                                        throw parseError; // Re-throw to be caught by outer retry logic
                                    }

                                    // For non-rate-limit errors, show the error and stop
                                    setIsLoading(false);
                                    setConversationStatus('');

                                    const displayMessage = errorDetails ?
                                        `${errorMessage}\n\n${errorDetails}` :
                                        errorMessage;

                                    const finalErrorMessage = Messages.createErrorMessage(displayMessage);
                                    setMessages(prevMessages => Messages.addMessage(prevMessages, finalErrorMessage));
                                    stopProcessingEvents = true; // halt further SSE processing
                                    return;
                                }
                            }
                        },
                        onError: (error) => {
                            setIsLoading(false);
                            setConversationStatus('');
                            const errorMessage = Messages.createErrorMessage(`Stream parsing error: ${error.message}`);
                            setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                            stopProcessingEvents = true; // halt further SSE processing
                        }
                    });

                    do {
                        const {done, value} = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, {stream: true});
                        parser.feed(chunk);
                    } while (!stopProcessingEvents);

                    // Finalise the last streaming message: clear isStreaming/isIntermediate flags
                    // so the bubble is no longer treated as in-progress.
                    if (intermediateMessageCreated) {
                        setMessages(prevMessages => {
                            const last = prevMessages[prevMessages.length - 1];
                            if (last && last.type === 'received' && (last.isStreaming || last.isIntermediate)) {
                                return prevMessages.slice(0, -1).concat([{
                                    ...last,
                                    isIntermediate: false,
                                    isStreaming: false
                                }]);
                            }
                            return prevMessages;
                        });
                    }
                } catch (streamError) {
                    // Handle stream reading errors (e.g., ERR_INCOMPLETE_CHUNKED_ENCODING)
                    setIsLoading(false);
                    setAbortController(null);
                    setConversationStatus('');

                    // Check if this was an abort (user stopped generation)
                    if (streamError.name === 'AbortError') {
                        const stoppedMessage = Messages.createInfoMessage('Response generation stopped. Feel free to ask another question.');
                        setMessages(prevMessages => Messages.addMessage(prevMessages, stoppedMessage));
                        return;
                    }

                    // Log full error details for debugging
                    // eslint-disable-next-line no-console
                    console.error('📋 Stream error details:', {
                        name: streamError.name,
                        message: streamError.message,
                        stack: streamError.stack?.substring(0, 500)
                    });

                    let errorMessage = 'Stream reading error: Connection to server was interrupted';

                    // Try to extract more meaningful error information
                    if (streamError.message) {
                        // Check for specific error patterns
                        if (streamError.message.includes('JSON') || streamError.message.includes('parse')) {
                            errorMessage = 'Error parsing server response';

                            // If there's a JSON parsing error, it might indicate a backend error was sent
                            if (streamError.message.includes('position')) {
                                errorMessage += '. The server may have sent an error message.';
                            }
                        } else if (streamError.message.includes('network') || streamError.message.includes('fetch')) {
                            errorMessage = 'Network error: Unable to connect to server';
                        } else if (streamError.message.includes('timeout')) {
                            errorMessage = 'Request timeout: Server took too long to respond';
                        }

                        // Append the original error message for context (limit length)
                        const errorDetail = streamError.message.length > 500
                            ? streamError.message.substring(0, 500) + '...'
                            : streamError.message;
                        errorMessage += ` (${errorDetail})`;
                    }

                    // If we've already accumulated some text, it means the agent was working
                    if (accumulatedText) {
                        errorMessage = 'Server connection interrupted. The response may be incomplete.';
                    }

                    const errorMsg = Messages.createErrorMessage(errorMessage);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMsg));
                } finally {
                    reader.releaseLock();
                }

                // Success - end loading state and exit retry loop
                setIsLoading(false);
                setConversationStatus('');
                setAbortController(null);

                // Check if we should ask about Mia components setup after this response
                const shouldAskAboutComponents = sessionStorage.getItem('mia_ask_about_components_next');
                if (shouldAskAboutComponents === 'true') {
                    sessionStorage.removeItem('mia_ask_about_components_next');

                    // Wait a moment, then append a follow-up message about Mia components
                    setTimeout(() => {
                        setMessages(prev => [
                            ...prev,
                            {
                                id: `system-${Date.now()}`,
                                role: 'assistant',
                                content: '\n\n---\n\n**🎯 Next Steps: Mia Platform Components**\n\nYour frontend is ready! Would you like me to set up additional Mia Platform components for your project?\n\n- 🌐 **API Gateway** (e.g., Envoy) - Route and manage API traffic\n- 📊 **CRUD Service** - Create database collections and REST APIs\n- 🔐 **Authentication** - Add user authentication and authorization\n- 📈 **Monitoring** - Set up logging and metrics\n- 💾 **Database** - Configure MongoDB or other data stores\n\nJust let me know what components you need, or say "skip" to continue coding!',
                                timestamp: new Date().toISOString(),
                                text: '\n\n---\n\n**🎯 Next Steps: Mia Platform Components**\n\nYour frontend is ready! Would you like me to set up additional Mia Platform components for your project?\n\n- 🌐 **API Gateway** (e.g., Envoy) - Route and manage API traffic\n- 📊 **CRUD Service** - Create database collections and REST APIs\n- 🔐 **Authentication** - Add user authentication and authorization\n- 📈 **Monitoring** - Set up logging and metrics\n- 💾 **Database** - Configure MongoDB or other data stores\n\nJust let me know what components you need, or say "skip" to continue coding!',
                                type: 'received'
                            }
                        ]);
                    }, 800);
                }

                // Check if there's pending file content to auto-send
                if (window._pendingFileContent && window._pendingFileSessionId === sessionId) {
                    const fileContent = window._pendingFileContent;
                    delete window._pendingFileContent;
                    delete window._pendingFileSessionId;

                    // eslint-disable-next-line no-console
                    console.log('📤 Auto-sending file content to AI...');

                    // Brief delay to let UI update
                    setTimeout(async () => {
                        // Ensure fileContent is a string
                        const contentStr = typeof fileContent === 'string' ? fileContent : JSON.stringify(fileContent);

                        // Add user message showing we're sending files
                        const userMessage = Messages.createUserMessage(contentStr);
                        setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));

                        // Re-trigger send with file content
                        setInputValue(contentStr);
                        setTimeout(() => {
                            // Manually trigger send
                            document.querySelector('.chat-input-submit')?.click();
                        }, 50);
                    }, 500);
                }

                break; // Exit retry loop on success

            } catch (error) {
                // Check if this is a rate limit error that should be retried
                const isRateLimitError = error.isRateLimitError ||
                    (error.message && (error.message.includes('RateLimitError') ||
                     error.message.includes('RESOURCE_EXHAUSTED') ||
                     error.message.includes('429')));

                const isLastAttempt = attempt === maxRetries - 1;

                if (isRateLimitError && !isLastAttempt) {
                    // Calculate exponential backoff delay: 2^(attempt+1) seconds
                    const delaySeconds = Math.pow(2, attempt + 1);

                    // eslint-disable-next-line no-console
                    console.log(`⏳ Rate limit error - retrying in ${delaySeconds}s (attempt ${attempt + 2}/${maxRetries})...`);

                    setConversationStatus(`⏱️ Rate limit reached. Retrying in ${delaySeconds} seconds (attempt ${attempt + 2}/${maxRetries})...`);

                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, delaySeconds * 1000));

                    // Reset status for next attempt
                    setConversationStatus('Retrying request...');

                    // Continue to next iteration of retry loop
                    continue;
                }

                // Non-retriable error or max retries exceeded
                setIsLoading(false);
                setAbortController(null);
                setConversationStatus('');

                // Check if this was an abort (user stopped generation)
                if (error.name === 'AbortError') {
                    return; // Don't show error message, we already handled it
                }

                // Show appropriate error message
                let errorMessage = error.message;
                if (isRateLimitError && isLastAttempt) {
                    errorMessage = '⏱️ Rate limit error persists after multiple retries. Please try again in a few minutes.';
                }

                const errorMsg = Messages.createErrorMessage(errorMessage);
                setMessages(prevMessages => Messages.addMessage(prevMessages, errorMsg));
                break; // Exit retry loop on error
            }
            } // End retry loop
        }
    }, [inputValue, setMessages, setIsLoading, sessionId, canvasProjectContext, setCanvasContent, extractCodeUpdate, filesModifiedInCanvas, buildFileContextWithFreshContent, extractAllCodeUpdates, sessionFilesSent, attachedFiles]);

    /**
     * Adds the current input to the message queue to be sent after the active generation completes.
     * Keyboard shortcut: Alt+Enter (⌥Enter)
     */
    const handleAddToQueue = useCallback(() => {
        if (typeof inputValue === 'string' && inputValue.trim()) {
            setMessageQueue(prev => [...prev, { inputValue: inputValue.trim(), attachedFiles: [...attachedFiles] }]);
            setInputValue('');
            setAttachedFiles([]);
            setShowSendOptions(false);
        }
    }, [inputValue, attachedFiles]);

    /**
     * Stops the current generation and immediately sends the typed message.
     */
    const handleStopAndSend = useCallback(() => {
        setShowSendOptions(false);
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setIsLoading(false);
            setConversationStatus('');
        }
        // Small delay to let state settle before sending
        setTimeout(() => {
            handleSendMessage();
        }, 50);
    }, [abortController, handleSendMessage]);

    /**
     * Sends the typed message as a steering input while generation continues.
     * When not generating this is equivalent to a regular send.
     * Keyboard shortcut: Enter
     */
    const handleSteerWithMessage = useCallback(() => {
        setShowSendOptions(false);
        handleSendMessage();
    }, [handleSendMessage]);

    // Process message queue: when generation stops, send the next queued message
    useEffect(() => {
        if (!isLoading && messageQueue.length > 0) {
            const [next, ...rest] = messageQueue;
            setMessageQueue(rest);
            setInputValue(next.inputValue);
            setAttachedFiles(next.attachedFiles);
            // Use a timeout so the state updates propagate before sending
            setTimeout(() => {
                handleSendMessage();
            }, 100);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    // Effect to handle AI message requests from Monaco editor error tooltips
    useEffect(() => {
        const handleAIMessageRequest = (event) => {
            console.log('📨 Received sendAIMessage event:', event.detail);
            const { message, context } = event.detail;

            if (message) {
                console.log('✍️ Setting input value:', message);
                // Set the input value with the AI request
                setInputValue(message);

                // Trigger send after state update
                const sendTimeout = setTimeout(() => {
                    console.log('🚀 Calling handleSendMessage...');
                    // Create a synthetic form submission event
                    const syntheticEvent = {
                        preventDefault: () => {},
                        stopPropagation: () => {}
                    };
                    handleSendMessage(syntheticEvent);
                }, 100);

                return () => clearTimeout(sendTimeout);
            } else {
                console.log('⚠️ No message in sendAIMessage event');
            }
        };

        window.addEventListener('sendAIMessage', handleAIMessageRequest);

        return () => {
            window.removeEventListener('sendAIMessage', handleAIMessageRequest);
        };
    }, [handleSendMessage, setInputValue]);

    // Handle file selection
    const handleFileSelect = useCallback(async (event) => {
        const files = Array.from(event.target.files);
        const validFiles = [];

        // Text/code extensions always treated as plain text
        const TEXT_EXTENSIONS = new Set([
            'txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'yaml', 'yml',
            'toml', 'ini', 'env', 'xml', 'html', 'htm', 'css', 'scss', 'less',
            'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs',
            'py', 'rb', 'php', 'java', 'kt', 'swift', 'go', 'rs', 'c', 'cpp',
            'h', 'hpp', 'cs', 'sh', 'bash', 'zsh', 'fish', 'ps1', 'bat', 'cmd',
            'sql', 'graphql', 'gql', 'proto', 'tf', 'tfvars', 'dockerfile',
            'gitignore', 'editorconfig', 'prettierrc', 'eslintrc', 'babelrc',
            'log', 'diff', 'patch',
        ]);

        for (const file of files) {
            const ext = file.name.split('.').pop()?.toLowerCase() || '';
            // Check file type (images, PDFs, and text/code files)
                    const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            const OFFICE_EXTENSIONS = new Set(['docx', 'xlsx', 'pptx']);
            const isOffice = OFFICE_EXTENSIONS.has(ext);
            const isText = !isImage && !isPDF && !isOffice && (
                file.type.startsWith('text/') ||
                TEXT_EXTENSIONS.has(ext) ||
                file.type === 'application/json' ||
                file.type === 'application/xml' ||
                file.type === 'application/javascript' ||
                file.type === 'application/typescript'
            );

            if (!isImage && !isPDF && !isOffice && !isText) {
                showToast(`File "${file.name}" is not supported. Supported: images, PDFs, Office docs (.docx/.xlsx/.pptx), and text/code files.`, 'error');
                continue;
            }

            // Normalize MIME type: handle all JPEG variations (browser inconsistency)
            let normalizedType = file.type.toLowerCase() || 'text/plain';
            const jpegVariations = ['image/jpg', 'image/jpe', 'image/jfif'];
            if (jpegVariations.includes(normalizedType)) {
                normalizedType = 'image/jpeg';
            }
            // Treat unknown text-like types as plain text
            if (isText && !normalizedType) {
                normalizedType = 'text/plain';
            }

            // Validate image MIME types (only formats supported by Claude)
            const supportedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (isImage && !supportedImageTypes.includes(normalizedType)) {
                showToast(`Image format "${normalizedType}" is not supported. Please use JPEG, PNG, GIF, or WebP.`, 'error');
                continue;
            }

            // PDFs are OK - backend middleware will extract text from them
            // Text/code files are sent as-is to the AI

            // Check file size (max 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                showToast(`File "${file.name}" is too large. Maximum size is 10MB.`, 'error');
                continue;
            }

            try {
                // Read file content
                const content = await readFileAsBase64(file);

                validFiles.push({
                    name: file.name,
                    type: normalizedType,  // Use normalized MIME type
                    size: file.size,
                    content: content,
                    isImage: isImage,
                    isPDF: isPDF,
                    isText: isText,
                    isOffice: isOffice,
                });
            } catch (error) {
                showToast(`Failed to read file "${file.name}"`, 'error');
                // eslint-disable-next-line no-console
                console.error('Error reading file:', error);
            }
        }

        if (validFiles.length > 0) {
            setAttachedFiles(prev => [...prev, ...validFiles]);
        }

        // Reset file input
        if (event.target) {
            event.target.value = '';
        }
    }, [showToast]);

    // Read file as base64
    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Extract base64 content (remove data:*/*;base64, prefix)
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Remove attached file
    const handleRemoveFile = useCallback((index) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Trigger file input click
    const handleAttachClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    // Handler for opening AI Instructions modal
    const handleOpenAIInstructions = useCallback(() => {
        setIsAIInstructionsModalOpen(true);
    }, []);

    // Handler for applying custom AI instructions
    const handleApplyAIInstructions = useCallback(async (instructions, promptName = '', agentData = null, playbookData = null) => {
        // When clearing a playbook (playbookData is null), restore the previously saved tool selection.
        // null means no pre-existing selection → reset triggers the Mia Flow Tools default.
        if (!playbookData && prePlaybookSelectedToolsRef.current !== undefined) {
            const restored = prePlaybookSelectedToolsRef.current;
            prePlaybookSelectedToolsRef.current = undefined;
            playbookToolIdsRef.current = [];
            setPlaybookLocksTools(false);
            setSelectedTools(restored !== null ? restored : miaFlowToolIdsRef.current);
        }

        setCustomAIInstructions(instructions);
        setCustomAIInstructionsName(promptName);
        setCustomAIInstructionsPlaybookData(playbookData);
        window.dispatchEvent(new CustomEvent('activePlaybookChanged', { detail: playbookData }));

        // When applying a playbook, derive selected tools from all agents + playbook-level tools
        if (playbookData) {
            setAiMode('agent');

            const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
            const baseUrl = config.bffUrl || window.location.origin;
            const agentRefs = getPlaybookAgentRefs(playbookData);
            let allAgentTools = [];
            if (agentRefs.length > 0 && tenantId) {
                const toolsPerAgent = await Promise.all(
                    agentRefs.map(async (agentRef) => {
                        const agentItemId = agentRef.itemId || agentRef.id;
                        if (!agentItemId) return [];
                        try {
                            const resp = await authorizedFetch(
                                `${baseUrl}/${AI_API_PREFIX}/ai-agents/${tenantId}/${agentItemId}/latest`,
                                { headers: getBffHeaders() }
                            );
                            if (resp.ok) {
                                const result = await resp.json();
                                const fetchedAgent = result.agent || result.data || result;
                                return fetchedAgent.resources?.tools || [];
                            }
                        } catch (e) {
                            console.error('Failed to fetch agent tools for playbook:', e);
                        }
                        return [];
                    })
                );
                allAgentTools = toolsPerAgent.flat();
            }
            const playbookDirectTools = (playbookData.tools || [])
                .map(t => t.itemId || t.name || t)
                .filter(Boolean);
            const rawTools = [...new Set([...allAgentTools, ...playbookDirectTools])];

            // Normalize tool IDs: agents store tools by metadata.name slug (e.g. "list-flow-types").
            // Map each raw ID to a known tool.id, also handling legacy category:name and snake_case.
            const resolveToolIds = (rawIds) => {
                const tools = availableToolsRef.current;
                if (tools.length === 0) return rawIds;
                const knownIds = new Set(tools.map(t => t.id));
                const nameToId = new Map(tools.map(t => [t.name, t.id]));
                const toKebab = s => s.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
                return [...new Set(rawIds.flatMap(rawId => {
                    if (knownIds.has(rawId)) return [rawId];
                    if (nameToId.has(rawId)) return [nameToId.get(rawId)];
                    // Strip legacy "category:name" format
                    const colonIdx = rawId.indexOf(':');
                    const stripped = colonIdx >= 0 ? rawId.substring(colonIdx + 1) : rawId;
                    if (knownIds.has(stripped)) return [stripped];
                    if (nameToId.has(stripped)) return [nameToId.get(stripped)];
                    // Try kebab-case variant (snake_case from old ADK / playbooks)
                    const kebab = toKebab(stripped);
                    if (knownIds.has(kebab)) return [kebab];
                    const kebabRaw = toKebab(rawId);
                    if (knownIds.has(kebabRaw)) return [kebabRaw];
                    return [];
                }))];
            };
            const allTools = resolveToolIds(rawTools);
            playbookToolIdsRef.current = allTools;

            if (allTools.length > 0) {
                setPlaybookLocksTools(true);
                setSelectedTools(prev => {
                    if (prePlaybookSelectedToolsRef.current === undefined) {
                        prePlaybookSelectedToolsRef.current = prev;
                    }
                    return allTools;
                });
            } else {
                setPlaybookLocksTools(true);
                setSelectedTools(prev => {
                    if (prePlaybookSelectedToolsRef.current === undefined) {
                        prePlaybookSelectedToolsRef.current = prev;
                    }
                    return [];
                });
            }
        }

        // Update AdkClient app name override.
        // Playbook with agents → scoped catalog name of root agent (takes priority).
        // Custom agent without playbook → use promptName.
        // Otherwise → reset to default hub.
        if (playbookData) {
            const rootRef = getRootAgentRef(playbookData);
            if (rootRef) {
                const refId = rootRef.itemId || rootRef.id || rootRef.name;
                AdkClient.setOverrideAppName(_scopedName(playbookData.name, refId));
            } else {
                AdkClient.setOverrideAppName(null);
            }
        } else if (agentData) {
            AdkClient.setOverrideAppName(promptName);
        } else {
            AdkClient.setOverrideAppName(null);
        }

        if (instructions) {
            showToast(promptName ? `AI Playbook: ${promptName}` : 'AI Playbook applied to this session', 'success');
        } else {
            showToast('AI Playbook removed', 'info');
        }
    }, []);

    // Handler for opening Save Prompt modal
    const handleOpenSavePrompt = useCallback((promptText) => {
        if (!promptText || !promptText.trim()) {
            showToast('No prompt text to save', 'warning');
            return;
        }
        setInputValue(promptText); // Temporarily set for modal
        setIsSavePromptModalOpen(true);
    }, []);

    // Handler for saving prompt to marketplace
    const handleSavePrompt = useCallback(async (promptData) => {
        try {
            await miaPlatformDataService.saveAIPrompt(promptData);
            showToast(`Prompt "${promptData.name}" saved successfully!`, 'success');
        } catch (error) {
            showToast(`Failed to save prompt: ${error.message}`, 'error');
            throw error; // Re-throw so modal can handle it
        }
    }, []);

    // Handler for opening save message modal
    const handleOpenSaveMessage = useCallback((messageText) => {
        if (!messageText || !messageText.trim()) {
            showToast('No message text to save', 'warning');
            return;
        }
        setMessageToSave(messageText);
        setIsSaveMessageModalOpen(true);
    }, []);

    // Handler for saving message to marketplace
    const handleSaveMessage = useCallback(async (messageData) => {
        try {
            await miaPlatformDataService.saveAIMessage(messageData);
            showToast(`Message "${messageData.name}" saved successfully!`, 'success');
        } catch (error) {
            showToast(`Failed to save message: ${error.message}`, 'error');
            throw error; // Re-throw so modal can handle it
        }
    }, []);

    // Success callback for file save
    handleSaveMessage.successCallback = (message) => {
        showToast(message, 'success');
    };

    // Drag and drop handlers
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set to false if we're leaving the container entirely
        if (e.currentTarget === e.target) {
            setIsDragOver(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (isLoading) {
            return;
        }

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            // Create a synthetic event to reuse the existing handleFileSelect logic
            const syntheticEvent = {
                target: {
                    files: files,
                    value: ''
                }
            };
            handleFileSelect(syntheticEvent);
        }
    }, [isLoading, handleFileSelect]);

    // Format file size for display
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    /**
     * Sends a message directly with the provided text (for shortcuts/quick actions).
     * This bypasses the inputValue state to ensure immediate sending.
     */
    const handleSendDirectMessage = useCallback(async (messageText) => {
        if (messageText.trim()) {
            // Ensure we have an ADK session; create one on first user message
            let currentSessionId = sessionId;
            if (!currentSessionId) {
                const sessionResult = await AdkClient.createSession();
                if (sessionResult.success) {
                    currentSessionId = sessionResult.sessionId;
                    setSessionId(currentSessionId);
                } else {
                    const errorMessage = Messages.createErrorMessage(sessionResult.message);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                    return;
                }
            }

            const userMessage = Messages.createUserMessage(messageText);

            // Check if files were modified in Canvas mode - if so, clear chat history
            if (isCanvasMode && filesModifiedInCanvas) {
                // eslint-disable-next-line no-console
                console.log('🔄 Files modified in Canvas mode - clearing chat history and resending fresh source code');
                setMessages([userMessage]); // Clear history, keep only current message
                setFilesModifiedInCanvas(false); // Reset flag

                // Clear conversation context to force fresh start
                setConversationId(null);
                setConvName('');
            } else {
                setMessages(prevMessages => Messages.addMessage(prevMessages, userMessage));
            }

            setIsLoading(true);
            setConversationStatus('Deciphering User Intent');

            // Create AbortController for this request
            const controller = new AbortController();
            setAbortController(controller);

            // Retry wrapper with exponential backoff for rate limit errors
            const maxRetries = 5;
            for (let attempt = 0; attempt < maxRetries; attempt++) {
                try {
                    // Send message and stream the assistant response via SSE
                    let messageToSend = messageText;

                // Add project context if in canvas mode with selected project
                let projectContextToSend = null;
                if (isCanvasMode && canvasProjectContext && canvasProjectContext.project) {
                    projectContextToSend = {...canvasProjectContext};

                    // Try to load files from IndexedDB - check all services if no specific one is selected
                    let filesLoaded = false;
                    let serviceName = canvasProjectContext.selectedMicroservice?.name;

                    if (serviceName) {
                        // We have a specific service selected, load its files
                        try {
                            // eslint-disable-next-line no-console
                            console.log(`🔍 Loading files for selected microservice (retry): ${serviceName}`);
                            // eslint-disable-next-line no-console
                            console.log(`   Project ID: ${canvasProjectContext.projectId}`);

                            const filesFromDb = await indexedDbFileStorage.getAllFiles(
                                canvasProjectContext.projectId,
                                serviceName
                            );

                            // eslint-disable-next-line no-console
                            console.log(`📂 Retrieved ${filesFromDb?.length || 0} files from IndexedDB (retry)`);

                            if (filesFromDb && filesFromDb.length > 0) {
                                projectContextToSend.allFiles = filesFromDb.map(file => ({
                                    path: file.filePath,
                                    content: file.content
                                }));
                                filesLoaded = true;
                                // eslint-disable-next-line no-console
                                console.log(`📂 Loaded ${filesFromDb.length} files from IndexedDB for AI context (retry path)`);
                                // eslint-disable-next-line no-console
                                console.log(`   Sample files: ${filesFromDb.slice(0, 3).map(f => f.filePath).join(', ')}`);
                            }
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error('Failed to load files from IndexedDB:', error);
                        }
                    }

                    // If no service selected or no files found, try to find ANY service with files
                    if (!filesLoaded && canvasProjectContext.configuration?.services) {
                        // eslint-disable-next-line no-console
                        console.log(`🔍 No service selected or no files found, searching all services... (retry)`);
                        const services = Object.values(canvasProjectContext.configuration.services);

                        for (const service of services) {
                            try {
                                const filesFromDb = await indexedDbFileStorage.getAllFiles(
                                    canvasProjectContext.projectId,
                                    service.name
                                );

                                if (filesFromDb && filesFromDb.length > 0) {
                                    projectContextToSend.allFiles = filesFromDb.map(file => ({
                                        path: file.filePath,
                                        content: file.content
                                    }));
                                    // Auto-set the microservice so file requests work
                                    // Include full service data with repository URLs for Pull/Push operations
                                    projectContextToSend.selectedMicroservice = {
                                        ...service,
                                        name: service.name,
                                        type: service.type
                                    };
                                    filesLoaded = true;
                                    // eslint-disable-next-line no-console
                                    console.log(`📂 Auto-loaded ${filesFromDb.length} files from service: ${service.name} (retry)`);
                                    // eslint-disable-next-line no-console
                                    console.log(`   Sample files: ${filesFromDb.slice(0, 3).map(f => f.filePath).join(', ')}`);
                                    break; // Stop after finding first service with files
                                }
                            } catch (error) {
                                // Continue to next service
                            }
                        }
                    }

                    if (!filesLoaded) {
                        // eslint-disable-next-line no-console
                        console.warn(`⚠️ No files found in IndexedDB for project ${canvasProjectContext.projectId} (retry)`);
                        // eslint-disable-next-line no-console
                        console.warn(`   Make sure to Pull files from the Canvas before chatting with AI`);
                    }

                    // Add Canvas mode instructions at the very beginning
                    const canvasModeInstructions = `[CANVAS MODE - IMPORTANT RULES]\n⚠️ You are in Canvas Mode - working with Mia-Platform Console project configuration.\n\n🚫 FORBIDDEN ACTIONS IN CANVAS MODE:\n- NEVER use GitLab API tools (get_file_content, get_repository_tree, create_file, update_file, etc.)\n- DO NOT fetch file content from external APIs\n- DO NOT try to access Git repositories directly\n\n✅ ALLOWED ACTIONS IN CANVAS MODE:\n- Use Mia-Platform MCP tools to read/modify project configuration\n- Generate unified diff format for file changes (file content is provided in context)\n- Create/modify services, endpoints, collections via MCP tools\n- All file operations are handled through localStorage and the Monaco editor\n\n`;
                    messageToSend = `${canvasModeInstructions}${messageToSend}`;

                    // Enhance the message with project context
                    const contextInfo = [];
                    if (canvasProjectContext.tenant) {
                        contextInfo.push(`Tenant: ${canvasProjectContext.tenant.name}`);
                    }
                    if (canvasProjectContext.project) {
                        contextInfo.push(`Project: ${canvasProjectContext.project.name}`);
                    }
                    if (canvasProjectContext.revision) {
                        contextInfo.push(`Revision: ${canvasProjectContext.revision.name}`);
                    }

                    // Add selected resource context (service, endpoint, etc.)
                    if (canvasProjectContext.selectedResource) {
                        const resource = canvasProjectContext.selectedResource;
                        contextInfo.push(`Selected ${resource.type}: ${resource.name}`);
                    }

                    // Add selected microservice context when viewing source code
                    if (canvasProjectContext.selectedMicroservice) {
                        const microservice = canvasProjectContext.selectedMicroservice;
                        contextInfo.push(`Microservice: ${microservice.name}`);

                        // Include microservice info for AI context
                        const serviceContext = `\n\n[CURRENT MICROSERVICE]\nService Name: ${microservice.name}\nType: ${microservice.type || 'custom'}\nRepository: ${microservice.repositoryUrl || 'N/A'}\n\n⚠️ IMPORTANT INSTRUCTIONS FOR CANVAS MODE:\n1. When creating or modifying files, they should be added to the "${microservice.name}" microservice repository, NOT as a new service.\n2. NEVER use GitLab API tools (get_file_content, create_file, update_file, etc.) in Canvas mode.\n3. All file operations are handled through the Monaco editor and localStorage.\n4. File content is already provided above - do not fetch it from GitLab.\n5. Generate unified diff format for file changes - the UI will handle applying them.\n`;
                        messageToSend = `${messageToSend}${serviceContext}`;
                    }

                    // Add selected file context with content
                    if (canvasProjectContext.selectedFile) {
                        const file = canvasProjectContext.selectedFile;
                        contextInfo.push(`Viewing File: ${file.path || file.name}`);

                        // Use helper function to get fresh content from IndexedDB (fixes stale content bug)
                        const fileContext = await buildFileContextWithFreshContent(
                            file,
                            canvasProjectContext.projectId,
                            canvasProjectContext.selectedMicroservice?.name,
                            false // Don't include line count for direct messages
                        );
                        if (fileContext) {
                            messageToSend = `${messageToSend}${fileContext}`;
                        }
                    }

                    if (contextInfo.length > 0) {
                        messageToSend = `[CANVAS MODE - Working on ${contextInfo.join(' > ')}]\n\n${messageToSend}`;
                    }
                } else if (hideCanvas) {
                    // Compose mode: include tenant context, no project
                    const composeTenant = miaFlowAuth.getSelectedTenant();
                    if (composeTenant) {
                        const tenantLine = `Tenant: ${composeTenant.name || composeTenant.tenantId} (id: ${composeTenant.tenantId})`;
                        messageToSend = `[CONTEXT]\n${tenantLine}\nNote: No project is selected. Operate on the tenant level or provide general guidance.\n\n[USER REQUEST]\n${messageToSend}`;
                    }
                }

                // Add AI mode instruction at the beginning of the message
                if (aiMode === 'plan') {
                    messageToSend = `[PLAN MODE ACTIVE]\n⚠️ CRITICAL INSTRUCTION: You are currently in PLAN MODE. In this mode, you MUST NOT create, modify, deploy, or execute anything. You should ONLY provide information, explanations, suggestions, and planning guidance. Do not use any tools that would create or modify resources (projects, services, files, deployments, etc.). Only use read-only tools to gather information and provide helpful guidance to the user. You MAY use the curl_request_tool to make read-only HTTP requests (e.g. GET) to inspect or query APIs and retrieve information.\n\n${messageToSend}`;
                }

                // Add custom AI instructions at the very top (after all mode instructions)
                if (customAIInstructions) {
                    messageToSend = `[CUSTOM AI INSTRUCTIONS]\n${customAIInstructions}\n\n${messageToSend}`;
                }

                const sendResult = await AdkClient.sendMessage(
                    currentSessionId,
                    messageToSend,
                    controller.signal,
                    projectContextToSend,
                    isCanvasMode,  // Pass canvas mode to auto-select agent
                    aiMode  // Pass AI mode (agent or plan)
                );

                if (!sendResult.success) {
                    setIsLoading(false);
                    setAbortController(null);
                    setConversationStatus('');
                    const errorMessage = Messages.createErrorMessage(sendResult.message);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                    return;
                }

                const response = sendResult.response;
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let accumulatedText = '';
                let stopProcessingEvents = false;
                let intermediateMessageCreated = false;

                // Don't create an intermediate message yet - wait until we start receiving content
                // This prevents replacing/hiding previous messages that contain diagrams or content

                try {
                    // Parse SSE frames and update chat incrementally
                    const parser = createParser({
                        onEvent: (event) => {
                            try {
                                const eventData = JSON.parse(event.data);

                                // Handle usage metadata if present
                                if (eventData.usageMetadata) {
                                    const usage = eventData.usageMetadata;
                                    // eslint-disable-next-line no-console
                                    console.log('📊 [SSE-Image] Token usage metadata received:', usage);
                                    // Update token counts
                                    if (usage.promptTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Image] Updating inputTokens by', usage.promptTokenCount);
                                        setInputTokens(prev => {
                                            const newValue = prev + usage.promptTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Image] inputTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                    if (usage.candidatesTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Image] Updating outputTokens by', usage.candidatesTokenCount);
                                        setOutputTokens(prev => {
                                            const newValue = prev + usage.candidatesTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Image] outputTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                    if (usage.totalTokenCount !== undefined) {
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Image] Setting totalTokens to', usage.totalTokenCount);
                                        // FIXED: Should accumulate, not replace, to track total session usage
                                        setTotalTokens(prev => {
                                            const newValue = prev + usage.totalTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Image] totalTokens accumulated:', prev, '+', usage.totalTokenCount, '=', newValue);
                                            return newValue;
                                        });
                                    } else if (usage.promptTokenCount !== undefined && usage.candidatesTokenCount !== undefined) {
                                        // Calculate total if not provided
                                        // eslint-disable-next-line no-console
                                        console.log('[SSE-Image] Calculating totalTokens from input+output');
                                        setTotalTokens(prev => {
                                            const newValue = prev + usage.promptTokenCount + usage.candidatesTokenCount;
                                            // eslint-disable-next-line no-console
                                            console.log('[SSE-Image] totalTokens:', prev, '->', newValue);
                                            return newValue;
                                        });
                                    }
                                }

                                if (eventData.error) {
                                    setIsLoading(false);
                                    setConversationStatus('');

                                    // Handle rate limit errors with special formatting
                                    if (eventData.rate_limit_error) {
                                        const rateLimitMessage = Messages.createInfoMessage(
                                            `⏱️ ${eventData.error}\n\n${eventData.details || ''}\n\n${eventData.suggestion || 'Please wait 1 minute before trying again.'}`
                                        );
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, rateLimitMessage));
                                    }
                                    // Handle authentication errors differently
                                    else if (eventData.auth_required || eventData.error.toLowerCase().includes('authentication')) {
                                        const authMessage = Messages.createInfoMessage(
                                            `🔐 ${eventData.error}\n\n${eventData.details || ''}\n\n${eventData.suggestion || 'Please authenticate to access all features.'}`
                                        );
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, authMessage));
                                    } else {
                                        const errorMessage = Messages.createErrorMessage(eventData.error);
                                        setMessages(prevMessages => Messages.addMessage(prevMessages, errorMessage));
                                    }

                                    stopProcessingEvents = true;
                                    return;
                                }

                                if (eventData.content && eventData.content.parts && eventData.content.parts.length > 0) {
                                    for (const part of eventData.content.parts) {
                                        if (part.functionCall) {
                                            const toolName = part.functionCall.name || 'Tool';
                                            setConversationStatus(`Calling ${toolName}`);

                                            // Create intermediate message if not created yet
                                            if (!intermediateMessageCreated) {
                                                intermediateMessageCreated = true;
                                                setMessages(prevMessages => {
                                                    const intermediateMessage = {
                                                        id: Date.now() + Math.random(),
                                                        text: '',
                                                        type: 'received',
                                                        isIntermediate: true,
                                                        reasoningSteps: [{
                                                            title: 'Understanding Your Request',
                                                            description: 'Analyzing your query to determine the best approach',
                                                            reasoning: 'Processing natural language input and identifying intent, entities, and required actions to provide an accurate response.',
                                                            startTime: Date.now()
                                                        }],
                                                        currentReasoningStep: 0
                                                    };
                                                    return Messages.addMessage(prevMessages, intermediateMessage);
                                                });
                                            }

                                            setMessages(prevMessages => {
                                                const args = part.functionCall.args || {};
                                                let description = `Executing ${toolName}`;
                                                let title = `Using ${toolName}`;
                                                if (toolName === 'curl_request') {
                                                    const method = (args.method || 'GET').toUpperCase();
                                                    const url = args.url || '';
                                                    try {
                                                        const parsed = new URL(url);
                                                        title = `Using ${toolName}: ${method} ${parsed.pathname}`;
                                                    } catch (_) {
                                                        title = `Using ${toolName}: ${method} ${url}`;
                                                    }
                                                    description = `${method} ${url}`;
                                                } else if (Object.keys(args).length > 0) {
                                                    const firstKey = Object.keys(args)[0];
                                                    const firstVal = String(args[firstKey]).substring(0, 80);
                                                    title = `Using ${toolName} — ${firstKey}: ${firstVal}`;
                                                    description = `${firstKey}: ${firstVal}`;
                                                }

                                                return Messages.addReasoningStep(prevMessages, {
                                                    title: title,
                                                    description: description,
                                                    reasoning: `The model determined that ${toolName} is the appropriate tool to handle this part of your request.`,
                                                    functionCall: {
                                                        name: part.functionCall.name,
                                                        args: part.functionCall.args || {}
                                                    }
                                                });
                                            });
                                        }

                                        if (part.functionResponse) {
                                            setConversationStatus('Processing Response');

                                            setMessages(prevMessages => {
                                                let resultSummary = 'Tool executed successfully';
                                                if (part.functionResponse.response) {
                                                    const response = part.functionResponse.response;
                                                    if (typeof response === 'string') {
                                                        resultSummary = response.length > 100 ?
                                                            response.substring(0, 100) + '...' :
                                                            response;
                                                    } else if (typeof response === 'object') {
                                                        resultSummary = 'Received structured data response';
                                                    }
                                                }

                                                return Messages.updateCurrentReasoningStep(prevMessages, {
                                                    result: part.functionResponse.response || 'Success',
                                                    reasoning: `Tool completed. ${resultSummary}. Now synthesizing this information into the final response.`
                                                });
                                            });
                                        }

                                        let currentAccumulatedText = '';
                                        if (part.text) {
                                            // Create intermediate message if not created yet (happens when no tool calls, direct text response)
                                            if (!intermediateMessageCreated) {
                                                intermediateMessageCreated = true;
                                                setMessages(prevMessages => {
                                                    const intermediateMessage = {
                                                        id: Date.now() + Math.random(),
                                                        text: '',
                                                        type: 'received',
                                                        isIntermediate: true,
                                                        reasoningSteps: [{
                                                            title: 'Understanding Your Request',
                                                            description: 'Analyzing your query to provide a response',
                                                            reasoning: 'Processing your request and preparing the response.',
                                                            startTime: Date.now()
                                                        }],
                                                        currentReasoningStep: 0
                                                    };
                                                    return Messages.addMessage(prevMessages, intermediateMessage);
                                                });
                                            }

                                            setConversationStatus('Generating Response');

                                            if (eventData.partial) {
                                                accumulatedText += part.text;
                                                currentAccumulatedText = accumulatedText;
                                            } else {
                                                accumulatedText = '';
                                                currentAccumulatedText = part.text;
                                                setConversationStatus('');

                                                // Keep reasoning steps visible even after response is complete
                                            }

                                        if (!Messages.isInsideIncompleteMermaidBlock(currentAccumulatedText)) {
                                            setMessages(prevMessages => Messages.updateOrCreateIntermediateMessage(prevMessages, currentAccumulatedText, eventData.partial));
                                        }

                                        // If canvas is open and this is the final frame, check for code updates
                                        if (isCanvasMode && !eventData.partial) {
                                            // Extract ALL code updates from the response
                                            const codeUpdates = extractAllCodeUpdates(
                                                currentAccumulatedText,
                                                canvasProjectContext?.selectedFile?.path || null
                                            );

                                            if (codeUpdates && codeUpdates.length > 0) {
                                                // Collect modified files for summary display
                                                const modifiedFiles = [];

                                                // Process each code update
                                                for (const codeUpdate of codeUpdates) {
                                                    const eventDetail = {
                                                        filePath: codeUpdate.filePath,
                                                        projectId: canvasProjectContext?.projectId,
                                                        serviceName: canvasProjectContext?.selectedMicroservice?.name,
                                                        isStreaming: false
                                                    };

                                                    // Track modified file
                                                    modifiedFiles.push({
                                                        path: codeUpdate.filePath,
                                                        operation: codeUpdate.updateType || 'modified',
                                                        projectId: canvasProjectContext?.projectId,
                                                        serviceName: canvasProjectContext?.selectedMicroservice?.name
                                                    });

                                                    // Add appropriate fields based on update type
                                                    if (codeUpdate.isDiff && codeUpdate.updateType === 'diff') {
                                                        // Unified diff format - use custom diff provider
                                                        eventDetail.updateType = 'diff';
                                                        eventDetail.diffContent = codeUpdate.diffContent;
                                                        eventDetail.content = codeUpdate.diffContent;
                                                    } else if (codeUpdate.isPartialUpdate && codeUpdate.updateType === 'replace') {
                                                        // Partial replacement
                                                        eventDetail.updateType = 'replace';
                                                        eventDetail.oldString = codeUpdate.oldString;
                                                        eventDetail.newString = codeUpdate.newString;
                                                    } else {
                                                        // Full content replacement
                                                        eventDetail.updateType = 'full';
                                                        eventDetail.content = codeUpdate.content;
                                                    }

                                                    window.dispatchEvent(new CustomEvent('aiContentUpdate', {
                                                        detail: eventDetail
                                                    }));

                                                    // eslint-disable-next-line no-console
                                                    console.log(`✅ AI code ${codeUpdate.isDiff ? 'diff' : codeUpdate.isPartialUpdate ? 'partial' : 'full'} update dispatched:`, codeUpdate.filePath);
                                                }

                                                // Dispatch final event with all modified files for the loader to display
                                                if (modifiedFiles.length > 0) {
                                                    setTimeout(() => {
                                                        window.dispatchEvent(new CustomEvent('diffParserComplete', {
                                                            detail: {
                                                                files: modifiedFiles,
                                                                count: modifiedFiles.length
                                                            }
                                                        }));
                                                    }, 500); // Small delay to ensure all diffs are processed
                                                }

                                                // Monaco editor will handle localStorage save via its event listener
                                            }

                                            // DON'T update canvas content when code updates were extracted
                                            // The files will be opened individually by MonacoFileEditor/RepositoryTree
                                            // Showing the full AI response in Monaco would be confusing

                                            // Only update canvas if NO code updates were found (pure documentation response)
                                            if (!codeUpdates || codeUpdates.length === 0) {
                                                const hasCode = /```[\s\S]+```/g.test(currentAccumulatedText) ||
                                                               /function\s+\w+\s*\(/g.test(currentAccumulatedText) ||
                                                               /<\/?\w+[^>]*>/g.test(currentAccumulatedText);

                                                setCanvasContent({
                                                    type: hasCode ? 'code' : 'document',
                                                    content: currentAccumulatedText,
                                                    title: 'Generated Content'
                                                });
                                            }
                                        }
                                    }
                                }
                              }
                            } catch (parseError) {
                                // eslint-disable-next-line no-console
                                console.error('Error parsing SSE event:', parseError);
                            }
                        }
                    });

                    while (true) {
                        const {done, value} = await reader.read();
                        if (done || stopProcessingEvents) {
                            break;
                        }
                        const chunk = decoder.decode(value, {stream: true});
                        parser.feed(chunk);
                    }
                } catch (streamError) {
                    // Check if this is a rate limit error that should be retried
                    let errorMessage = 'Failed to process server response';
                    let isRateLimitError = false;

                    if (streamError.isRateLimitError) {
                        // Error was flagged as rate limit by the parse error handler
                        isRateLimitError = true;
                        errorMessage = streamError.errorMessage;
                    } else if (streamError.message) {
                        errorMessage += ` (${streamError.message})`;
                        // Check message for rate limit indicators
                        isRateLimitError = streamError.message.includes('RateLimitError') ||
                                          streamError.message.includes('RESOURCE_EXHAUSTED') ||
                                          streamError.message.includes('429');
                    }

                    if (accumulatedText && !isRateLimitError) {
                        errorMessage = 'Server connection interrupted. The response may be incomplete.';
                    }

                    // If rate limit error, rethrow to be caught by retry handler
                    if (isRateLimitError) {
                        const error = new Error('RATE_LIMIT_ERROR');
                        error.isRateLimitError = true;
                        error.originalMessage = errorMessage;
                        throw error;
                    }

                    const errorMsg = Messages.createErrorMessage(errorMessage);
                    setMessages(prevMessages => Messages.addMessage(prevMessages, errorMsg));
                } finally {
                    reader.releaseLock();
                }

                // Success - end loading state and exit retry loop
                setIsLoading(false);
                setConversationStatus('');
                setAbortController(null);
                break; // Exit retry loop on success

            } catch (error) {
                // Check if this is a rate limit error that should be retried
                const isRateLimitError = error.isRateLimitError ||
                    (error.message && (error.message.includes('RateLimitError') ||
                     error.message.includes('RESOURCE_EXHAUSTED') ||
                     error.message.includes('429') ||
                     error.message === 'RATE_LIMIT_ERROR'));

                const isLastAttempt = attempt === maxRetries - 1;

                if (isRateLimitError && !isLastAttempt) {
                    // Calculate exponential backoff delay: 2^(attempt+1) seconds
                    const delaySeconds = Math.pow(2, attempt + 1);

                    // eslint-disable-next-line no-console
                    console.log(`⏳ Rate limit error - retrying in ${delaySeconds}s (attempt ${attempt + 2}/${maxRetries})...`);

                    setConversationStatus(`⏱️ Rate limit reached. Retrying in ${delaySeconds} seconds (attempt ${attempt + 2}/${maxRetries})...`);

                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, delaySeconds * 1000));

                    // Reset status for next attempt
                    setConversationStatus('Retrying request...');

                    // Continue to next iteration of retry loop
                    continue;
                }

                // Non-retriable error or max retries exceeded
                setIsLoading(false);
                setAbortController(null);
                setConversationStatus('');

                if (error.name === 'AbortError') {
                    return;
                }

                // Show appropriate error message
                let errorMessage = error.originalMessage || error.message;
                if (isRateLimitError && isLastAttempt) {
                    errorMessage = '⏱️ Rate limit error persists after multiple retries. Please try again in a few minutes.';
                }

                const errorMsg = Messages.createErrorMessage(errorMessage);
                setMessages(prevMessages => Messages.addMessage(prevMessages, errorMsg));
                break; // Exit retry loop on error
            }
            } // End retry loop
        }
    }, [setMessages, setIsLoading, sessionId, isCanvasMode, filesModifiedInCanvas, setFilesModifiedInCanvas, setConversationId, setConvName, canvasProjectContext, setCanvasContent, extractCodeUpdate, buildFileContextWithFreshContent, extractAllCodeUpdates]);

    /**
     * Saves the current conversation (name, messages, session) to the backend.
     * If it's a new conversation, the backend will assign and return a new ID.
     * Auto-generates title from first user message if not set.
     */
    const handleSaveConversation = useCallback(async () => {
        // Safeguard: Don't save if we're updating an existing conversation but have no messages
        // This prevents race conditions from overwriting the database with empty data
        if (conversationId && messages.length === 0) {
            console.warn('Prevented saving empty messages array for existing conversation');
            return;
        }

        // Auto-generate title from first message if not already set
        let titleToSave = convName;
        if (!titleToSave && messages.length > 0) {
            // Find first user message
            const firstUserMessage = messages.find(msg => msg.type === 'sent');
            if (firstUserMessage) {
                // Take first 50 characters as title
                titleToSave = firstUserMessage.text.substring(0, 50);
                if (firstUserMessage.text.length > 50) {
                    titleToSave += '...';
                }
                setConvName(titleToSave);
            }
        }

        // Filter out intermediate messages before saving (keep only final messages)
        const finalMessages = messages.filter(msg => !msg.isIntermediate);

        const appName = AdkClient.overrideAppName || AdkClient.APP_NAME;
        const convMode = hideCanvas ? 'compose' : 'code';
        const convMetadata = { mode: convMode };
        if (!hideCanvas && canvasProjectContext?.projectId) {
            convMetadata.projectId = canvasProjectContext.projectId;
            convMetadata.projectName = canvasProjectContext.project?.name || canvasProjectContext.name || canvasProjectContext.projectId;
            convMetadata.tenantId = params.tenantId || canvasProjectContext?.tenant?.tenantId || null;
        }
        // Track playbook association so it can be restored when the memory is reopened
        if (customAIInstructionsPlaybookData?.itemId) {
            convMetadata.playbookItemId = customAIInstructionsPlaybookData.itemId;
            if (!convMetadata.tenantId) {
                convMetadata.tenantId = customAIInstructionsPlaybookData.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null;
            }
        }
        const result = await Conversations.saveConversation(conversationId, titleToSave, finalMessages, sessionId, selectedTools, appName, convMetadata);
        if (result.success) {
            const wasNewConversation = !conversationId;
            // eslint-disable-next-line no-console
            console.log('💾 Conversation saved', {
                wasNewConversation,
                conversationId: result.conversationId,
                messageCount: finalMessages.length
            });
            setConversationId(result.conversationId);

            // Update URL with conversationId if this was a new conversation
            // This prevents the useEffect from thinking user navigated away and clearing messages
            if (wasNewConversation && result.conversationId) {
                // IMPORTANT: Update the ref BEFORE navigate to prevent clearing
                prevUrlConversationIdRef.current = result.conversationId;
                // eslint-disable-next-line no-console
                console.log('🔗 Updating URL with conversationId:', result.conversationId);
                const currentPath = location.pathname;
                navigate(`${currentPath}?conversationId=${result.conversationId}`, { replace: true });
            }

            // Set session tools on first save (lock in the tools for this session)
            if (!sessionTools) {
                setSessionTools(selectedTools);
                setShowToolChangeNotice(false);
            }

            // Dispatch event to notify sidebar to refresh
            window.dispatchEvent(new CustomEvent('conversationSaved', {
                detail: {conversationId: result.conversationId}
            }));
        } else {
            showToast(`Failed to save conversation: ${result.message}`, 'error');
        }
    }, [conversationId, convName, messages, sessionId, showToast, navigate, location.pathname, selectedTools, sessionTools]);

    // Auto-save new conversations once (after first complete exchange)
    useEffect(() => {
        // Prevent duplicate saves using ref
        if (isSavingRef.current) {
            return;
        }

        const performAutoSave = async () => {
            // Only save new conversations (no conversationId yet)
            // Existing conversations are not updated (PATCH removed)
            if (!conversationId && messages.length >= 2) {
                // Guard: skip if no new messages were added this session
                // (e.g. messages were just restored from localStorage)
                if (messages.length <= restoredMessageCountRef.current) {
                    return;
                }

                // Auto-save new conversations ONLY after first complete exchange
                // (user message + non-intermediate assistant response)
                // This prevents multiple saves during streaming
                const lastMessage = messages[messages.length - 1];
                const hasUserMessage = messages.some(msg => msg.type === 'sent');
                const hasCompleteResponse = lastMessage &&
                                            lastMessage.type === 'received' &&
                                            !lastMessage.isIntermediate;

                // Only save when we have a complete response (not intermediate streaming updates)
                if (hasUserMessage && hasCompleteResponse) {
                    isSavingRef.current = true;
                    await handleSaveConversation();
                    // Don't reset flag here - it will be reset when conversationId is set
                }
            }
        };

        performAutoSave();
    }, [messages, conversationId, handleSaveConversation]);

    // Keep input focused after state changes
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [isLoading]);

    // Controlled component handler for the text input
    const handleInputChange = useCallback((event) => {
        const value = event.target.value;
        setInputValue(value);
        // Save to localStorage to prevent loss on refresh
        try {
            localStorage.setItem('chatInputValue', value);
        } catch (e) {
            console.error('Failed to save input to localStorage:', e);
        }
        // Show spec-kit slash command autocomplete:
        // open as soon as input starts with '/' and no space yet (still typing the command)
        if (value.startsWith('/') && !value.includes(' ')) {
            setShowSlashMenu(true);
            setSlashMenuIndex(0);
        } else {
            setShowSlashMenu(false);
        }
    }, [setInputValue]);

    // Auto-resize textarea logic
    useLayoutEffect(() => {
        if (inputRef.current) {
            // Reset height to allow shrinking
            inputRef.current.style.height = 'auto';

            // Calculate scrollHeight (max 200px or similar to avoid taking full screen)
            const scrollHeight = inputRef.current.scrollHeight;
            const maxHeight = 200; // Limit max growth

            // Set new height based on content
            inputRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;

            // Enable scrolling if content exceeds maxHeight
            inputRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        }
    }, [inputValue]);

    // Auto-resize textarea logic
    useLayoutEffect(() => {
        if (inputRef.current) {
            // Reset height to allow shrinking
            inputRef.current.style.height = 'auto';

            // Calculate scrollHeight (max 200px or similar to avoid taking full screen)
            const scrollHeight = inputRef.current.scrollHeight;
            const maxHeight = 200; // Limit max growth

            // Set new height based on content
            inputRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;

            // Enable scrolling if content exceeds maxHeight
            inputRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        }
    }, [inputValue]);

    // Select a spec-kit slash command from the autocomplete menu
    const handleSlashCommandSelect = useCallback((command) => {
        setInputValue(command + ' ');
        setShowSlashMenu(false);
        setSlashMenuIndex(0);
        try { localStorage.setItem('chatInputValue', command + ' '); } catch { /* ignore */ }
        setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    }, [setInputValue, inputRef]);

    // Sends message on Enter key press (but allows Shift+Enter for newlines).
    const handleKeyDown = useCallback((event) => {
        // Slash command menu navigation
        if (showSlashMenu) {
            const cmds = filterSpeckitCommands(inputValue, catalogSpecCommands.length ? catalogSpecCommands : undefined);
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSlashMenuIndex(i => Math.min(i + 1, cmds.length - 1));
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSlashMenuIndex(i => Math.max(i - 1, 0));
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setShowSlashMenu(false);
                return;
            }
            if (event.key === 'Tab') {
                const cmd = cmds[slashMenuIndex];
                if (cmd) {
                    event.preventDefault();
                    handleSlashCommandSelect(cmd.command);
                    return;
                }
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (event.altKey) {
                handleAddToQueue();
            } else if (isLoading) {
                handleSteerWithMessage();
            } else {
                handleSendMessage();
            }
        }
    }, [showSlashMenu, inputValue, slashMenuIndex, handleSlashCommandSelect, handleSendMessage, handleAddToQueue, isLoading, handleSteerWithMessage]);

    // Listen for Ask AI from logs event
    useEffect(() => {
        const handleAskAIFromLogs = (event) => {
            const {prompt} = event.detail || {};
            if (prompt) {
                setInputValue(prompt);
                // Auto-send the message
                setTimeout(() => {
                    handleSendMessage();
                }, 100);
            }
        };

        window.addEventListener('askAIFromLogs', handleAskAIFromLogs);

        return () => {
            window.removeEventListener('askAIFromLogs', handleAskAIFromLogs);
        };
    }, [handleSendMessage]);

    // Listen for Ask AI from file errors event
    useEffect(() => {
        const handleAskAIFromFile = (event) => {
            const {filePath, fileContent, errorDetails} = event.detail || {};
            if (filePath && fileContent) {
                const ext = filePath.split('.').pop();
                const prompt = `Fix the following errors on file "${filePath}" by providing the corrected code.\n\n**Errors:**\n${errorDetails}\n\n**Current file content:**\n\`\`\`${ext}\n${fileContent}\n\`\`\`\n\n`;

                setInputValue(prompt);
                // Auto-send the message
                setTimeout(() => {
                    handleSendMessage();
                }, 100);
            }
        };

        window.addEventListener('askAIFromFile', handleAskAIFromFile);

        return () => {
            window.removeEventListener('askAIFromFile', handleAskAIFromFile);
        };
    }, [handleSendMessage]);

    // Listen for Ask AI from pipeline jobs event
    useEffect(() => {
        const handleAskAIFromPipeline = (event) => {
            const {jobName, jobStatus, jobStage, jobLog, prompt} = event.detail || {};
            if (prompt) {
                setInputValue(prompt);
                // Auto-send the message
                setTimeout(() => {
                    handleSendMessage();
                }, 100);
            }
        };

        window.addEventListener('askAIFromPipeline', handleAskAIFromPipeline);

        return () => {
            window.removeEventListener('askAIFromPipeline', handleAskAIFromPipeline);
        };
    }, [handleSendMessage]);

    // Handle initial prompt from Home page navigation
    useEffect(() => {
        const initialPrompt = location.state?.initialPrompt;
        const mode = location.state?.mode;
        const initialFiles = location.state?.initialFiles;

        if (initialPrompt && !messages.length) {
            // Canvas is always enabled, just set AI mode based on user selection
            if (mode === 'code') {
                setAiMode('agent');
            } else if (mode === 'chat') {
                setAiMode('plan');
            }

            // Convert raw File objects to the expected {name, type, size, content, isImage, isPDF} format
            const loadFiles = async () => {
                if (initialFiles && initialFiles.length > 0) {
                    const converted = [];
                    for (const file of initialFiles) {
                        // Already converted (has a content property)
                        if (file.content !== undefined) {
                            converted.push(file);
                            continue;
                        }
                        try {
                            const content = await readFileAsBase64(file);
                            const isImage = file.type.startsWith('image/');
                            const isPDF = file.type === 'application/pdf';
                            const isText = !isImage && !isPDF;
                            converted.push({
                                name: file.name,
                                type: file.type || 'text/plain',
                                size: file.size,
                                content,
                                isImage,
                                isPDF,
                                isText,
                            });
                        } catch {
                            console.error('Failed to read file:', file.name);
                        }
                    }
                    if (converted.length > 0) {
                        setAttachedFiles(converted);
                    }
                }

                // Set the input value
                setInputValue(initialPrompt);

                // Auto-send the message after a short delay to ensure component is fully mounted
                setTimeout(() => {
                    handleSendMessage();
                }, 300);

                // Clear the state to prevent re-triggering
                navigate(location.pathname, { replace: true, state: {} });
            };

            loadFiles();
        }
    }, [location, messages.length, handleSendMessage, navigate]);

    // Listen for populate-chat-input events from Canvas component
    // This populates the input with the initial prompt and either auto-sends (for home-created projects) or shows a banner
    useEffect(() => {
        const handlePopulateChatInputEvent = (event) => {
            const { message, showSendPrompt, autoSend, setAskAboutComponents } = event.detail;
            if (message) {
                console.log('📝 [MainChat] Received populate-chat-input event:', message.substring(0, 100), autoSend ? '(auto-send)' : '');
                setInputValue(message);

                // Auto-send message for home-created projects
                if (autoSend) {
                    // Set flag to ask about Mia components after response if requested
                    if (setAskAboutComponents) {
                        sessionStorage.setItem('mia_ask_about_components_next', 'true');
                    }

                    // Auto-send after a brief delay to ensure input is set and state updates have propagated
                    setTimeout(() => {
                        console.log('🚀 [MainChat] Auto-sending message for home-created project');
                        // Trigger send button click programmatically to ensure it uses the latest state
                        const sendButton = document.querySelector('.send-button-icon');
                        if (sendButton && !sendButton.disabled) {
                            sendButton.click();
                        } else {
                            console.warn('⚠️ Send button not found or disabled');
                        }
                    }, 150);
                } else if (showSendPrompt) {
                    // Show banner to highlight send button if requested (legacy behavior)
                    setShowSendPromptBanner(true);

                    // Set flag to ask about Mia components after response if requested
                    if (setAskAboutComponents) {
                        sessionStorage.setItem('mia_ask_about_components_next', 'true');
                    }
                }
            }
        };

        window.addEventListener('populate-chat-input', handlePopulateChatInputEvent);
        return () => {
            window.removeEventListener('populate-chat-input', handlePopulateChatInputEvent);
        };
    }, [setInputValue]);

    // After config git pull completes, auto-send the pending brainstorming prompt
    useEffect(() => {
        const handleConfigPullCompleted = (event) => {
            const { success, serviceName: pulledService } = event.detail || {};
            if (!success) return;

            // Only react to the Project Configuration pull
            if (pulledService !== 'Project Configuration' && pulledService !== 'project-configuration') return;

            const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
            const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');

            if (pendingPrompt && pendingMode === 'brainstorming') {
                sessionStorage.removeItem('mia_pending_initial_prompt');
                sessionStorage.removeItem('mia_pending_initial_mode');

                const brainstormingPrompt = `Create a file named brainstorming.md that answers the following user request: "${pendingPrompt}"\n\nIn brainstorming.md, explore what is available in the Mia Platform catalog/marketplace to satisfy the request. Structure the document with:\n1. **Request Summary**: What the user wants to achieve\n2. **Available Mia Platform Components**: Relevant services, plugins, templates from the Mia catalog that can be used\n3. **Suggested Architecture**: How to combine these components to fulfill the request\n4. **Next Steps**: Concrete actions to implement the solution using Mia Platform`;

                console.log('🧠 [Brainstorming] Config pull completed, auto-triggering brainstorming prompt');

                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('populate-chat-input', {
                        detail: { message: brainstormingPrompt, autoSend: true }
                    }));
                }, 500);
            }
        };

        window.addEventListener('gitPullCompleted', handleConfigPullCompleted);
        return () => {
            window.removeEventListener('gitPullCompleted', handleConfigPullCompleted);
        };
    }, []);

    // Handler for Canvas mode (canvas is always enabled, so this is a no-op)
    const handleToggleCanvas = useCallback(() => {
        // Canvas mode is always on, nothing to toggle
        // This function is kept for compatibility but does nothing
    }, []);


    // Handler for project context changes from Canvas
    const handleProjectContextChange = useCallback((context) => {
        setCanvasProjectContext(context);
        // eslint-disable-next-line no-console
        console.log('📋 Canvas project context updated:', context);
    }, []);

    // Sync URL with canvas selections (microservice, branch, tab, filePath, lineNumber)
    const syncUrlWithCanvasSelections = useCallback((microservice, branch, tab, filePath, lineNumber) => {
        const tenantId = params.tenantId || canvasProjectContext?.tenant?.tenantId;
        const projectId = params.projectId || canvasProjectContext?.projectId;

        if (!tenantId || !projectId) {
            return;
        }

        // Check if we're switching to this project - if so, don't sync URL with old selections
        const switchingToProject = sessionStorage.getItem('mia_switching_to_project');
        if (switchingToProject === projectId) {
            // eslint-disable-next-line no-console
            console.log('🚫 Skipping URL sync - project switching in progress for:', projectId);
            return;
        }

        // Build URL path with encoded parameters
        let path = `/chat/${tenantId}/${projectId}`;
        if (microservice) {
            path += `/${encodeURIComponent(microservice)}`;
            if (branch) {
                path += `/${encodeURIComponent(branch)}`;
                if (tab) {
                    path += `/${encodeURIComponent(tab)}`;
                    // Add file path if provided (for code tab)
                    if (filePath && tab === 'code') {
                        path += `/${encodeURIComponent(filePath)}`;
                    }
                }
            }
        }

        // Add line number as hash if provided
        const hash = lineNumber ? `#L${lineNumber}` : '';

        // Preserve existing query params (e.g. playbookId) when syncing the URL
        const existingSearch = location.search || '';

        // Only navigate if path, search or hash changed
        const fullPath = path + existingSearch + hash;
        const currentFullPath = location.pathname + location.search + location.hash;

        if (currentFullPath !== fullPath) {
            navigate(path + existingSearch + hash, { replace: true });
        }
    }, [params.tenantId, params.projectId, canvasProjectContext, location.pathname, location.search, location.hash, navigate]);

    // Reconstruct URL from localStorage when partial URL is provided
    useEffect(() => {
        // Only run when we have tenantId and projectId but missing microservice/branch/tab
        if (!params.tenantId || !params.projectId || params.microservice) return;

        try {
            // Check if we're switching to this specific project - if so, skip URL reconstruction
            const switchingToProject = sessionStorage.getItem('mia_switching_to_project');
            if (switchingToProject === params.projectId) {
                // eslint-disable-next-line no-console
                console.log('🚫 Skipping URL reconstruction - switching to project:', params.projectId);
                // DON'T clear the flag here - it will be cleared when project is fully loaded
                return;
            }

            // Check localStorage for saved selections
            const savedResource = localStorage.getItem('mia_selectedResource');
            const savedRevision = localStorage.getItem('mia_selectedRevision');
            const savedTab = localStorage.getItem('mia_activeTab');

            if (savedResource || savedRevision || savedTab) {
                let microservice = null;
                let branch = null;
                let tab = null;

                if (savedResource) {
                    const resource = JSON.parse(savedResource);
                    // Validate that the resource belongs to the current project
                    const resourceProjectId = resource?.projectId;
                    if (resourceProjectId && resourceProjectId !== params.projectId) {
                        // Resource is from a different project, clear it and don't use it
                        // eslint-disable-next-line no-console
                        console.log('🔄 Clearing localStorage resource from different project:', resourceProjectId, '!==', params.projectId);
                        localStorage.removeItem('mia_selectedResource');
                        // Don't use this resource
                    } else if (resource?.type === 'service' && resource?.data?.name) {
                        microservice = resource.data.name;
                    }
                }

                if (savedRevision) {
                    const revision = JSON.parse(savedRevision);
                    if (revision?.name) {
                        branch = revision.name;
                    }
                }

                if (savedTab) {
                    tab = savedTab;
                }

                // Reconstruct URL with saved selections (only if we have valid data AND we actually need to change the URL)
                if (microservice || branch || tab) {
                    // Build the expected path
                    let expectedPath = `/chat/${params.tenantId}/${params.projectId}`;
                    if (microservice) {
                        expectedPath += `/${encodeURIComponent(microservice)}`;
                        if (branch) {
                            expectedPath += `/${encodeURIComponent(branch)}`;
                            if (tab) {
                                expectedPath += `/${encodeURIComponent(tab)}`;
                            }
                        }
                    }

                    // Only navigate if the current path is different from expected path
                    // This prevents unnecessary navigation that would clear conversationId
                    if (location.pathname !== expectedPath) {
                        // Preserve conversationId query parameter if it exists
                        const searchParams = createSearchParams(location.search);
                        const conversationIdParam = searchParams.get('conversationId');
                        let finalPath = expectedPath;
                        if (conversationIdParam) {
                            finalPath += `?conversationId=${conversationIdParam}`;
                        }

                        // eslint-disable-next-line no-console
                        console.log('🔗 Reconstructing URL from localStorage:', finalPath);
                        navigate(finalPath, { replace: true });
                    }
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error reconstructing URL from localStorage:', error);
        }
    }, [params.tenantId, params.projectId, params.microservice, navigate, location.pathname]);

    // Listen for diff application failures and auto-send correct content to AI
    useEffect(() => {
        const handleDiffApplicationFailed = async (event) => {
            const {filePath, fileName, currentContent, currentLineCount, error} = event.detail;

            // eslint-disable-next-line no-console
            console.warn('🔄 Diff application failed - auto-recovering by sending correct content to AI');
            // eslint-disable-next-line no-console
            console.warn('   File:', filePath);
            // eslint-disable-next-line no-console
            console.warn('   Current lines:', currentLineCount);
            // eslint-disable-next-line no-console
            console.warn('   Error:', error);

            if (!sessionId) {
                // eslint-disable-next-line no-console
                console.warn('⚠️ Cannot auto-recover: no active session');
                return;
            }

            // Send a system message to AI with the correct file content
            const recoveryMessage = `⚠️ DIFF APPLICATION FAILED - Context Mismatch Detected

File: ${filePath}
Current file size: ${currentLineCount} lines
Error: ${error}

The diff you generated couldn't be applied because the file has a different number of lines than expected.
This usually happens when the diff was based on an outdated or incorrect version of the file.

Here is the ACTUAL current content of the file (${currentLineCount} lines):

\`\`\`${fileName?.split('.').pop() || 'text'}
${currentContent}
\`\`\`

IMPORTANT: Base your new diff on the ACTUAL content above (${currentLineCount} lines). 
Double-check that the line numbers in your diff match the actual file line count.`;

            // Add red failure message to chat with clickable file link
            const failureMessage = Messages.createErrorMessage(
                `Failed to apply changes to [${fileName}](file://${filePath})\n\n` +
                `Error: ${error}\n\n` +
                `Click the filename to view the current file content. I'll regenerate the changes based on the actual file content.`
            );

            setMessages(prevMessages => Messages.addMessage(prevMessages, failureMessage));

            // Send the recovery message to AI (but don't show it to user)
            try {
                await AdkClient.sendMessage(
                    sessionId,
                    recoveryMessage,
                    null, // No abort signal
                    canvasProjectContext,
                    true, // isCanvasMode
                    aiMode // Pass AI mode
                );
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('❌ Failed to send recovery message to AI:', err);
            }
        };

        window.addEventListener('diffApplicationFailed', handleDiffApplicationFailed);
        return () => {
            window.removeEventListener('diffApplicationFailed', handleDiffApplicationFailed);
        };
    }, [sessionId, canvasProjectContext, setMessages]);


    return (
        <>
            <div
                className={`main-content canvas-active ${isFullscreen ? 'fullscreen-mode' : ''}`}
                style={{
                    width: hideCanvas ? '100%' : (isFullscreen ? '100vw' : `${chatSidebarWidth}px`),
                    zIndex: isFullscreen ? 9999999999 : 'auto'
                }}
            >
                {/* Toast Notification */}
                {toast && (
                    <Toast
                        message={toast.message}
                        onClose={hideToast}
                        type={toast.type}
                    />
                )}

                {/* URL Validation Error - Missing Tenant */}
                {urlValidationError && urlValidationError.type === 'missing_tenant' && (
                    <div className="chat-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div style={{
                        maxWidth: '600px',
                        padding: '40px',
                        textAlign: 'center',
                        background: 'var(--background-secondary)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            margin: '0 auto 24px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px'
                        }}>
                            ⚠️
                        </div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: 'var(--text-primary)'
                        }}>
                            Tenant Required
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--text-secondary)',
                            marginBottom: '32px',
                            lineHeight: '1.6'
                        }}>
                            {urlValidationError.message}<br/>
                            Please contact your administrator to be added to a tenant.
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                padding: '12px 32px',
                                fontSize: '16px',
                                fontWeight: '500',
                                color: 'white',
                                background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                            }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            {urlValidationError.action}
                        </button>
                    </div>
                </div>
            )}

            {/* Main Chat Content - Always show except for critical tenant error */}
            {(!urlValidationError || urlValidationError.type !== 'missing_tenant') && (
                <div className="chat-container">
                    {/* Project Header - Always show, displays project name or create prompt */}
                    {isCanvasMode && (
                        <div className="chat-project-header">
                            {hideCanvas ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    <h2
                                        className="chat-project-name"
                                        style={{padding: '0 8px', cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => navigate('/')}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
                                    >{'Chat'}</h2>
                                    {messages.length > 0 && (
                                        <button
                                            title="Transfer this conversation to a Code project"
                                            onClick={async () => {
                                                setShowComposeToCodePicker(true);
                                                if (tenantProjects.length === 0) {
                                                    const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
                                                    if (tenantId) {
                                                        try {
                                                            setIsLoadingProjects(true);
                                                            const projectsData = await miaPlatformDataService.getProjects(tenantId);
                                                            setTenantProjects(projectsData.data || []);
                                                        } catch (err) {
                                                            console.error('Failed to load projects:', err);
                                                        } finally {
                                                            setIsLoadingProjects(false);
                                                        }
                                                    }
                                                }
                                            }}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '5px 12px',
                                                fontSize: '13px',
                                                fontWeight: '500',
                                                color: 'var(--text-primary)',
                                                background: 'var(--background-tertiary)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                transition: 'background 0.15s',
                                                whiteSpace: 'nowrap',
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.background = 'var(--background-hover, var(--border-color))'}
                                            onMouseOut={(e) => e.currentTarget.style.background = 'var(--background-tertiary)'}
                                            type="button"
                                        >
                                            <svg fill="none" height="13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="13"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                                            {'Open in Code'}
                                        </button>
                                    )}
                                </div>
                            ) : (
                            <button
                                className="chat-project-header-button"
                                onClick={() => {
                                    const dropdown = document.getElementById('project-dropdown');
                                    if (dropdown) {
                                        dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                    }
                                    // Close tools dropdown if open
                                    setIsToolsModalOpen(false);
                                }}
                                onBlur={(e) => {
                                    // Delay to allow click on dropdown items
                                    setTimeout(() => {
                                        const dropdown = document.getElementById('project-dropdown');
                                        if (dropdown && e.currentTarget && !e.currentTarget.contains(document.activeElement)) {
                                            dropdown.style.display = 'none';
                                        }
                                    }, 200);
                                }}
                            >
                                <div className="chat-project-logo">
                                    <svg width="32" height="32" viewBox="0 0 50 50" fill="none">
                                        <defs>
                                            <linearGradient id="miaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#5B9FD5"/>
                                                <stop offset="50%" stopColor="#60a5fa"/>
                                                <stop offset="100%" stopColor="#7BB3E0"/>
                                            </linearGradient>
                                        </defs>
                                        <path d="M 8 25 C 8 25, 8 15, 13 10 C 18 5, 23 5, 28 10 C 33 15, 33 25, 33 25 C 33 25, 28 28, 23 25 C 18 22, 18 15, 23 12 C 28 9, 33 12, 38 18 C 43 24, 43 32, 43 32"
                                              fill="none"
                                              stroke="url(#miaGradient)"
                                              strokeWidth="6"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <h2 className="chat-project-name">
                                    {canvasProjectContext && canvasProjectContext.project
                                        ? canvasProjectContext.project.name
                                        : '➕ Create a new project'}
                                </h2>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{marginLeft: '8px'}}>
                                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            )}

                            {/* Tools Dropdown Button Container */}
                            <div style={{position: 'relative', display: 'inline-block'}}>
                                <button
                                    className="chat-tools-button"
                                    onClick={() => {
                                        if (!isToolsModalOpen) {
                                            toolsModalOpenRef.current = [...selectedTools];
                                        }
                                        setIsToolsModalOpen(prev => !prev);
                                        // Close project dropdown if open
                                        const projectDropdown = document.getElementById('project-dropdown');
                                        if (projectDropdown) {
                                            projectDropdown.style.display = 'none';
                                        }
                                    }}
                                    title={customAIInstructionsPlaybookData && playbookLocksTools ? "Some tools are locked by the playbook's agents" : "View available tools"}
                                >
                                    <Icon name="tools" size={16} />
                                    {customAIInstructionsPlaybookData && playbookLocksTools && (
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{marginLeft: '4px', opacity: 0.7}} title="Locked by agent">
                                            <path d="M18 8h-1V6A5 5 0 0 0 7 6v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm3.1-9H8.9V6a3.1 3.1 0 0 1 6.2 0v2z"/>
                                        </svg>
                                    )}
                                    <span style={{marginLeft: '8px', fontSize: '14px', fontWeight: '500'}}>
                                        {availableTools.length > 0 ? `Tools (${(selectedTools || []).length}/${availableTools.length})` : 'Tools'}
                                        {(selectedTools || []).length > MAX_SUGGESTED_TOOLS && (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{marginLeft: '6px'}} title="Warning: Many tools selected may impact performance">
                                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                <line x1="12" y1="9" x2="12" y2="13" />
                                                <line x1="12" y1="17" x2="12.01" y2="17" />
                                            </svg>
                                        )}
                                    </span>
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style={{marginLeft: '6px'}}>
                                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>

                            <div id="project-dropdown" className="project-dropdown" style={{display: 'none'}}>
                                <button
                                    className="dropdown-item"
                                    onClick={() => {
                                        document.getElementById('project-dropdown').style.display = 'none';
                                        navigate('/');
                                        localStorage.removeItem('mia_selectedProject');
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M7 3l-5 5 5 5M2 8h12" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Go Back Home</span>
                                </button>
                                <div className="dropdown-divider"></div>

                                <button
                                    className="dropdown-item"
                                    onClick={() => {
                                        document.getElementById('project-dropdown').style.display = 'none';
                                        navigate('/projects');
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M2 3h5a1 1 0 011 1v5a1 1 0 01-1 1H2V3zM9 3h5v7H9V3zM2 11h12v2H2v-2z" stroke="currentColor" strokeWidth="0.5" fill="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Browse Projects</span>
                                </button>

                                 <div className="dropdown-divider"></div>
                                  <button
                                      className="dropdown-item"
                                      onClick={() => {
                                          document.getElementById('project-dropdown').style.display = 'none';
                                          if (window.miaFlowCreateProject) {
                                              window.miaFlowCreateProject();
                                          } else {
                                              navigate('/projects');
                                          }
                                      }}
                                  >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                          <rect x="4" y="4" width="8" height="8" stroke="currentColor" strokeWidth="1.5" fill="none" rx="1"/>
                                          <path d="M8 6v4M6 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                      </svg>
                                      <span>Create Project</span>
                                  </button>
                            </div>


                            </div> {/* End Tools Dropdown Button Container */}

                            {/* Tools Modal - rendered via portal to escape stacking context */}
                            {isToolsModalOpen && createPortal(
                                <div className="ai-agents-settings-modal-overlay" onClick={() => { setIsToolsModalOpen(false); setToolSearchQuery(''); }}>
                                <div className="project-dropdown tools-dropdown" style={{
                                    background: 'var(--bg-primary)',
                                    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6), 0 8px 16px rgba(0, 0, 0, 0.3)',
                                    border: '2px solid var(--border)',
                                    backdropFilter: 'blur(10px)',
                                    position: 'relative',
                                    transform: 'none',
                                    top: 'auto',
                                    left: 'auto'
                                }} onClick={(e) => e.stopPropagation()}>
                                {toolsLoadError ? (
                                    <div style={{padding: '16px', textAlign: 'center'}}>
                                        <div style={{color: 'var(--error)', fontSize: '14px', marginBottom: '8px'}}>
                                            {toolsAuthRequired ? '🔐 Authentication Required' : '⚠️ Failed to Load Tools'}
                                        </div>
                                        <div style={{color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '12px'}}>
                                            {toolsLoadError}
                                        </div>
                                        {toolsAuthRequired && (
                                            <button
                                                onClick={() => {
                                                    setIsToolsModalOpen(false);
                                                    navigate('/tools');
                                                }}
                                                style={{
                                                    padding: '8px 16px',
                                                    fontSize: '13px',
                                                    background: 'var(--primary)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Configure Authentication
                                            </button>
                                        )}
                                    </div>
                                ) : availableTools.length === 0 ? (
                                    <div style={{padding: '12px', color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'center'}}>
                                        Loading tools...
                                    </div>
                                ) : (
                                    <>
                                        {/* Search Bar */}
                                        <div style={{padding: '12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)'}}>
                                            <input
                                                type="text"
                                                placeholder="Search tools..."
                                                value={toolSearchQuery}
                                                onChange={(e) => setToolSearchQuery(e.target.value)}
                                                style={{
                                                    width: '95%',
                                                    padding: '8px 12px',
                                                    fontSize: '13px',
                                                    background: 'var(--bg-primary)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: '6px',
                                                    color: 'var(--text-primary)',
                                                    outline: 'none'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                                                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                                            />
                                        </div>

                                        {/* Agent Tools Lock Notice */}
                                        {customAIInstructionsPlaybookData && playbookLocksTools && (
                                            <div style={{
                                                padding: '10px 12px',
                                                background: 'rgba(99, 102, 241, 0.08)',
                                                border: '1px solid rgba(99, 102, 241, 0.3)',
                                                borderLeft: '4px solid #6366f1',
                                                margin: '12px',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#6366f1" style={{flexShrink: 0}}>
                                                    <path d="M18 8h-1V6A5 5 0 0 0 7 6v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm3.1-9H8.9V6a3.1 3.1 0 0 1 6.2 0v2z"/>
                                                </svg>
                                                <span style={{fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4'}}>
                                                    Tools selected by the playbook's agents are locked and cannot be deselected. You can still enable or disable other tools.
                                                </span>
                                            </div>
                                        )}

                                        {/* Tool Change Notice */}
                                        {showToolChangeNotice && (
                                            <div style={{
                                                padding: '12px',
                                                background: 'rgba(255, 193, 7, 0.1)',
                                                border: '1px solid rgba(255, 193, 7, 0.3)',
                                                borderLeft: '4px solid #ffc107',
                                                margin: '12px',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px'
                                            }}>
                                                <span style={{fontSize: '16px', marginTop: '2px'}}>⚠️</span>
                                                <div style={{flex: 1}}>
                                                    <div style={{fontSize: '13px', fontWeight: '600', color: '#ffc107', marginBottom: '4px'}}>
                                                        Tool Selection Changed
                                                    </div>
                                                    <div style={{fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5'}}>
                                                        Start a new chat session to use the updated tool selection. The current session will continue using the original tools.
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Too Many Tools Warning */}
                                        {(selectedTools || []).length > MAX_SUGGESTED_TOOLS && (
                                            <div style={{
                                                padding: '12px',
                                                background: 'rgba(255, 152, 0, 0.1)',
                                                border: '1px solid rgba(255, 152, 0, 0.3)',
                                                borderLeft: '4px solid #ff9800',
                                                margin: '12px',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px'
                                            }}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="#ff9800" strokeWidth="2" width="16" height="16" style={{marginTop: '2px', flexShrink: 0}}>
                                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                                    <line x1="12" y1="9" x2="12" y2="13" />
                                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                                </svg>
                                                <div style={{flex: 1}}>
                                                    <div style={{fontSize: '13px', fontWeight: '600', color: '#ff9800', marginBottom: '4px'}}>
                                                        Performance Warning
                                                    </div>
                                                    <div style={{fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5'}}>
                                                        You have selected {(selectedTools || []).length} tools. Selecting more than 90 tools may impact response time and token usage. Consider selecting only the tools you need.
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Header */}
                                        <div style={{padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                            <div style={{fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                Available Tools ({availableTools.filter(tool =>
                                                    !toolSearchQuery ||
                                                    tool.name.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                                                    tool.description.toLowerCase().includes(toolSearchQuery.toLowerCase()) ||
                                                    (tool.owner && tool.owner.toLowerCase().includes(toolSearchQuery.toLowerCase()))
                                                ).length})
                                            </div>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                                <button
                                                    onClick={() => {
                                                        const filteredTools = availableTools.filter(tool => {
                                                            if (!toolSearchQuery) return true;
                                                            const query = toolSearchQuery.toLowerCase();
                                                            return tool.name.toLowerCase().includes(query) ||
                                                                   tool.description.toLowerCase().includes(query) ||
                                                                   (tool.owner && tool.owner.toLowerCase().includes(query));
                                                        });
                                                        // Filtra solo i tool abilitati
                                                        const enabledFilteredTools = filteredTools.filter(tool => tool.enabled !== false);
                                                        const filteredToolIds = enabledFilteredTools.map(t => t.id);
                                                        const current = selectedTools || [];
                                                        // If playbook active, locked = tools explicitly set by the playbook
                                                        const lockedIds = (customAIInstructionsPlaybookData && playbookLocksTools)
                                                            ? filteredToolIds.filter(id => playbookToolIdsRef.current.includes(id))
                                                            : [];
                                                        const unlockableIds = filteredToolIds.filter(id => !lockedIds.includes(id));
                                                        const allSelected = unlockableIds.length > 0 && unlockableIds.every(id => current.includes(id));
                                                        if (allSelected) {
                                                            // Deselect all unlockable filtered tools
                                                            setSelectedTools(current.filter(id => !unlockableIds.includes(id)));
                                                        } else {
                                                            // Select all filtered tools
                                                            const newSelected = [...new Set([...current, ...filteredToolIds])];
                                                            setSelectedTools(newSelected);
                                                        }
                                                    }}
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: '11px',
                                                        fontWeight: '600',
                                                        background: 'var(--primary)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        transition: 'opacity 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                >
                                                    {(() => {
                                                        const filteredTools = availableTools.filter(tool => {
                                                            if (!toolSearchQuery) return true;
                                                            const query = toolSearchQuery.toLowerCase();
                                                            return tool.name.toLowerCase().includes(query) ||
                                                                   tool.description.toLowerCase().includes(query) ||
                                                                   (tool.owner && tool.owner.toLowerCase().includes(query));
                                                        });
                                                        // Filtra solo i tool abilitati
                                                        const enabledFilteredTools = filteredTools.filter(tool => tool.enabled !== false);
                                                        const filteredToolIds = enabledFilteredTools.map(t => t.id);
                                                        const current = selectedTools || [];
                                                        const lockedIds = (customAIInstructionsPlaybookData && playbookLocksTools)
                                                            ? filteredToolIds.filter(id => playbookToolIdsRef.current.includes(id))
                                                            : [];
                                                        const unlockableIds = filteredToolIds.filter(id => !lockedIds.includes(id));
                                                        return unlockableIds.length > 0 && unlockableIds.every(id => current.includes(id)) ? 'Deselect All' : 'Select All';
                                                    })()}
                                                </button>
                                                <div style={{fontSize: '11px', color: 'var(--text-secondary)'}}>
                                                    {(selectedTools || []).length} selected
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tools List */}
                                        <div style={{maxHeight: '450px', overflowY: 'auto'}}>
                                            {(() => {
                                                // Filter tools by search query; hide built-in tools from other applications
                                                const filteredTools = availableTools.filter(tool => {
                                                    if (tool.type === 'built-in' && !isMiaFlowTool(tool)) return false;
                                                    if (!toolSearchQuery) return true;
                                                    const query = toolSearchQuery.toLowerCase();
                                                    return tool.name.toLowerCase().includes(query) ||
                                                           tool.description.toLowerCase().includes(query) ||
                                                           (tool.owner && tool.owner.toLowerCase().includes(query));
                                                });

                                                // Group filtered tools by category; MCP server tools get a label derived from their server name
                                                const formatMcpServerName = (name) =>
                                                    name.split('-')
                                                        .map(w => w.toLowerCase() === 'mcp' ? 'MCP' : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                                        .join(' ');

                                                const toolsByCategory = filteredTools.reduce((acc, tool) => {
                                                    let category;
                                                    if (tool.type === 'mcp-server') {
                                                        category = tool.mcpServer?.name ? formatMcpServerName(tool.mcpServer.name) : 'Other';
                                                    } else {
                                                        category = tool.ownerDescription || tool.category || 'Other';
                                                    }
                                                    if (!acc[category]) acc[category] = [];
                                                    acc[category].push(tool);
                                                    return acc;
                                                }, {});

                                                // Sort categories: Mia Flow Tools first, then alphabetically
                                                const sortedCategories = Object.entries(toolsByCategory).sort(([catA], [catB]) => {
                                                    if (catA === 'Mia Flow Tools') return -1;
                                                    if (catB === 'Mia Flow Tools') return 1;
                                                    return catA.localeCompare(catB);
                                                });

                                                return sortedCategories.map(([category, tools], catIndex) => {
                                                    // Filtra solo i tool abilitati per il calcolo dello stato della categoria
                                                    const enabledTools = tools.filter(t => t.enabled !== false);
                                                    const categoryToolIds = enabledTools.map(t => t.id);
                                                    const current = selectedTools || [];
                                                    const allSelected = categoryToolIds.length > 0 && categoryToolIds.every(id => current.includes(id));
                                                    const someSelected = categoryToolIds.some(id => current.includes(id)) && !allSelected;
                                                    const isExpanded = expandedCategories[category] || false;
                                                    const allToolsDisabled = enabledTools.length === 0;

                                                    return (
                                                    <div key={catIndex}>
                                                        {/* Category Header with checkbox and expand/collapse */}
                                                        <div
                                                            style={{
                                                                padding: '8px 12px',
                                                                background: 'var(--bg-tertiary)',
                                                                borderBottom: '1px solid var(--border)',
                                                                position: 'sticky',
                                                                top: 0,
                                                                zIndex: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                cursor: allToolsDisabled ? 'not-allowed' : 'pointer',
                                                                transition: 'background-color 0.2s',
                                                                opacity: allToolsDisabled ? 0.5 : 1
                                                            }}
                                                            onMouseEnter={(e) => !allToolsDisabled && (e.currentTarget.style.background = 'var(--shade-medium)')}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                                        >
                                                            {/* Expand/Collapse icon */}
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 16 16"
                                                                fill="currentColor"
                                                                style={{
                                                                    marginRight: '6px',
                                                                    cursor: 'pointer',
                                                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                    transition: 'transform 0.2s',
                                                                    flexShrink: 0
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleCategoryExpansion(category);
                                                                }}
                                                            >
                                                                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                                            </svg>

                                                            <input
                                                                type="checkbox"
                                                                checked={allSelected}
                                                                disabled={allToolsDisabled}
                                                                ref={input => {
                                                                    if (input) input.indeterminate = someSelected;
                                                                }}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    if (!allToolsDisabled) {
                                                                        handleCategorySelection(category, tools);
                                                                    }
                                                                }}
                                                                style={{
                                                                    marginRight: '8px',
                                                                    cursor: allToolsDisabled ? 'not-allowed' : 'pointer',
                                                                    width: '14px',
                                                                    height: '14px'
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <div
                                                                style={{
                                                                    fontSize: '11px',
                                                                    fontWeight: '700',
                                                                    color: 'var(--text-secondary)',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px'
                                                                }}
                                                                onClick={() => toggleCategoryExpansion(category)}
                                                            >
                                                                <span>{category} ({tools.length})</span>
                                                                {allToolsDisabled && (
                                                                    <span style={{
                                                                        fontSize: '9px',
                                                                        padding: '2px 6px',
                                                                        borderRadius: '3px',
                                                                        background: 'rgba(239, 68, 68, 0.15)',
                                                                        color: '#ef4444',
                                                                        fontWeight: '600',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.3px'
                                                                    }}>
                                                                        Disabled
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Tools in this category - only show if expanded */}
                                                        {isExpanded && tools.map((tool, toolIndex) => {
                                                            const toolId = tool.id;
                                                            const isSelected = (tool.enabled !== false) && (selectedTools || []).includes(toolId);
                                                            const isPlaybookLocked = !!(customAIInstructionsPlaybookData && playbookLocksTools && playbookToolIdsRef.current.includes(toolId));
                                                            const isEnabled = tool.enabled !== false && !isPlaybookLocked;
                                                            return (
                                                            <div
                                                                key={toolIndex}
                                                                className="dropdown-item"
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                    padding: '8px 12px 8px 24px',
                                                                    cursor: isEnabled ? 'pointer' : 'not-allowed',
                                                                    borderBottom: '1px solid var(--border-subtle)',
                                                                    background: isSelected ? 'var(--shade-medium)' : 'transparent',
                                                                    transition: 'background-color 0.2s',
                                                                    width: '90%',
                                                                    opacity: tool.enabled !== false ? 1 : 0.4
                                                                }}
                                                                onClick={(e) => {
                                                                    // Only handle clicks on the container itself, not on the checkbox
                                                                    if (e.target.tagName !== 'INPUT' && isEnabled) {
                                                                        e.stopPropagation();
                                                                        handleToolSelection(tool, isEnabled);
                                                                    }
                                                                }}
                                                            >
                                                                <div style={{display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, flexWrap: 'wrap', gap: '4px'}}>
                                                                    {isPlaybookLocked ? (
                                                                        // Custom indicator when locked by playbook — native disabled checkbox has poor
                                                                        // contrast in dark mode; this keeps selected state clearly visible.
                                                                        <span style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            width: '13px',
                                                                            height: '13px',
                                                                            flexShrink: 0,
                                                                            marginRight: '8px',
                                                                            borderRadius: '2px',
                                                                            border: isSelected ? '1.5px solid #6366f1' : '1.5px solid var(--text-secondary)',
                                                                            background: isSelected ? '#6366f1' : 'transparent',
                                                                            opacity: isSelected ? 1 : 0.4,
                                                                        }}>
                                                                            {isSelected && (
                                                                                <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                                                    <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                                                </svg>
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            disabled={!isEnabled}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                if (isEnabled) {
                                                                                    handleToolSelection(tool, isEnabled);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                marginRight: '8px',
                                                                                cursor: isEnabled ? 'pointer' : 'not-allowed',
                                                                                width: '13px',
                                                                                height: '13px',
                                                                                flexShrink: 0
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style={{marginRight: '8px', flexShrink: 0, opacity: 0.6}}>
                                                                        <circle cx="8" cy="8" r="2" fill="currentColor"/>
                                                                    </svg>
                                                                    <span style={{fontWeight: '500', fontSize: '13px', color: 'var(--text-primary)', wordBreak: 'break-word'}}>
                                                                        {getToolFriendlyName(tool.name)}
                                                                    </span>
                                                                    {!isEnabled && (
                                                                        <span style={{
                                                                            fontSize: '9px',
                                                                            padding: '2px 6px',
                                                                            borderRadius: '3px',
                                                                            background: isPlaybookLocked ? 'rgba(99, 102, 241, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                                                            color: isPlaybookLocked ? '#6366f1' : '#ef4444',
                                                                            fontWeight: '600',
                                                                            textTransform: 'uppercase',
                                                                            letterSpacing: '0.3px'
                                                                        }}>
                                                                            {isPlaybookLocked ? 'Enabled by Playbook' : 'Disabled'}
                                                                        </span>
                                                                    )}
                                                                    <span style={{fontWeight: '400', fontSize: '10px', color: 'var(--text-secondary)', opacity: 0.7, fontFamily: 'monospace', wordBreak: 'break-all'}}>
                                                                        {tool.name}
                                                                    </span>
                                                                </div>
                                                                {tool.description && (
                                                                    <div style={{
                                                                        marginTop: '3px',
                                                                        marginLeft: '33px',
                                                                        fontSize: '11px',
                                                                        color: 'var(--text-secondary)',
                                                                        lineHeight: '1.4',
                                                                        opacity: 0.8,
                                                                        wordWrap: 'break-word',
                                                                        overflowWrap: 'break-word',
                                                                        whiteSpace: 'normal'
                                                                    }}>
                                                                        {tool.description.length > 200
                                                                            ? tool.description.substring(0, 200) + '...'
                                                                            : tool.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );})}
                                                    </div>
                                                );})
                                            })()}
                                        </div>

                                        {/* Confirmation Footer */}
                                        <div style={{
                                            padding: '12px',
                                            borderTop: '1px solid var(--palette-common-grey-300, #d1d5db)',
                                            background: 'var(--palette-background-neutral-50, #f8f9fa)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}>
                                            <div style={{fontSize: '12px', color: 'var(--palette-text-secondary, #6b7280)', fontWeight: '500'}}>
                                                {(selectedTools || []).length} tool{(selectedTools || []).length !== 1 ? 's' : ''} selected
                                            </div>
                                            <div style={{display: 'flex', gap: '8px'}}>
                                                <button
                                                    onClick={() => {
                                                        // Restore original selection
                                                        setSelectedTools(toolsModalOpenRef.current || []);
                                                        setIsToolsModalOpen(false);
                                                        setToolSearchQuery('');
                                                    }}
                                                    style={{
                                                        padding: '8px 24px',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        background: 'transparent',
                                                        color: 'var(--text-primary)',
                                                        border: '1px solid var(--border)',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.background = 'var(--shade-light)'}
                                                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        // Check if tools have changed
                                                        const initialTools = toolsModalOpenRef.current || [];
                                                        const currentTools = selectedTools || [];
                                                        const hasChanged = initialTools.length !== currentTools.length ||
                                                            !initialTools.every(tool => currentTools.includes(tool));

                                                        if (hasChanged) {
                                                            console.log('🔄 Tool selection changed - user should start new session for changes to take effect');
                                                        }

                                                        setIsToolsModalOpen(false);
                                                        setToolSearchQuery('');
                                                    }}
                                                    style={{
                                                        padding: '8px 24px',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        background: 'var(--accent-color, #2563EB)',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.opacity = '0.9';
                                                        e.target.style.background = 'var(--accent-hover, #1D4ED8)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.opacity = '1';
                                                        e.target.style.background = 'var(--accent-color, #2563EB)';
                                                    }}
                                                >
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                                </div>
                            , document.body)}

                            {/* New Conversation Button */}
                            <button
                              className="add-context-button"
                              disabled={isLoading}
                              onClick={onNewConversation}
                              title="Start new conversation"
                              type="button"
                            >
                              <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                <line x1="12" y1="5" x2="12" y2="19"/>
                                <line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                            </button>

                            {/* Show History Button */}
                            <button
                              className="add-context-button"
                              disabled={isLoading}
                              onClick={() => {
                                navigate('/memories');
                              }}
                              title="Show conversation history"
                              type="button"
                            >
                              <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                              </svg>
                            </button>


                    {/* Fullscreen Button */}

                        {!hideCanvas && <button
                          className="add-context-button"
                          disabled={isLoading}
                          onClick={() => setIsFullscreen(!isFullscreen)}
                          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                          type="button"
                          style={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                          }}
                        >
                          {isFullscreen ? (
                            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                            </svg>
                          ) : (
                            <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                            </svg>
                          )}
                        </button>}


                        </div>
                    )}


                    <div className="chat-messages" ref={chatMessagesRef} style={hideCanvas && showComposeToCodePicker ? {padding: 0} : undefined}>
                        {/* Compose → Code project picker rendered as a full page inside the chat area */}
                        {hideCanvas && showComposeToCodePicker ? (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                padding: '40px',
                                gap: '24px',
                                overflowY: 'auto',
                                background: 'var(--bg-primary)',
                            }}>
                                {/* Page header */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                                    <div>
                                        <h2 style={{ fontSize: '22px', fontWeight: '600', margin: 0, color: 'var(--text-primary)' }}>
                                            {'Open in Code'}
                                        </h2>
                                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                                            {'Select a project to continue. Your conversation history and context will be carried over.'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowComposeToCodePicker(false)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '22px', lineHeight: 1, padding: '4px' }}
                                        type="button"
                                        aria-label="Back"
                                    >{'←'}</button>
                                </div>

                                {/* Project grid */}
                                {isLoadingProjects ? (
                                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                                        <p style={{ marginTop: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>{'Loading projects…'}</p>
                                    </div>
                                ) : tenantProjects.length === 0 ? (
                                    <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px 0' }}>{'No projects found for this tenant.'}</p>
                                ) : (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                        gap: '12px',
                                    }}>
                                        {tenantProjects.map(project => {
                                            const pid = project._id || project.projectId;
                                            return (
                                                <button
                                                    key={pid}
                                                    type="button"
                                                    onClick={() => {
                                                        const tenantIdValue = miaFlowAuth.getSelectedTenant()?.tenantId;
                                                        if (!tenantIdValue || !pid) return;
                                                        localStorage.setItem(`chatMessagesBackup_${pid}`, JSON.stringify(messages));
                                                        localStorage.setItem(`chatSessionState_${pid}`, JSON.stringify({
                                                            sessionId: `compose-transfer-${Date.now()}`,
                                                            conversationId: null,
                                                            timestamp: Date.now(),
                                                            wasStreaming: false,
                                                        }));
                                                        setShowComposeToCodePicker(false);
                                                        navigate(`/chat/${tenantIdValue}/${pid}/project-configuration/main/code`);
                                                    }}
                                                    style={{
                                                        padding: '16px',
                                                        textAlign: 'left',
                                                        background: 'var(--background-tertiary)',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                                        e.currentTarget.style.borderColor = 'var(--accent-color, #3B82F6)';
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                        e.currentTarget.style.borderColor = 'var(--border-color)';
                                                    }}
                                                >
                                                    <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {project.name || pid}
                                                    </div>
                                                    {project.description && (
                                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                            {project.description}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Footer actions */}
                                <div style={{ display: 'flex', gap: '10px', paddingTop: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => { setShowComposeToCodePicker(false); navigate('/projects'); }}
                                        style={{
                                            padding: '9px 20px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: 'white',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            border: 'none',
                                            borderRadius: '7px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {'➕ Create new project'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowComposeToCodePicker(false)}
                                        style={{
                                            padding: '9px 20px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: 'var(--text-secondary)',
                                            background: 'transparent',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '7px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {'← Back to chat'}
                                    </button>
                                </div>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="empty-state">
                                <h1 className="empty-state-title">{`Hello, ${user?.name?.split(' ')[0] || 'there'}!`}</h1>
                                <div className="prompt-suggestions">
                                    {isSuggestionsLoading ? (
                                        /* Skeleton placeholders while AI generates suggestions */
                                        [0, 1, 2].map((i) => (
                                            <div key={i} className="prompt-card prompt-card-skeleton" style={{opacity: 0.5, pointerEvents: 'none'}}>
                                                <span className="prompt-icon">{'⋯'}</span>
                                                <span style={{background: 'var(--border-color)', borderRadius: '4px', height: '1em', display: 'inline-block', width: `${60 + i * 10}%`}} />
                                            </div>
                                        ))
                                    ) : (promptSuggestions || (hideCanvas ? [
                                        {icon: '🏗️', text: 'Create a new project from a marketplace template and deploy it to the dev environment'},
                                        {icon: '🔗', text: 'Add a CRUD collection called "orders" to the current project, expose its endpoints via the API Gateway, and save the configuration'},
                                        {icon: '📊', text: 'List the running pods for the current project and show the logs for any failing services'},
                                    ] : [
                                        {icon: '🧠', text: 'Read the open file and explain what each function does, then list any code smells or improvement opportunities'},
                                        {icon: '🔒', text: 'Read all files in the canvas, identify OWASP security issues, and apply the fixes directly in the affected files'},
                                        {icon: '🧪', text: 'Read the open file and generate a complete test suite that covers all functions and their edge cases'},
                                    ])).map((s, i) => (
                                        <button
                                            key={i}
                                            className="prompt-card"
                                            onClick={() => setInputValue(s.text)}
                                            type="button"
                                        >
                                            <span className="prompt-icon">{s.icon}</span>
                                            {s.text}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="chat-messages-container">
                                {messages.map((message) => {
                                    let cssClasses = `message ${message.type}`;
                                    if (message.isFunctionCall) {
                                        cssClasses += ' function-call';
                                    }

                                    return (
                                        <div className={cssClasses} key={message.id}>
                                            {/* Show reasoning status for messages that have reasoning steps (intermediate or complete) */}
                                            {!message.isFunctionCall && message.reasoningSteps && message.reasoningSteps.length > 0 && (
                                                <ReasoningStatus
                                                    currentStep={message.currentReasoningStep ?? message.reasoningSteps.length - 1}
                                                    isActive={message.isIntermediate === true}
                                                    reasoningSteps={message.reasoningSteps}
                                                />
                                            )}

                                            <MemoizedMessageContent
                                                contentType={message.contentType}
                                                onChoice={handleSendDirectMessage}
                                                rawStructuredText={message.rawStructuredText}
                                                structuredContent={message.structuredContent}
                                                text={message.text}
                                            />

                                            {/* Browser Stream Viewer - Show live browser automation */}
                                            {message.browserSessionId && (
                                                <BrowserStreamViewer
                                                    sessionId={message.browserSessionId}
                                                    initialQuality={message.browserQuality || 'medium'}
                                                    onClose={() => {
                                                        // Update message to hide browser viewer
                                                        setMessages(prevMessages =>
                                                            prevMessages.map(m =>
                                                                m.id === message.id
                                                                    ? { ...m, browserSessionId: null }
                                                                    : m
                                                            )
                                                        );
                                                    }}
                                                />
                                            )}

                                            {/* MCP App View - Render interactive UI if enabled and present */}
                                            {enableMcpApps && message.mcpApp && message.mcpApp.resourceUri && (
                                                <MCPAppView
                                                    hostContext={{
                                                        theme: document.documentElement.getAttribute('data-theme') || 'light',
                                                        locale: 'en',
                                                        projectId: canvasProjectContext?.projectId,
                                                        serviceName: canvasProjectContext?.selectedMicroservice?.name,
                                                        branch: canvasProjectContext?.selectedBranch
                                                    }}
                                                    onCallTool={async (toolName, toolArgs) => {
                                                        // Execute tool and return result
                                                        // This would need integration with the AI agent system
                                                        console.log('MCP App requesting tool execution:', toolName, toolArgs);
                                                        return { success: true, message: 'Tool execution not yet implemented' };
                                                    }}
                                                    onOpenLink={(url) => {
                                                        window.open(url, '_blank', 'noopener,noreferrer');
                                                    }}
                                                    onRequestMode={(mode) => {
                                                        console.log('MCP App requesting display mode:', mode);
                                                    }}
                                                    onSendMessage={(msgText) => {
                                                        handleSendDirectMessage(msgText);
                                                    }}
                                                    onUpdateContext={(updates) => {
                                                        console.log('MCP App context updates:', updates);
                                                    }}
                                                    resourceUri={message.mcpApp.resourceUri}
                                                    toolInput={message.mcpApp.toolInput}
                                                    toolResult={message.mcpApp.toolResult}
                                                />
                                            )}

                                            {/* Save Prompt Button - Only show for user messages */}
                                            {message.type === 'sent' && message.text && (
                                                <button
                                                    className="save-prompt-button"
                                                    onClick={() => handleOpenSavePrompt(message.text)}
                                                    title="Save this prompt to marketplace"
                                                    type="button"
                                                >
                                                    <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                                        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                                                        <polyline points="17 21 17 13 7 13 7 21"/>
                                                        <polyline points="7 3 7 8 15 8"/>
                                                    </svg>
                                                </button>
                                            )}

                                            {/* Save Message Button - Only show for AI assistant messages */}
                                            {message.type === 'received' && message.text && !message.isFunctionCall && (
                                                <button
                                                    className="save-message-button"
                                                    onClick={() => handleOpenSaveMessage(message.text)}
                                                    title="Save this message to marketplace"
                                                    type="button"
                                                >
                                                    <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
                                                        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                                                        <polyline points="17 21 17 13 7 13 7 21"/>
                                                        <polyline points="7 3 7 8 15 8"/>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                                {conversationStatus && (
                                    <div className="conversation-status">
                                        <div className="status-icon-container">
                                            <svg className="status-spinner" height="20" viewBox="0 0 24 24" width="20">
                                                <polygon points="12,2 20.66,7 20.66,17 12,22 3.34,17 3.34,7" fill="currentColor"/>
                                            </svg>
                                        </div>
                                        <span className="status-text">{conversationStatus}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                {/* Chat Input at Bottom */}
                <div
                    className={`chat-input-container ${isDragOver ? 'drag-over' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <div className="chat-input-wrapper" style={{position: 'relative'}}>
                        {/* Spec-Kit slash command autocomplete */}
                        {showSlashMenu && (
                            <SlashCommandMenu
                                commands={filterSpeckitCommands(inputValue, catalogSpecCommands.length ? catalogSpecCommands : undefined)}
                                onClose={() => setShowSlashMenu(false)}
                                onSelect={handleSlashCommandSelect}
                                prefix={inputValue}
                                selectedIndex={slashMenuIndex}
                            />
                        )}
                        {/* File Attachments Preview */}
                        {attachedFiles.length > 0 && (
                            <div className="attached-files-preview">
                                {attachedFiles.map((file, index) => (
                                    <div className="attached-file-item" key={index}>
                                        <span className="file-icon">
                                            {file.isImage ? '🖼️' : '📄'}
                                        </span>
                                        <div className="file-info">
                                            <span className="file-name">{file.name}</span>
                                            <span className="file-size">{formatFileSize(file.size)}</span>
                                        </div>
                                        <button
                                            className="remove-file-button"
                                            onClick={() => handleRemoveFile(index)}
                                            title="Remove file"
                                            type="button"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <input
                            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/markdown,text/csv,text/html,text/xml,text/css,text/javascript,application/json,application/xml,.md,.yaml,.yml,.toml,.ini,.env,.jsx,.ts,.tsx,.mjs,.py,.rb,.php,.java,.kt,.swift,.go,.rs,.c,.cpp,.h,.cs,.sh,.sql,.graphql,.proto,.tf,.log,.diff,.docx,.xlsx,.pptx"
                            multiple
                            onChange={handleFileSelect}
                            ref={fileInputRef}
                            style={{display: 'none'}}
                            type="file"
                        />

                        <div className="chat-input">
                            <textarea
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder={isLoading ? 'Type to steer, or press Stop to cancel...' : 'Start the Flow ...'}
                                ref={inputRef}
                                value={inputValue}
                                className="message-input"
                                rows={1}
                            />
                        </div>

                        {/* Input Controls Bar */}
                        <div className="input-controls-bar">
                            <div className="input-controls-left">
                                {!customAIInstructionsPlaybookData && (
                                    <>
                                        {/* AI Mode Dropdown (Agent/Plan) */}
                                        <AIModeDropdown
                                            aiMode={aiMode}
                                            onModeChange={setAiMode}
                                            isOpen={isAiModeDropdownOpen}
                                            onToggle={setIsAiModeDropdownOpen}
                                            isLoading={isLoading}
                                        />

                                        {/* Model Dropdown */}
                                        <ModelDropdown
                                            model={selectedModel}
                                            onModelChange={setSelectedModel}
                                            isOpen={isModelDropdownOpen}
                                            onToggle={setIsModelDropdownOpen}
                                            isLoading={isLoading}
                                        />
                                    </>
                                )}

                                {/* Add Context Button */}
                                <button
                                    className="add-context-button"
                                    disabled={isLoading}
                                    onClick={handleAttachClick}
                                    title="Add Context - Attach images or PDFs"
                                    type="button"
                                >
                                    <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                                    </svg>
                                </button>

                                {/* Active Instruction Indicator */}
                                {customAIInstructions && (
                                    <button
                                        type="button"
                                        onClick={handleOpenAIInstructions}
                                        title={customAIInstructionsName ? `Active: ${customAIInstructionsName}` : "Custom AI Playbook Active"}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '0 10px',
                                            height: '36px',
                                            borderRadius: '10px',
                                            backgroundColor: 'rgba(94, 92, 230, 0.1)',
                                            border: '1px solid var(--mia-purple-primary)',
                                            color: 'var(--text-primary)',
                                            cursor: 'pointer',
                                            marginRight: '8px',
                                            fontSize: '13px',
                                            transition: 'all 0.2s ease',
                                            maxWidth: '200px'
                                        }}
                                        className="active-instruction-btn"
                                    >
                                        <svg fill="none" height="14" stroke="var(--mia-purple-primary)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                            <circle cx="12" cy="12" r="10"/>
                                            <path d="M12 16v-4M12 8h.01"/>
                                        </svg>
                                        <span style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontWeight: 500
                                        }}>
                                            {customAIInstructionsName || 'Instructions'}
                                        </span>
                                    </button>
                                )}

                                {/* More Options Dropdown */}
                                <div className="more-options-dropdown-container" style={{position: 'relative', display: 'inline-block'}}>
                                    <button
                                        className={`add-context-button ${isMoreOptionsOpen ? 'active' : ''}`}
                                        disabled={isLoading}
                                        onClick={() => setIsMoreOptionsOpen(!isMoreOptionsOpen)}
                                        title="More options"
                                        type="button"
                                    >
                                        <svg fill="currentColor" height="16" viewBox="0 0 24 24" width="16">
                                            <circle cx="12" cy="5" r="2"/>
                                            <circle cx="12" cy="12" r="2"/>
                                            <circle cx="12" cy="19" r="2"/>
                                        </svg>
                                    </button>

                                    {isMoreOptionsOpen && (
                                        <div className="more-options-menu" style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: '0',
                                            marginBottom: '8px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            border: '1px solid var(--border)',
                                            borderRadius: '8px',
                                            padding: '4px',
                                            minWidth: '200px',
                                            zIndex: 10000,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                        }}>
                                            {/* AI Instructions Button */}
                                            <button
                                                className={`dropdown-item ${customAIInstructions ? 'active' : ''}`}
                                                disabled={isLoading}
                                                onClick={() => {
                                                    handleOpenAIInstructions();
                                                    setIsMoreOptionsOpen(false);
                                                }}
                                                title={customAIInstructions ? `AI Playbook Active: ${customAIInstructions.substring(0, 100)}...` : "Add AI Playbook"}
                                                type="button"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    color: 'var(--text-primary)',
                                                    cursor: 'pointer',
                                                    gap: '8px',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                                    <circle cx="12" cy="12" r="10"/>
                                                    <path d="M12 16v-4M12 8h.01"/>
                                                </svg>
                                                <span style={{flex: 1}}>
                                                    AI Playbooks
                                                    {customAIInstructionsName && (
                                                        <small style={{display: 'block', fontSize: '10px', opacity: 0.8}}>
                                                            {customAIInstructionsName}
                                                        </small>
                                                    )}
                                                </span>
                                                {customAIInstructions && <span style={{color: 'var(--primary)'}}>✓</span>}
                                            </button>

                                            {/* Export to PDF Button */}
                                            {messages.length > 0 && !isLoading && (
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        exportConversationToPDF(messages, convName || 'Conversation');
                                                        setIsMoreOptionsOpen(false);
                                                    }}
                                                    title="Export conversation to PDF"
                                                    type="button"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        textAlign: 'left',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: 'var(--text-primary)',
                                                        cursor: 'pointer',
                                                        gap: '8px',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    <svg fill="none" height="14" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-15" />
                                                        <polyline points="7 10 12 15 17 10" />
                                                        <line x1="12" x2="12" y1="15" y2="3" />
                                                    </svg>
                                                    <span>Export to PDF</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="input-controls-right">
                                {/* Mobile AI Mode Selector */}
                                {!customAIInstructionsPlaybookData && (
                                    <MobileAIMode
                                        aiMode={aiMode}
                                        onModeChange={setAiMode}
                                        isOpen={isAiModeDropdownOpen}
                                        onToggle={setIsAiModeDropdownOpen}
                                    />
                                )}

                                {/* Queue badge */}
                                {messageQueue.length > 0 && (
                                    <span className="queue-badge" title={`${messageQueue.length} message${messageQueue.length > 1 ? 's' : ''} queued`}>
                                        {messageQueue.length}
                                    </span>
                                )}

                                {isLoading ? (
                                    <div className="agent-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {/* Stop */}
                                        <button
                                            className="agent-ctrl-btn agent-ctrl-stop"
                                            onClick={handleStopGeneration}
                                            title="Stop generation"
                                            type="button"
                                        >
                                            <svg height="14" viewBox="0 0 24 24" width="14">
                                                <rect fill="currentColor" height="10" width="10" x="7" y="7"/>
                                            </svg>
                                        </button>
                                        {/* Queue */}
                                        <button
                                            className="agent-ctrl-btn agent-ctrl-queue"
                                            disabled={!(typeof inputValue === 'string' && inputValue.trim())}
                                            onClick={handleAddToQueue}
                                            title="Add to queue (⌥↵)"
                                            type="button"
                                        >
                                            <svg fill="none" height="14" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14">
                                                <line x1="12" x2="12" y1="5" y2="19"/>
                                                <line x1="5" x2="19" y1="12" y2="12"/>
                                            </svg>
                                        </button>
                                        {/* Steer / Send */}
                                        <button
                                            className="agent-ctrl-btn agent-ctrl-steer"
                                            disabled={!(typeof inputValue === 'string' && inputValue.trim())}
                                            onClick={handleSteerWithMessage}
                                            title="Steer with message (↵)"
                                            type="button"
                                        >
                                            <svg fill="currentColor" height="14" viewBox="0 0 24 24" width="14">
                                                <path d="M7 10l5 5 5-5z" transform="rotate(-90 12 12)"/>
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    (typeof inputValue === 'string' && inputValue.trim()) && (
                                        <div className="send-options-container" style={{ position: 'relative' }}>
                                            {showSendPromptBanner && (
                                                <>
                                                    <svg className="banner-connection-svg">
                                                        <defs>
                                                            <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                                <stop offset="0%" stopColor="#667eea" stopOpacity="0.7"/>
                                                                <stop offset="100%" stopColor="#764ba2" stopOpacity="0.7"/>
                                                            </linearGradient>
                                                        </defs>
                                                        <path
                                                            d="M calc(100vw - 90px) 55 Q calc(100vw - 200px) calc(100vh - 200px) calc(100vw - var(--chat-sidebar-width, 400px) + 330px) calc(100vh - 80px)"
                                                            stroke="url(#connectionGradient)"
                                                            strokeWidth="3"
                                                            fill="none"
                                                            className="banner-connection-path"
                                                        />
                                                    </svg>
                                                    <div
                                                        id="start-mia-flow-from-home"
                                                        className="send-prompt-banner"
                                                    >
                                                        <span>
                                                            Click here to start building!
                                                        </span>
                                                        <span className="send-prompt-banner-emoji">
                                                            👇
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            <button
                                                className={`send-button-icon ${showSendPromptBanner ? 'send-button-highlighted' : ''}`}
                                                disabled={!(typeof inputValue === 'string' && inputValue.trim())}
                                                onClick={() => {
                                                    setShowSendPromptBanner(false);
                                                    handleSendMessage();
                                                }}
                                                title="Send message (Enter)"
                                                type="button"
                                            >
                                                <svg fill="currentColor" height="24" viewBox="0 0 24 24" width="24">
                                                    <path d="M7 10l5 5 5-5z" transform="rotate(-90 12 12)"/>
                                                </svg>
                                            </button>
                                        </div>
                                    )
                                )}
                        </div>
                    </div>
                </div>
                </div>
                </div>
            )}

            {/* AI Instructions Modal */}
            <AIInstructionsModal
                isOpen={isAIInstructionsModalOpen}
                onClose={() => setIsAIInstructionsModalOpen(false)}
                onApply={handleApplyAIInstructions}
                currentInstructions={customAIInstructions}
                currentPlaybookData={customAIInstructionsPlaybookData}
                tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
            />

            {/* Save Prompt Modal */}
            <SavePromptModal
                isOpen={isSavePromptModalOpen}
                onClose={() => {
                    setIsSavePromptModalOpen(false);
                    // Don't clear inputValue here as it might be the current input
                }}
                onSave={handleSavePrompt}
                promptText={inputValue}
                tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
            />

            {/* Save Message Modal */}
            <SaveMessageModal
                isOpen={isSaveMessageModalOpen}
                onClose={() => {
                    setIsSaveMessageModalOpen(false);
                    setMessageToSave('');
                }}
                onSave={handleSaveMessage}
                messageText={messageToSave}
                tenantId={canvasProjectContext?.tenant?.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId || null}
                projectId={canvasProjectContext?.projectId || null}
                serviceName={canvasProjectContext?.selectedMicroservice?.name || null}
            />

            {/* Conversation History Modal */}
            <ConversationHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={() => setIsHistoryModalOpen(false)}
                currentConversationId={sessionId}
                onNewChat={() => {
                    window.dispatchEvent(new Event('forceNewChat'));
                    navigate('/chat');
                }}
            />

            {/* Script Execution Confirmation Modal */}
            <Modal
                isOpen={scriptExecutionModal.isOpen}
                onClose={scriptExecutionModal.onCancel || (() => setScriptExecutionModal({ isOpen: false }))}
                title={scriptExecutionModal.title}
                message={scriptExecutionModal.message}
                type={scriptExecutionModal.type || 'confirm'}
                confirmText={scriptExecutionModal.confirmText}
                cancelText={scriptExecutionModal.cancelText}
                onConfirm={scriptExecutionModal.onConfirm}
                onCancel={scriptExecutionModal.onCancel}
                showCancel={scriptExecutionModal.showCancel}
                isDangerous={scriptExecutionModal.isDangerous}
                isLoading={scriptExecutionModal.isLoading}
            >
                {scriptExecutionModal.children}
            </Modal>

            {/* Resize Handle */}
            {!hideCanvas && <div
                ref={resizeHandleRef}
                className="chat-resize-handle"
                style={{ left: `${chatSidebarWidth}px` }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    resizeHandleRef.current.dataset.dragging = 'true';
                    document.body.style.cursor = 'ew-resize';
                    document.body.style.userSelect = 'none';
                }}
            />}

            {/* Project Selector on Right Side - When projectId is missing */}
            {!urlValidationError && showProjectSelector && (
                <div style={{
                    position: 'fixed',
                    left: `${chatSidebarWidth}px`,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    background: 'var(--background-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                }}>
                    <div style={{
                        maxWidth: '800px',
                        width: '100%',
                        padding: '40px',
                        background: 'var(--background-secondary)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                margin: '0 auto 24px',
                                background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '32px'
                            }}>
                                📁
                            </div>
                            <h2 style={{
                                fontSize: '24px',
                                fontWeight: '600',
                                marginBottom: '16px',
                                color: 'var(--text-primary)'
                            }}>
                                Select a Project
                            </h2>
                            <p style={{
                                fontSize: '16px',
                                color: 'var(--text-secondary)',
                                marginBottom: '32px',
                                lineHeight: '1.6'
                            }}>
                                Choose an existing project to work with, or create a new one.
                            </p>
                        </div>

                        {isLoadingProjects ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                                <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading projects...</p>
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: '16px',
                                    marginBottom: '24px',
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}>
                                    {tenantProjects.map(project => (
                                        <button
                                            key={project._id || project.projectId}
                                            onClick={() => {
                                                const projectIdValue = project._id || project.projectId;
                                                const tenantIdValue = miaFlowAuth.getSelectedTenant()?.tenantId;
                                                if (tenantIdValue) {
                                                    // Store project in auth
                                                    miaFlowAuth.setSelectedProject({
                                                        projectId: projectIdValue,
                                                        name: project.name,
                                                        description: project.description
                                                    });
                                                    // Navigate with path parameters
                                                    navigate(`/chat/${tenantIdValue}/${projectIdValue}`, { replace: true });
                                                    setShowProjectSelector(false);
                                                }
                                            }}
                                            style={{
                                                padding: '20px',
                                                textAlign: 'left',
                                                background: 'var(--background-tertiary)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <h3 style={{
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: 'var(--text-primary)'
                                            }}>
                                                {project.name}
                                            </h3>
                                            {project.description && (
                                                <p style={{
                                                    fontSize: '14px',
                                                    color: 'var(--text-secondary)',
                                                    lineHeight: '1.4',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}>
                                                    {project.description}
                                                </p>
                                            )}
                                        </button>
                                    ))}
                                </div>

                                <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    justifyContent: 'center',
                                    paddingTop: '24px',
                                    borderTop: '1px solid var(--border-color)'
                                }}>
                                    <button
                                        onClick={() => setIsInlineCreateProjectModalOpen(true)}
                                        style={{
                                            padding: '12px 32px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            color: 'white',
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s',
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        ➕ Create New Project
                                    </button>
                                    <button
                                        onClick={() => navigate('/')}
                                        style={{
                                            padding: '12px 32px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            color: 'var(--text-primary)',
                                            background: 'var(--background-tertiary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s',
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Inline Create Project Modal - opened from project selector when playbookId is in URL */}
            <CreateProjectModal
                isOpen={isInlineCreateProjectModalOpen}
                onClose={() => setIsInlineCreateProjectModalOpen(false)}
                onCreateProject={async (projectData) => {
                    const result = await miaPlatformDataService.createProject(projectData);
                    const tenantId = projectData.tenantId;
                    const projectId = result.project?._id;
                    setIsInlineCreateProjectModalOpen(false);
                    setShowProjectSelector(false);
                    if (tenantId && projectId) {
                        const searchParams = new URLSearchParams(location.search);
                        const playbookId = searchParams.get('playbookId');
                        const targetUrl = playbookId
                            ? `/chat/${tenantId}/${projectId}?playbookId=${playbookId}`
                            : `/chat/${tenantId}/${projectId}`;
                        navigate(targetUrl);
                    }
                }}
                selectedTenant={miaFlowAuth.getSelectedTenant()}
            />

            {/* URL Validation Error (Project Not Found) - Show on right side */}
            {urlValidationError && urlValidationError.type !== 'missing_tenant' && (
                <div style={{
                    position: 'fixed',
                    left: `${chatSidebarWidth}px`,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    background: 'var(--background-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1
                }}>
                    <div style={{
                        maxWidth: '600px',
                        padding: '40px',
                        textAlign: 'center',
                        background: 'var(--background-secondary)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            margin: '0 auto 24px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px'
                        }}>
                            🔍
                        </div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: 'var(--text-primary)'
                        }}>
                            Project Not Found
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--text-secondary)',
                            marginBottom: '32px',
                            lineHeight: '1.6'
                        }}>
                            {urlValidationError.message}
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={() => {
                                    setUrlValidationError(null);
                                    setShowProjectSelector(true);
                                }}
                                style={{
                                    padding: '12px 32px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    color: 'white',
                                    background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                Choose Another Project
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                style={{
                                    padding: '12px 32px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    color: 'var(--text-primary)',
                                    background: 'var(--background-tertiary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s',
                                }}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                Go to Home
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Compose → Code picker – full page, replaces chat area */}

            {/* Canvas Component - Show on right side when project is selected */}
            {!hideCanvas && !urlValidationError && !showProjectSelector && (
                <Canvas
                    content={canvasContent}
                    isVisible={true}
                    chatSidebarWidth={chatSidebarWidth}
                    projectContext={canvasProjectContext}
                    onNavigateHome={() => navigate('/')}
                    onClose={() => {
                        // Canvas is always on, but we can navigate home on close
                        navigate('/');
                    }}
                   onProjectContextChange={handleProjectContextChange}
                   ref={canvasRef}
                   initialMicroservice={params.microservice}
                   initialBranch={params.branch}
                   initialTab={params.tab}
                   onSelectionChange={syncUrlWithCanvasSelections}
                   onNewConversation={onNewConversation}
                   onShowHistory={() => navigate('/memories')}
                   onAskAI={(prompt) => {
                       setInputValue(prompt);
                       // Focus on input after a short delay
                       setTimeout(() => {
                           const input = document.querySelector('.chat-input');
                           if (input) {
                               input.focus();
                           }
                       }, 100);
                   }}
                />
            )}

            {/* Mobile Canvas Drawer - Provides mobile access to Canvas features */}
            <MobileCanvasDrawer
                isOpen={isMobileCanvasDrawerOpen}
                onClose={() => setIsMobileCanvasDrawerOpen(false)}
            />

            {/* Mobile Code Page - Full-screen mobile coding experience */}
            <MobileCodePage
                isOpen={isMobileCodePageOpen}
                onClose={() => setIsMobileCodePageOpen(false)}
                onAskAI={(prompt) => {
                    setInputValue(prompt);
                    setIsMobileCodePageOpen(false);
                    // Focus on input after a short delay
                    setTimeout(() => {
                        const input = document.querySelector('.chat-input');
                        if (input) {
                            input.focus();
                        }
                    }, 300);
                }}
            />

            {/* Mobile Preview Page - Full-screen mobile preview experience */}
            <MobilePreviewPage
                isOpen={isMobilePreviewPageOpen}
                onClose={() => setIsMobilePreviewPageOpen(false)}
                onAskAI={(prompt) => {
                    setInputValue(prompt);
                    setIsMobilePreviewPageOpen(false);
                    // Focus on input after a short delay
                    setTimeout(() => {
                        const input = document.querySelector('.chat-input');
                        if (input) {
                            input.focus();
                        }
                    }, 300);
                }}
            />

            {/* Project Creation Modal - Shows progress during project/service creation */}
            <ProjectCreationModal
                isOpen={isCreationModalOpen}
                projectName={creationProjectName}
                serviceName={creationServiceName}
                step={creationStep}
                mode={creationMode}
            />

            {/* Mobile Navigation */}
            <MobileNav
                onNewChatPlan={() => {
                    setAiMode('plan');
                    setMobileActiveView('chat');
                    navigate('/', {replace: true});
                }}
                onNewChatAgent={() => {
                    setAiMode('agent');
                    setMobileActiveView('chat');
                    navigate('/', {replace: true});
                }}
                onNewChat={() => {
                    // Keep current mode, start new chat (same as desktop New Chat button)
                    setMobileActiveView('chat');
                    window.dispatchEvent(new Event('forceNewChat'));
                    navigate('/', {replace: true});
                }}
                onOpenCode={() => {
                    setMobileActiveView('code');
                    setIsMobileCodePageOpen(true);
                }}
                onOpenPreview={() => {
                    setMobileActiveView('preview');
                    setIsMobilePreviewPageOpen(true);
                }}
                activeMode={aiMode}
                activeView={mobileActiveView}
            />
            </div>
        </>
    );
}

// Main App component with routing
function App() {
    const location = useLocation();

    // Determine if animations should be disabled (on pages other than login and home)
    const shouldDisableAnimations = useMemo(() => {
        const path = location.pathname;
        // Enable animations only on login, register, and home pages
        return path !== '/login' && path !== '/register' && path !== '/';
    }, [location.pathname]);

    return (
        <div className={shouldDisableAnimations ? 'disable-animations' : ''}>
            {/* Side-effect component: redirects to /bff/login when no active session */}
            <RedirectToLogin />
            <Routes>
                {/* Public Routes - accessible without authentication */}
                <Route element={<Login />} path="/login" />
                <Route element={<Register />} path="/register" />
                <Route element={<AuthCallback />} path="/auth/callback" />
                <Route element={<AuthCallback />} path="/auth/github/callback" />
                <Route element={<AuthCallback />} path="/auth/mia-platform/callback" />
                <Route element={<AuthCallback />} path="/auth/generic-mcp/:serverId/callback" />

                {/* Browser Stream Viewer Standalone - can be opened in separate window */}
                <Route element={<BrowserStreamViewerStandalone />} path="/browser-stream-viewer" />

                {/* Collaboration invite route */}
                <Route element={<JoinCollaboration />} path="/join/:token" />

                {/* Protected Routes - require authentication */}
                <Route element={<MainApp />} path="/*" />
            </Routes>
        </div>
    );
}

// App wrapper with Sidebar - Protected content only
function MainApp() {
    const {isAuthenticated, isLoading, user, authorizedFetch} = useAuth();
    // Selected authz organization (populates x-mia-acl-context) — the catalog scope.
    const {selectedOrganization} = useSelectedOrganization();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    // Memoize searchParams to prevent unnecessary re-renders
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const conversationId = searchParams.get('conversationId');

    // Extract tenant ID from URL if present (e.g., /b933f1ef-5b8e-4adf-a346-24a3b03d13e8)
    const tenantIdFromUrl = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        // Check if first segment looks like a UUID (tenant ID)
        if (pathSegments.length > 0) {
            const firstSegment = pathSegments[0];
            // UUID regex pattern
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            // Known routes that should not be treated as tenant IDs
            const knownRoutes = ['chat', 'compose', 'playbook', 'projects', 'catalog', 'templates', 'learn', 'news', 'discover', 'tools', 'settings', 'auth', 'join', 'browser-stream-viewer', 'memories'];
            if (uuidPattern.test(firstSegment) && !knownRoutes.includes(firstSegment)) {
                return firstSegment;
            }
        }
        return null;
    }, [location.pathname]);

    const [isSidebarVisible, setIsSidebarVisible] = useState(() => {
        // Load sidebar state from localStorage, default to true
        return localStorage.getItem('sidebarVisible') !== 'false';
    });

    // Navigation sidebar visibility state
    const [isNavSidebarVisible, setIsNavSidebarVisible] = useState(true);
    const [isNavSidebarCollapsed, setIsNavSidebarCollapsed] = useState(() => {
        // Load nav sidebar collapsed state from localStorage, default to false
        return localStorage.getItem('navSidebarCollapsed') === 'true';
    });

    const [isCanvasActive, setIsCanvasActive] = useState(false);

    // Tenant management state
    const [tenants, setTenants] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState(() => {
        // Load from MiaFlowAuth
        return miaFlowAuth.getSelectedTenant();
    });
    const [isLoadingTenants, setIsLoadingTenants] = useState(true);
    // Tenant switch transition overlay
    const [isTenantSwitching, setIsTenantSwitching] = useState(false);
    const [switchingToTenantName, setSwitchingToTenantName] = useState('');

    // Playbooks state — sidebar quick-launch items
    const [sidebarPlaybooks, setSidebarPlaybooks] = useState([]);

    // Git connection state — true if any git provider is connected
    const [hasGitConnection, setHasGitConnection] = useState(false);

    // Onboarding state
    const [showOnboarding, setShowOnboarding] = useState(() => {
        return localStorage.getItem('onboardingCompleted') !== 'true';
    });

    // Project creation modal state
    const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
    const [creationProjectName, setCreationProjectName] = useState('');
    const [creationServiceName, setCreationServiceName] = useState('');
    const [creationStep, setCreationStep] = useState(0); // 0=hidden, 1-5=steps
    const [creationMode, setCreationMode] = useState('code'); // 'code' | 'brainstorming'
    const [isMobilePreviewPageOpen, setIsMobilePreviewPageOpen] = useState(false);
    const [mobileActiveView, setMobileActiveView] = useState('chat');
    const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false); // Global search modal state

    // Listen for canvas toggle events to hide sidebar when canvas is open
    useEffect(() => {
        const handleCanvasToggle = (event) => {
            setIsCanvasActive(event.detail.isActive);
        };

        window.addEventListener('canvasToggled', handleCanvasToggle);
        return () => {
            window.removeEventListener('canvasToggled', handleCanvasToggle);
        };
    }, []);

    // Initialize analytics on mount
    useEffect(() => {
        analyticsService.initialize();
        analyticsService.trackPageView('Main App');

        // Expose analytics globally for components that can't use hooks
        window.analytics = {
          trackLanguage: (language, properties) => analyticsService.trackLanguage(language, properties),
          trackProject: (action, properties) => analyticsService.trackProject(action, properties),
          trackAction: (category, action, properties) => analyticsService.trackAction(category, action, properties),
          trackFile: (action, properties) => analyticsService.trackFile(action, properties),
          trackCodeGeneration: (language, properties) => analyticsService.trackCodeGeneration(language, properties),
          trackAIInteraction: (interactionType, properties) => analyticsService.trackAIInteraction(interactionType, properties),
          trackDeployment: (deployType, properties) => analyticsService.trackDeployment(deployType, properties)
        };

        return () => {
            // Cleanup on unmount
            analyticsService.endSession();
        };
    }, []);

    // Track page navigation
    useEffect(() => {
        const pageName = location.pathname === '/' ? 'home' : location.pathname.slice(1);
        analyticsService.trackPageView(pageName);
    }, [location.pathname]);

    // Show sidebar when on home page or projects page, keep hidden state when on chat page
    useEffect(() => {
        if (location.pathname === '/' || location.pathname === '/projects' || location.pathname === '/templates' || location.pathname === '/tools' || location.pathname === '/compose' || location.pathname === '/discover' || location.pathname.startsWith('/settings')) {
            setIsNavSidebarVisible(true);
            setIsCanvasActive(false); // Reset canvas active state when navigating to home
        }
        // Hide sidebar immediately when navigating to any /chat route (canvas mode)
        if (location.pathname.startsWith('/chat')) {
            setIsNavSidebarVisible(false);
        }
        // Hide sidebar when canvas is active
        if (isCanvasActive) {
            setIsNavSidebarVisible(false);
        }
    }, [location.pathname, isCanvasActive]);

    // Fetch available tenants on mount (only when authenticated)
    useEffect(() => {
        // Skip if still loading auth or not authenticated
        if (isLoading || !isAuthenticated) {
            return;
        }

        const fetchTenants = async () => {
            try {
                setIsLoadingTenants(true);
                const tenantsData = await miaPlatformDataService.getTenants();

                // Format tenants to match expected structure
                const tenantList = tenantsData.map(tenant =>
                    miaPlatformDataService.formatTenant(tenant)
                );
                setTenants(tenantList);

                // Priority order for tenant selection:
                // 1. Tenant from URL (if present)
                // 2. Previously selected tenant (from localStorage)
                // 3. First tenant in the list

                let tenantToSelect = null;

                // Check if URL contains a tenant ID
                if (tenantIdFromUrl) {
                    const urlTenant = tenantList.find(t =>
                        t.tenantId === tenantIdFromUrl || t.id === tenantIdFromUrl
                    );
                    if (urlTenant) {
                        tenantToSelect = urlTenant;
                        console.log('Pre-selecting tenant from URL:', urlTenant.name || urlTenant.tenantId);
                    } else {
                        console.warn('Tenant ID from URL not found in tenant list:', tenantIdFromUrl);
                    }
                }

                // If no tenant from URL, check for previously selected tenant
                if (!tenantToSelect && selectedTenant) {
                    const tenantExists = tenantList.find(t => t.tenantId === selectedTenant.tenantId);
                    if (tenantExists) {
                        tenantToSelect = tenantExists;
                    }
                }

                // If still no tenant, select the first one
                if (!tenantToSelect && tenantList.length > 0) {
                    tenantToSelect = tenantList[0];
                }

                // Apply the selected tenant
                if (tenantToSelect) {
                    setSelectedTenant(tenantToSelect);
                    miaFlowAuth.setSelectedTenant(tenantToSelect);
                    localStorage.setItem('currentTenantId', tenantToSelect.tenantId);
                }
            } catch (error) {
                console.error('Error fetching tenants:', error);
            } finally {
                setIsLoadingTenants(false);
            }
        };

        fetchTenants();
    }, [isLoading, isAuthenticated, tenantIdFromUrl]); // Wait for auth to complete

    // Check git provider connection on mount
    useEffect(() => {
        if (!isAuthenticated || isLoading) return;
        Promise.all([
            GitLabAuth.isAuthenticated(),
            GitHubAuth.isAuthenticated(),
            BitbucketAuth.isAuthenticated(),
            AzureDevOpsAuth.isAuthenticated(),
        ])
            .then((results) => setHasGitConnection(results.some(Boolean)))
            .catch(() => setHasGitConnection(false));
    }, [isAuthenticated, isLoading]);

    // Re-check git connection whenever the Connectors page reports a status change
    useEffect(() => {
        if (!isAuthenticated || isLoading) return;
        const handleGitConnectionChanged = () => {
            Promise.all([
                GitLabAuth.isAuthenticated(),
                GitHubAuth.isAuthenticated(),
                BitbucketAuth.isAuthenticated(),
                AzureDevOpsAuth.isAuthenticated(),
            ])
                .then((results) => setHasGitConnection(results.some(Boolean)))
                .catch(() => {});
        };
        window.addEventListener('gitConnectionChanged', handleGitConnectionChanged);
        return () => window.removeEventListener('gitConnectionChanged', handleGitConnectionChanged);
    }, [isAuthenticated, isLoading]);

    // Load playbooks for sidebar whenever the selected authz org changes.
    // Scope comes from x-mia-acl-context (via getBffHeaders), not a tenantId query param.
    useEffect(() => {
        if (!isAuthenticated) return;
        const baseUrl = (typeof config !== 'undefined' && config.bffUrl) || window.location.origin;
        const qs = new URLSearchParams({ page: '1', perPage: '100' });
        fetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, { headers: getBffHeaders() })
            .then(r => r.json())
            .then(result => {
                if (result.success && result.data) setSidebarPlaybooks(result.data.items || []);
            })
            .catch(() => {});
    }, [isAuthenticated, selectedOrganization?.tenant]);

    // Refresh sidebar playbooks when the Playbook catalog signals a change
    useEffect(() => {
        const refresh = () => {
            const baseUrl = (typeof config !== 'undefined' && config.bffUrl) || window.location.origin;
            const qs = new URLSearchParams({ page: '1', perPage: '100' });
            fetch(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${qs}`, { headers: getBffHeaders() })
                .then(r => r.json())
                .then(result => { if (result.success && result.data) setSidebarPlaybooks(result.data.items || []); })
                .catch(() => {});
        };
        window.addEventListener('mia-flow-playbooks-changed', refresh);
        return () => window.removeEventListener('mia-flow-playbooks-changed', refresh);
    }, []);

    // Effect to handle global search keyboard shortcut (⌘K or Ctrl+K)
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsGlobalSearchOpen(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Handler for tenant selection
    const handleTenantChange = useCallback((tenant) => {
        // Show animated transition overlay
        setSwitchingToTenantName(tenant.name || tenant.tenantId || '');
        setIsTenantSwitching(true);

        setSelectedTenant(tenant);
        miaFlowAuth.setSelectedTenant(tenant);
        localStorage.setItem('currentTenantId', tenant.tenantId);

        // Show notification
        window.dispatchEvent(new CustomEvent('show-toast', {
            detail: {
                message: `Switched to ${tenant.name || tenant.tenantId}`,
                type: 'success'
            }
        }));
    }, []);

    // Handler for toggling navigation sidebar collapse
    const handleToggleNavSidebarCollapse = useCallback(() => {
        setIsNavSidebarCollapsed(prev => {
            const newValue = !prev;
            localStorage.setItem('navSidebarCollapsed', String(newValue));
            return newValue;
        });
    }, []);

    // Listen for onboarding actions (trigger tools, etc.)
    useEffect(() => {
        const handleOnboardingAction = (event) => {
            const {action} = event.detail;

            if (action === 'trigger-tools-menu') {
                const toolsButton = document.querySelector('.tools-button');
                if (toolsButton) {
                    toolsButton.click();
                }
            } else if (action === 'enable-canvas') {
                // Enable canvas mode
                const canvasToggle = document.querySelector('.canvas-toggle');
                if (canvasToggle && !canvasToggle.classList.contains('active')) {
                    canvasToggle.click();
                }
            }
        };

        window.addEventListener('onboarding-action', handleOnboardingAction);
        return () => {
            window.removeEventListener('onboarding-action', handleOnboardingAction);
        };
    }, []);

    // Handle OAuth callback at root path (when redirect_uri is base URL)
    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        // Only process if we have OAuth callback parameters
        if (code && state) {
            // eslint-disable-next-line no-console
            console.log('🔄 [FE] OAuth callback detected at root path:', {
                code: code.substring(0, 20) + '...',
                state,
                full_search: location.search,
                will_redirect_to: `/auth/mia-platform/callback${location.search}`
            });
            // Navigate to dedicated callback handler WITH query parameters
            navigate(`/auth/mia-platform/callback${location.search}`, {replace: true});
        } else if (error) {
            // eslint-disable-next-line no-console
            console.error('❌ [FE] OAuth error received:', error);
            // Still redirect to callback handler to show error
            navigate(`/auth/mia-platform/callback${location.search}`, {replace: true});
        }
    }, [searchParams, navigate, location.search]);

    const handleOnboardingComplete = useCallback(() => {
        setShowOnboarding(false);
        localStorage.setItem('onboardingCompleted', 'true');
    }, []);

    const handleOnboardingSkip = useCallback(() => {
        setShowOnboarding(false);
        localStorage.setItem('onboardingCompleted', 'true');
    }, []);

    const restartOnboardingTour = useCallback(() => {
        localStorage.removeItem('onboardingCompleted');
        localStorage.removeItem('onboarding_completed');
        setShowOnboarding(false);
        // Force remount of OnboardingGuide component
        setTimeout(() => setShowOnboarding(true), 0);
    }, []);

    // Home page handlers
    const handleStartChatFromHome = useCallback(async (prompt, mode = 'code', files = [], fullStackTemplate = null, playbookOptions = {}) => {
        try {
            // If mode is 'code', activate canvas mode and create project first
            if (mode === 'code' && prompt && prompt.trim()) {
                // Get current tenant from selectedTenant state or use first available tenant
                let tenantId = selectedTenant?.tenantId || selectedTenant?.id;

                // If no tenant is selected yet, wait for tenants to load and use the first one
                if (!tenantId && tenants.length > 0) {
                    const firstTenant = tenants[0];
                    tenantId = firstTenant.tenantId || firstTenant.id;
                    setSelectedTenant(firstTenant);
                    miaFlowAuth.setSelectedTenant(firstTenant);
                    console.log('⚡ Auto-selected first tenant:', tenantId);
                }

                if (tenantId) {
                    // Get project name from prompt (first few words)
                    // Step 1: Generate name with AI
                    setCreationProjectName('...');
                    setCreationServiceName('');
                    setCreationStep(1); // Generating name
                    setCreationMode('code');
                    setIsCreationModalOpen(true);

                    let fullProjectName = '';
                    let serviceName = '';
                    let aiGeneratedPrompt = prompt; // Default to user prompt

                    try {
                        // Call BFF to generate AI-powered project name and prompt
                        const nameResult = await miaPlatformDataService.generateProjectName(
                            prompt,
                            null,
                            playbookOptions.playbookId || null,
                            tenantId || null
                        );

                        if (nameResult && nameResult.suggestedName) {
                            fullProjectName = nameResult.suggestedName;
                            // Use AI-generated prompt if available
                            if (nameResult.aiPrompt) {
                                aiGeneratedPrompt = nameResult.aiPrompt;
                                console.log('✅ Using AI-generated prompt:', aiGeneratedPrompt.substring(0, 100) + '...');
                            }
                        } else {
                            // Fallback: generate from prompt only if AI fails
                            const projectName = prompt.split(' ').slice(0, 3).join('-').toLowerCase()
                                .replace(/[^a-z0-9-]/g, '-')
                                .replace(/-+/g, '-')
                                .substring(0, 30);
                            fullProjectName = `miaflow-${projectName}`;
                        }

                        serviceName = `${fullProjectName} App`;
                        setCreationProjectName(fullProjectName);
                        setCreationServiceName(serviceName);

                        // Step 1.5: Preparing AI prompt
                        setCreationStep(1.5);
                        await new Promise(resolve => setTimeout(resolve, 600));

                        // Step 2: Ready to create
                        setCreationStep(2);
                        await new Promise(resolve => setTimeout(resolve, 800));

                        // Step 3: Creating project
                        setCreationStep(3);

                        // Use the tenant's default template (from its project blueprint).
                        // playbookOptions.projectTemplateId overrides the default if provided.
                        let defaultTemplateId = playbookOptions.projectTemplateId || null;
                        if (!defaultTemplateId) {
                            try {
                                const blueprint = await miaPlatformDataService.getProjectBlueprint(tenantId);
                                if (blueprint?.defaultTemplateId) {
                                    defaultTemplateId = blueprint.defaultTemplateId;
                                    console.log('✅ Using tenant default template:', defaultTemplateId);
                                } else {
                                    // Fallback: first available template in the blueprint
                                    const firstTemplate = blueprint?.templates?.[0];
                                    if (firstTemplate) {
                                        defaultTemplateId = firstTemplate.templateId || firstTemplate._id || firstTemplate.id;
                                        console.warn('⚠️ No default template configured, using first available:', defaultTemplateId, '/', firstTemplate.name);
                                    } else {
                                        console.warn('⚠️ No templates found for tenant:', tenantId);
                                    }
                                }
                            } catch (templateError) {
                                console.warn('⚠️ Could not fetch project blueprint, proceeding without templateId:', templateError.message);
                            }
                        } else {
                            console.log('✅ Using playbook project template:', defaultTemplateId);
                        }
                        console.log('🚀 Creating Mia Platform project:', {
                            tenantId,
                            projectName: fullProjectName,
                            templateId: defaultTemplateId
                        });

                        // Create project via BFF using miaPlatformDataService
                        const result = await miaPlatformDataService.createProject({
                            tenantId,
                            projectName: fullProjectName,
                            projectDescription: prompt,
                            templateId: defaultTemplateId
                        });

                        const projectId = result.project?._id || result.project?.id;

                        if (projectId) {
                            // Step 4: Creating frontend service
                            setCreationStep(4);

                            // Create service from Mia Flow React Template
                            try {
                                console.log('🚀 Creating service from Mia Flow React Template...');

                                // Service template configuration - use per-template config when fullStack template selected.
                                // Otherwise leave it empty: the BFF resolves the canonical Mia Flow React
                                // Template (itemId + tenantId + latest version + item _id) dynamically at
                                // request time, so there's nothing to pin here for the default Web App mode.
                                // playbookOptions.sourceTemplateId overrides the default templateItemId if provided
                                const perTemplateConfig = fullStackTemplate && config.fullStackTemplateConfigs?.[fullStackTemplate];
                                const baseServiceTemplateConfig = perTemplateConfig
                                    ? {
                                        templateItemId:   perTemplateConfig.templateItemId   || '',
                                        templateTenantId: perTemplateConfig.templateTenantId || '',
                                        templateVersion:  perTemplateConfig.templateVersion  || '',
                                        templateId:       perTemplateConfig.templateId       || '',
                                    }
                                    : {};
                                const serviceTemplateConfig = playbookOptions.sourceTemplateId
                                    ? { ...baseServiceTemplateConfig, templateItemId: playbookOptions.sourceTemplateId }
                                    : baseServiceTemplateConfig;

                                const serviceResult = await miaPlatformDataService.createServiceFromTemplate({
                                    projectId,
                                    revisionId: config.defaultAppServiceRevisionId || 'main',
                                    serviceName,
                                    serviceDescription: `Service for ${result.project?.name || projectName}`,
                                    ...serviceTemplateConfig
                                });

                                if (serviceResult.success) {
                                    // Step 5: Complete
                                    setCreationStep(5);

                                    // Set up context for preview page
                                    const projectContext = {
                                        id: projectId,
                                        _id: projectId,
                                        name: result.project?.name || fullProjectName,
                                        tenantId: tenantId
                                    };

                                    const revisionContext = {
                                        id: config.defaultAppServiceRevisionId,
                                        name: config.defaultAppServiceRevisionId,
                                        isDefault: true
                                    };

                                    const tenantContext = {
                                        tenantId: tenantId,
                                        id: tenantId,
                                        name: selectedTenant?.name || 'Tenant'
                                    };

                                    // Store in localStorage for MobilePreviewPage
                                    localStorage.setItem('mia_selectedProject', JSON.stringify(projectContext));
                                    localStorage.setItem('mia_selectedRevision', JSON.stringify(revisionContext));
                                    localStorage.setItem('mia_selectedTenant', JSON.stringify(tenantContext));
                                    localStorage.setItem('currentProjectId', projectId);

                                    // Activate canvas mode
                                    localStorage.setItem('canvasMode', 'true');

                                    // Wait a moment then close modal and navigate
                                    setTimeout(() => {
                                        setIsCreationModalOpen(false);

                                        // Store the AI-generated prompt in sessionStorage to be used after git clone
                                        sessionStorage.setItem('mia_pending_initial_prompt', aiGeneratedPrompt);
                                        sessionStorage.setItem('mia_pending_initial_mode', 'code');
                                        // Set flag to indicate this is a new project that needs git pull
                                        sessionStorage.setItem('mia_force_git_pull_on_load', 'true');
                                        if (files && files.length > 0) {
                                            sessionStorage.setItem('mia_pending_initial_files', JSON.stringify(files));
                                        }

                                        // Navigate to chat with full URL including tenant, project, service, revision, and preview
                                        // Format: /chat/{tenantId}/{projectId}/{serviceName}/{revision}/preview
                                        // DON'T pass initialPrompt - wait for git clone to complete first
                                        const chatNavSuffix = playbookOptions.playbookId ? `?playbookId=${encodeURIComponent(playbookOptions.playbookId)}` : '';
                                        navigate(`/chat/${tenantId}/${projectId}/${serviceName}/main/preview${chatNavSuffix}`, {
                                            state: {}
                                        });

                                        // Hide navigation sidebar when entering chat
                                        setIsNavSidebarVisible(false);

                                        // Open preview page after navigation
                                        setTimeout(() => {
                                            setIsMobilePreviewPageOpen(true);
                                            setMobileActiveView('preview');
                                        }, 300);
                                    }, 800);
                                }
                            } catch (serviceError) {
                                console.error('Service creation failed:', serviceError);
                                setIsCreationModalOpen(false);
                                window.dispatchEvent(new CustomEvent('show-toast', {
                                    detail: {
                                        message: '⚠️ Service creation failed',
                                        type: 'error'
                                    }
                                }));
                            }
                        }

                    } catch (createError) {
                        console.error('Project creation failed:', createError);
                        setIsCreationModalOpen(false);
                        window.dispatchEvent(new CustomEvent('show-toast', {
                            detail: {
                                message: '⚠️ Project creation failed',
                                type: 'error'
                            }
                        }));

                        // Still navigate to chat even if project creation fails
                        localStorage.setItem('canvasMode', 'true');
                        navigate('/chat', { state: { initialPrompt: prompt, mode, initialFiles: files } });
                        setIsNavSidebarVisible(false);
                    }
                } else {
                    console.warn('No tenant selected, skipping project creation');
                    // Navigate to chat anyway
                    localStorage.setItem('canvasMode', 'true');
                    navigate('/chat', { state: { initialPrompt: prompt, mode, initialFiles: files } });
                    setIsNavSidebarVisible(false);
                }
            } else if (mode === 'chat' && prompt && prompt.trim()) {
                // Chat mode: navigate directly to /compose, no project creation
                navigate('/compose', { state: { initialPrompt: prompt, mode: 'chat', initialFiles: files } });
                setIsNavSidebarVisible(false);
            } else if (mode === 'brainstorming' && prompt && prompt.trim()) {
                // Brainstorming mode: create project only (no service), then brainstorm using Mia catalog
                let tenantId = selectedTenant?.tenantId || selectedTenant?.id;

                if (!tenantId && tenants.length > 0) {
                    const firstTenant = tenants[0];
                    tenantId = firstTenant.tenantId || firstTenant.id;
                    setSelectedTenant(firstTenant);
                    miaFlowAuth.setSelectedTenant(firstTenant);
                    console.log('⚡ [Brainstorming] Auto-selected first tenant:', tenantId);
                }

                if (tenantId) {
                    setCreationProjectName('...');
                    setCreationServiceName('');
                    setCreationStep(1);
                    setCreationMode('brainstorming');
                    setIsCreationModalOpen(true);

                    let fullProjectName = '';

                    try {
                        const nameResult = await miaPlatformDataService.generateProjectName(prompt, null);

                        if (nameResult && nameResult.suggestedName) {
                            fullProjectName = nameResult.suggestedName;
                        } else {
                            const projectName = prompt.split(' ').slice(0, 3).join('-').toLowerCase()
                                .replace(/[^a-z0-9-]/g, '-')
                                .replace(/-+/g, '-')
                                .substring(0, 30);
                            fullProjectName = `miaflow-${projectName}`;
                        }

                        setCreationProjectName(fullProjectName);

                        setCreationStep(1.5);
                        await new Promise(resolve => setTimeout(resolve, 600));

                        setCreationStep(2);
                        await new Promise(resolve => setTimeout(resolve, 800));

                        setCreationStep(3);

                        let defaultTemplateId = null;
                        try {
                            const blueprint = await miaPlatformDataService.getProjectBlueprint(tenantId);
                            if (blueprint?.defaultTemplateId) {
                                defaultTemplateId = blueprint.defaultTemplateId;
                                console.log('✅ [Brainstorming] Using tenant default template:', defaultTemplateId);
                            } else {
                                // Fallback: first available template in the blueprint
                                const firstTemplate = blueprint?.templates?.[0];
                                if (firstTemplate) {
                                    defaultTemplateId = firstTemplate.templateId || firstTemplate._id || firstTemplate.id;
                                    console.warn('⚠️ [Brainstorming] No default template configured, using first available:', defaultTemplateId);
                                }
                            }
                        } catch (templateError) {
                            console.warn('⚠️ [Brainstorming] Could not fetch project blueprint:', templateError.message);
                        }

                        console.log('🧠 [Brainstorming] Creating project (no service):', { tenantId, projectName: fullProjectName });

                        const result = await miaPlatformDataService.createProject({
                            tenantId,
                            projectName: fullProjectName,
                            projectDescription: prompt,
                            templateId: defaultTemplateId
                        });

                        const projectId = result.project?._id || result.project?.id;

                        if (projectId) {
                            // Complete - step 5 directly (no service creation)
                            setCreationStep(5);

                            const projectContext = {
                                id: projectId,
                                _id: projectId,
                                name: result.project?.name || fullProjectName,
                                tenantId: tenantId
                            };

                            const tenantContext = {
                                tenantId: tenantId,
                                id: tenantId,
                                name: selectedTenant?.name || 'Tenant'
                            };

                            localStorage.setItem('mia_selectedProject', JSON.stringify(projectContext));
                            localStorage.setItem('mia_selectedTenant', JSON.stringify(tenantContext));
                            localStorage.setItem('currentProjectId', projectId);
                            localStorage.setItem('canvasMode', 'true');

                            setTimeout(() => {
                                setIsCreationModalOpen(false);

                                // Store the original user prompt for auto-trigger after config is pulled
                                sessionStorage.setItem('mia_pending_initial_prompt', prompt);
                                sessionStorage.setItem('mia_pending_initial_mode', 'brainstorming');

                                // Navigate to project chat with project-configuration code tab
                                // so Canvas pulls the config repo into IndexedDB before the prompt fires
                                navigate(`/chat/${tenantId}/${projectId}/project-configuration/main/code`);
                                setIsNavSidebarVisible(false);
                            }, 800);
                        }

                    } catch (createError) {
                        console.error('[Brainstorming] Project creation failed:', createError);
                        setIsCreationModalOpen(false);
                        window.dispatchEvent(new CustomEvent('show-toast', {
                            detail: { message: '⚠️ Project creation failed', type: 'error' }
                        }));
                        localStorage.setItem('canvasMode', 'true');
                        navigate('/chat', { state: { initialPrompt: prompt, mode } });
                        setIsNavSidebarVisible(false);
                    }
                } else {
                    console.warn('[Brainstorming] No tenant selected, navigating without project creation');
                    navigate('/chat', { state: { initialPrompt: prompt, mode } });
                    setIsNavSidebarVisible(false);
                }
            } else {
                // For non-code modes, just navigate normally
                navigate('/chat', { state: { initialPrompt: prompt, mode } });
                setIsNavSidebarVisible(false);
            }

        } catch (error) {
            console.error('Error starting chat:', error);
            setIsCreationModalOpen(false);
            window.dispatchEvent(new CustomEvent('show-toast', {
                detail: { message: '❌ Error starting chat', type: 'error' }
            }));
        }
    }, [navigate, selectedTenant, tenants, setIsNavSidebarVisible, setSelectedTenant]);

    const handleImportFromGit = useCallback(async (gitUrl) => {
        try {
            // Show loading toast
            const toastContainer = document.querySelector('.toast-container');
            if (toastContainer) {
                const event = new CustomEvent('show-toast', {
                    detail: { message: '🔄 Importing repository...', type: 'info' }
                });
                window.dispatchEvent(event);
            }

            // Call git import API
            const response = await authorizedFetch(`${config.bffUrl}/api/import-git`, {
                method: 'POST',
                headers: getBffHeaders({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({ gitUrl })
            });

            if (!response.ok) {
                throw new Error('Failed to import repository');
            }

            const data = await response.json();

            // Success - navigate to chat with context
            if (toastContainer) {
                const event = new CustomEvent('show-toast', {
                    detail: { message: '✅ Repository imported successfully!', type: 'success' }
                });
                window.dispatchEvent(event);
            }

            // Navigate to chat
            navigate('/chat');

            // Set project context if projectId returned
            if (data.projectId) {
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('select-project', {
                        detail: { projectId: data.projectId }
                    }));
                }, 100);
            }

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Git import error:', error);
            const event = new CustomEvent('show-toast', {
                detail: { message: `❌ Failed to import: ${error.message}`, type: 'error' }
            });
            window.dispatchEvent(event);
        }
    }, [navigate]);

    const handleModernizeLegacy = useCallback(async (path, options) => {
        try {
            // Show loading toast
            const event = new CustomEvent('show-toast', {
                detail: { message: '🪄 Starting AI modernization...', type: 'info' }
            });
            window.dispatchEvent(event);

            // Call modernization API
            const response = await authorizedFetch(`${config.bffUrl}/api/modernize`, {
                method: 'POST',
                headers: getBffHeaders({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify({ path, options })
            });

            if (!response.ok) {
                throw new Error('Failed to modernize application');
            }

            const data = await response.json();

            // Success
            const successEvent = new CustomEvent('show-toast', {
                detail: { message: '✅ Modernization complete!', type: 'success' }
            });
            window.dispatchEvent(successEvent);

            // Navigate to chat
            navigate('/chat');

            // Set project context if projectId returned
            if (data.projectId) {
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('select-project', {
                        detail: { projectId: data.projectId }
                    }));
                }, 100);
            }

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Modernization error:', error);
            const event = new CustomEvent('show-toast', {
                detail: { message: `❌ Modernization failed: ${error.message}`, type: 'error' }
            });
            window.dispatchEvent(event);
        }
    }, [navigate]);

    const handleSelectProjectFromHome = useCallback((project) => {
        console.log('🎯 handleSelectProjectFromHome called with:', project);

        // Track project visit via API
        const trackProjectVisit = async () => {
            try {
                const baseUrl = config.bffUrl || window.location.origin;
                const userEmail = user?.email || user?.sub || 'demo-user';
                console.log('🔵 [POST recent-projects] Home page - tracking visit:', {
                    url: `${baseUrl}/api/recent-projects`,
                    userEmail,
                    projectId: project._id || project.projectId || project.id
                });
                await authorizedFetch(`${baseUrl}/api/recent-projects`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: getBffHeaders({
                        'Content-Type': 'application/json',
                        'x-user-id': userEmail,
                    }),
                    body: JSON.stringify(project),
                });
                console.log('✅ [POST recent-projects] Home page - visit tracked successfully');
            } catch (error) {
                console.error('❌ [POST recent-projects] Home page - Failed to track project visit:', error);
            }
        };

        trackProjectVisit();

        // Store project in auth BEFORE navigation
        const projectIdValue = project._id || project.projectId || project.id;
        if (!projectIdValue) {
            console.error('❌ No project ID found in project object:', project);
            window.dispatchEvent(new CustomEvent('show-toast', {
                detail: { message: '❌ Invalid project: missing project ID', type: 'error' }
            }));
            return;
        }

        miaFlowAuth.setSelectedProject({
            projectId: projectIdValue,
            name: project.name,
            description: project.description
        });

        // Get tenant ID from project or from auth
        const tenantIdValue = project.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId;

        if (tenantIdValue && projectIdValue) {
            // Clear ALL project-specific localStorage when navigating to different project
            console.log('🧹 Clearing all project-specific localStorage for project switch');
            localStorage.removeItem('mia_selectedResource');
            localStorage.removeItem('mia_selectedRevision');
            localStorage.removeItem('mia_activeTab');
            localStorage.removeItem('mia_fullProjectConfig');
            localStorage.removeItem('mia_selectedSession');

            // Set flag with target projectId to prevent URL reconstruction
            sessionStorage.setItem('mia_switching_to_project', projectIdValue);

            console.log('✅ Navigating to:', `/chat/${tenantIdValue}/${projectIdValue}`);
            navigate(`/chat/${tenantIdValue}/${projectIdValue}`);
        } else {
            console.warn('⚠️ Missing tenant or project ID, using fallback navigation');
            console.log('  tenantId:', tenantIdValue, 'projectId:', projectIdValue);
            // Fallback to old behavior if tenant not available
            navigate('/chat');
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('select-project', {
                    detail: {
                        project,
                        projectId: projectIdValue
                    }
                }));
            }, 100);
        }
    }, [navigate, user]);

    const handleSelectTemplate = useCallback((template) => {
        // Navigate to chat and start with template
        navigate('/chat');
        setTimeout(() => {
            // Send message about template
            const prompt = `Create a new project based on the "${template.name}" template`;
            const inputElement = document.querySelector('.message-input');
            if (inputElement) {
                inputElement.value = prompt;
                const event = new Event('submit', { bubbles: true, cancelable: true });
                inputElement.closest('form')?.dispatchEvent(event);
            }
        }, 100);
    }, [navigate]);

    // Mock projects and templates data (replace with real API calls)
    const [projects, setProjects] = useState([]);
    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        // Load projects from IndexedDB or Mia Platform
        const loadProjects = async () => {
            try {
                // Try to get Mia Platform projects
                // For now, return empty array - integrate with actual Mia Platform API when available
                // The recent projects will be populated from the BFF MongoDB storage
                setProjects([]);

                // Load templates
                setTemplates([
                    {
                        name: 'React SPA Starter',
                        description: 'Modern React single-page application with routing and state management',
                        preview: null,
                        tags: ['React', 'Vite', 'TypeScript']
                    },
                    {
                        name: 'Next.js Blog',
                        description: 'Server-side rendered blog with markdown support',
                        preview: null,
                        tags: ['Next.js', 'MDX', 'Tailwind']
                    },
                    {
                        name: 'Node.js REST API',
                        description: 'Express-based REST API with database integration',
                        preview: null,
                        tags: ['Node.js', 'Express', 'MongoDB']
                    },
                    {
                        name: 'E-commerce Store',
                        description: 'Full-stack e-commerce platform with cart and checkout',
                        preview: null,
                        tags: ['React', 'Node.js', 'Stripe']
                    },
                    {
                        name: 'Dashboard Admin',
                        description: 'Admin dashboard with charts and data tables',
                        preview: null,
                        tags: ['React', 'Charts', 'Material-UI']
                    },
                    {
                        name: 'Landing Page',
                        description: 'Responsive marketing landing page',
                        preview: null,
                        tags: ['HTML', 'CSS', 'JavaScript']
                    }
                ]);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load projects:', error);
            }
        };

        loadProjects();
    }, []);

    const handleResetConversation = () => {
        // Dispatch a custom event to signal intentional reset
        window.dispatchEvent(new Event('forceNewChat'));
        // Remove conversationId from URL while keeping the current path and other params
        if (location.search.includes('conversationId')) {
            const newSearchParams = new URLSearchParams(location.search);
            newSearchParams.delete('conversationId');
            const newSearch = newSearchParams.toString();
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        }
    };

    const handleToggleSidebar = useCallback(() => {
        setIsSidebarVisible(prev => {
            const newValue = !prev;
            localStorage.setItem('sidebarVisible', newValue.toString());
            // Dispatch custom event for same-tab updates
            window.dispatchEvent(new Event('sidebarToggled'));
            return newValue;
        });
    }, []);

    // Sidebar should hide when canvas is active, otherwise respect user's visibility preference
    // Always show sidebar on settings pages
    const isOnSettingsPage = location.pathname.startsWith('/settings');
    const isOnComposePage = location.pathname === '/compose';
    // Hide navigation sidebar when canvas is active (project is open)
    // Always show on Compose page regardless of canvas state
    const shouldShowNavSidebar = isOnComposePage || (!isCanvasActive && isNavSidebarVisible);

    return (
        <div className="app-container">
            {/* Tenant switch loading overlay */}
            {isTenantSwitching && (
                <TenantSwitchOverlay
                    tenantName={switchingToTenantName}
                    onDone={() => setIsTenantSwitching(false)}
                />
            )}

            {/* Left Navigation Sidebar - Lovable style */}
            {shouldShowNavSidebar && (
                <NavigationSidebar
                    currentPath={location.pathname}
                    onNavigate={(path) => navigate(path)}
                    onNewChat={handleResetConversation}
                    tenants={tenants}
                    selectedTenant={selectedTenant}
                    onTenantChange={handleTenantChange}
                    isLoadingTenants={isLoadingTenants}
                    isCollapsed={isNavSidebarCollapsed}
                    onToggleCollapse={handleToggleNavSidebarCollapse}
                    user={user}
                    onOpenSearch={() => setIsGlobalSearchOpen(true)}
                    playbooks={sidebarPlaybooks}
                    hasGitConnection={hasGitConnection}
                />
            )}

            {/* Main content area */}
            <div className="main-content">
                <Routes>
                    {/* All routes in MainApp are protected */}

                    {/* Home page route */}
                    <Route
                        element={
                            <Home
                                onStartChat={handleStartChatFromHome}
                                onSelectProject={handleSelectProjectFromHome}
                                onSelectTemplate={handleSelectTemplate}
                                onNavigate={(path) => navigate(path)}
                                projects={projects}
                                templates={templates}
                                user={user}
                                selectedTenant={selectedTenant}
                                hasGitConnection={hasGitConnection}
                                playbooks={sidebarPlaybooks.filter(i => i.showOnHome)}
                                onLaunchIntent={(playbook, prompt) => {
                                    try {
                                        sessionStorage.setItem('mia_active_playbook', JSON.stringify(playbook));
                                    } catch (_) { /* ignore */ }
                                    if (playbook.mode === 'code') {
                                        // Run the same webapp creation workflow, using the playbook's template IDs if set
                                        handleStartChatFromHome(prompt, 'code', [], null, {
                                            playbookId: playbook.itemId,
                                            projectTemplateId: playbook.homeProjectTemplateId || '',
                                            sourceTemplateId: playbook.homeSourceTemplateId || '',
                                        });
                                    } else if (playbook.mode === 'both') {
                                        // Let the PlaybookLauncher show the chat/code chooser
                                        try {
                                            if (prompt) sessionStorage.setItem('mia_playbook_home_prompt', prompt);
                                        } catch (_) { /* ignore */ }
                                        navigate(`/playbook/${playbook.itemId}`);
                                        setIsNavSidebarVisible(false);
                                    } else {
                                        navigate(`/compose?playbookId=${playbook.itemId}`, {
                                            state: { initialPrompt: prompt, mode: 'chat' }
                                        });
                                        setIsNavSidebarVisible(false);
                                    }
                                }}
                            />
                        }
                        path="/"
                    />

                    {/* Projects page route */}
                    <Route
                        element={<Projects />}
                        path="/projects/:filter?"
                    />

                    {/* Playbook launcher route */}
                    <Route
                        element={<PlaybookLauncher />}
                        path="/playbook/:playbookId"
                    />

                    {/* Templates page route */}
                    <Route
                        element={<Templates />}
                        path="/templates"
                    />

                    {/* Learn page route */}
                    <Route
                        element={<Learn />}
                        path="/learn"
                    />

                    {/* News page route */}
                    <Route
                        element={<News />}
                        path="/news"
                    />

                    {/* Discover page route */}
                    <Route
                        element={<Discover />}
                        path="/discover"
                    />

                    {/* Memories page route */}
                    <Route
                        element={<Memories />}
                        path="/memories"
                    />

                    {/* Tools page route (Connectors) */}
                    <Route
                        element={<Connectors />}
                        path="/tools"
                    />

                    {/* Settings Layout with all sub-pages */}
                    <Route
                        element={<SettingsLayout />}
                        path="/settings"
                    >
                        <Route
                            element={<UserSettings />}
                            index
                        />
                        <Route
                            element={<Theme />}
                            path="theme"
                        />
                        <Route
                            element={<Advanced />}
                            path="advanced"
                        />
                        <Route
                            element={<Analytics />}
                            path="analytics"
                        />
                        <Route
                            element={<Privacy />}
                            path="privacy"
                        />
                        <Route
                            element={<Billing />}
                            path="billing"
                        />
                    </Route>

                    {/* Main chat route without top bar */}
                    <Route
                        element={
                            <MainChat
                                restartOnboardingTour={restartOnboardingTour}
                                onNewConversation={handleResetConversation}
                                isCreationModalOpen={isCreationModalOpen}
                                creationProjectName={creationProjectName}
                                creationServiceName={creationServiceName}
                                creationStep={creationStep}
                                creationMode={creationMode}
                            />
                        }
                        path="/chat/:tenantId?/:projectId?/:microservice?/:branch?/:tab?/*"
                    />

                    {/* Compose route - chat only, no canvas */}
                    <Route
                        element={
                            <MainChat
                                restartOnboardingTour={restartOnboardingTour}
                                onNewConversation={handleResetConversation}
                                isCreationModalOpen={isCreationModalOpen}
                                creationProjectName={creationProjectName}
                                creationServiceName={creationServiceName}
                                creationStep={creationStep}
                                creationMode={creationMode}
                                hideCanvas={true}
                            />
                        }
                        path="/compose"
                    />

                    {/* Tenant ID route - when URL is just /:tenantId, show home page with tenant pre-selected */}
                    <Route
                        element={
                            <Home
                                onStartChat={handleStartChatFromHome}
                                onSelectProject={handleSelectProjectFromHome}
                                onSelectTemplate={handleSelectTemplate}
                                onNavigate={(path) => navigate(path)}
                                projects={projects}
                                templates={templates}
                                user={user}
                                selectedTenant={selectedTenant}
                                hasGitConnection={hasGitConnection}
                            />
                        }
                        path="/:tenantId"
                    />
                </Routes>
            </div>

            {/* Onboarding Guide */}
            {showOnboarding && (
                <OnboardingGuide
                    onComplete={handleOnboardingComplete}
                    onSkip={handleOnboardingSkip}
                />
            )}

            {/* Project Creation Modal - Shows progress during project/service creation */}
            <ProjectCreationModal
                isOpen={isCreationModalOpen}
                projectName={creationProjectName}
                serviceName={creationServiceName}
                step={creationStep}
                mode={creationMode}
            />

            {/* Mobile Preview Page - Full-screen mobile preview experience */}
            <MobilePreviewPage
                isOpen={isMobilePreviewPageOpen}
                onClose={() => setIsMobilePreviewPageOpen(false)}
                onAskAI={(prompt) => {
                    // Handle ask AI from preview
                    navigate('/chat');
                }}
            />

            {/* Global Search Modal - Activated via ⌘K or sidebar Search button */}
            <GlobalSearch
                isOpen={isGlobalSearchOpen}
                onClose={() => setIsGlobalSearchOpen(false)}
                onNavigate={(path, options) => {
                    if (path === '/chat' && options) {
                        // Handle different navigation types
                        if (options.project) {
                            // Navigate to project chat
                            const project = options.project;
                            const tenantId = project.tenantId || project.tenant?.tenantId;
                            const projectId = project._id || project.projectId;
                            if (tenantId && projectId) {
                                // Clear project-specific localStorage and set switching flag
                                localStorage.removeItem('mia_selectedResource');
                                localStorage.removeItem('mia_selectedRevision');
                                localStorage.removeItem('mia_activeTab');
                                sessionStorage.setItem('mia_switching_to_project', projectId);
                                navigate(`/chat/${tenantId}/${projectId}`);
                            } else {
                                navigate('/chat');
                            }
                        } else if (options.conversationId) {
                            // Navigate to existing conversation
                            navigate(`/chat?conversationId=${options.conversationId}`);
                        } else if (options.template) {
                            // Navigate to chat with template context
                            navigate('/chat', { state: { template: options.template } });
                        } else if (options.file) {
                            // Navigate to chat with file context
                            navigate('/chat', { state: { file: options.file } });
                        }
                    } else {
                        navigate(path);
                    }
                }}
                user={user}
            />
        </div>
    );
}

// Wrapper to provide authenticated user to NotificationProvider
function NotificationWrapper() {
    const { user } = useAuth();
    const userId = user?.id || user?.sub || user?.email || 'demo-user';

    return (
        <NotificationProvider userId={userId}>
            <App />
        </NotificationProvider>
    );
}

// Wrap App with the authorization providers + AuthProvider + NotificationProvider.
// OrganizationProvider (authz org/tenant selection) is outermost so PermissionsProvider —
// which resolves roles scoped to the selected org — can read it; TenantAccessProvider loads
// the tenant list + per-org access map. All authz providers sit inside AuthProvider since they
// need an authenticated user before calling the gateway/BFF.
function AppWithAuth() {
    return (
        <OrganizationProvider>
            <AuthProvider>
                <PermissionsProvider>
                    <TenantAccessProvider>
                        <NotificationWrapper />
                    </TenantAccessProvider>
                </PermissionsProvider>
            </AuthProvider>
        </OrganizationProvider>
    );
}

export default AppWithAuth;
